/**
 * Orgacia Style Cart Drawer Engine for Shopify
 * Ultra-optimized, Instantaneous State Engine & Bundle Integration
 * Supports:
 * - 10-Product Mock Storefront Integration
 * - Dynamic Bundle Quantity Tier Discounts (Auto-adjusts on +/- qty changes)
 * - Automatic Free Gift Attachment / Removal on line items
 * - Universal AJAX Cart Interceptor (Works on ANY Shopify Theme)
 * - Multi-tier Reward & Free Shipping Progress Bar
 * - Live Urgency Countdown Timer
 * - Sliding Coupon & Discount Drawer (Image 2 Match)
 * - 1-Click Gift Wrap Addon
 * - Expandable Order Instructions
 * - "You May Also Like!" Upsell Carousel
 * - 100% Configurable Settings API
 */

(function (window, document) {
  'use strict';

  // Raw unintercepted fetch for internal cart operations (prevents loops & delays)
  const rawFetch = (typeof window.fetch === 'function') ? window.fetch.bind(window) : null;
  window._orgaciaOriginalFetch = window._orgaciaOriginalFetch || rawFetch;

  // Instantaneous Zero-Latency Embedded Product Vectors (0ms network delay, 0 DNS/TLS hops, 100% offline resilient)
  const ORGACIA_DEFAULT_PRODUCT_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 120'%3E%3Cdefs%3E%3ClinearGradient id='oc-bgrad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%231B4D3E'/%3E%3Cstop offset='100%25' stop-color='%230B231C'/%3E%3C/linearGradient%3E%3ClinearGradient id='oc-ggrad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23FCD34D'/%3E%3Cstop offset='100%25' stop-color='%23D97706'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect x='34' y='6' width='32' height='14' rx='3' fill='%231E293B'/%3E%3Crect x='30' y='18' width='40' height='6' rx='2' fill='%23D97706'/%3E%3Cpath d='M26 28 C26 24 32 24 36 24 L64 24 C68 24 74 24 74 28 L78 48 C78 52 78 106 74 110 C70 114 30 114 26 110 C22 106 22 52 22 48 Z' fill='url(%23oc-bgrad)'/%3E%3Crect x='28' y='46' width='44' height='48' rx='4' fill='%23FFFFFF'/%3E%3Ccircle cx='50' cy='58' r='5' fill='url(%23oc-ggrad)'/%3E%3Crect x='34' y='68' width='32' height='3' rx='1.5' fill='%231B4D3E'/%3E%3Crect x='38' y='74' width='24' height='2' rx='1' fill='%23D97706'/%3E%3Crect x='41' y='79' width='18' height='2' rx='1' fill='%2394A3B8'/%3E%3C/svg%3E";
  const ORGACIA_MAGNESIUM_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 120'%3E%3Cdefs%3E%3ClinearGradient id='oc-mgrad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%233B1E54'/%3E%3Cstop offset='100%25' stop-color='%231A0B2E'/%3E%3C/linearGradient%3E%3ClinearGradient id='oc-pgrad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23A855F7'/%3E%3Cstop offset='100%25' stop-color='%237E22CE'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect x='34' y='6' width='32' height='14' rx='3' fill='%231E293B'/%3E%3Crect x='30' y='18' width='40' height='6' rx='2' fill='%23A855F7'/%3E%3Cpath d='M26 28 C26 24 32 24 36 24 L64 24 C68 24 74 24 74 28 L78 48 C78 52 78 106 74 110 C70 114 30 114 26 110 C22 106 22 52 22 48 Z' fill='url(%23oc-mgrad)'/%3E%3Crect x='28' y='46' width='44' height='48' rx='4' fill='%23FAF5FF'/%3E%3Ccircle cx='50' cy='58' r='5' fill='url(%23oc-pgrad)'/%3E%3Crect x='34' y='68' width='32' height='3' rx='1.5' fill='%237E22CE'/%3E%3Crect x='38' y='74' width='24' height='2' rx='1' fill='%23A855F7'/%3E%3Crect x='41' y='79' width='18' height='2' rx='1' fill='%2394A3B8'/%3E%3C/svg%3E";
  const ORGACIA_CURCUMIN_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 120'%3E%3Cdefs%3E%3ClinearGradient id='oc-cgrad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%237C2D12'/%3E%3Cstop offset='100%25' stop-color='%23431407'/%3E%3C/linearGradient%3E%3ClinearGradient id='oc-ograd' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23F97316'/%3E%3Cstop offset='100%25' stop-color='%23EA580C'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect x='34' y='6' width='32' height='14' rx='3' fill='%231E293B'/%3E%3Crect x='30' y='18' width='40' height='6' rx='2' fill='%23EA580C'/%3E%3Cpath d='M26 28 C26 24 32 24 36 24 L64 24 C68 24 74 24 74 28 L78 48 C78 52 78 106 74 110 C70 114 30 114 26 110 C22 106 22 52 22 48 Z' fill='url(%23oc-cgrad)'/%3E%3Crect x='28' y='46' width='44' height='48' rx='4' fill='%23FFF7ED'/%3E%3Ccircle cx='50' cy='58' r='5' fill='url(%23oc-ograd)'/%3E%3Crect x='34' y='68' width='32' height='3' rx='1.5' fill='%23EA580C'/%3E%3Crect x='38' y='74' width='24' height='2' rx='1' fill='%23F97316'/%3E%3Crect x='41' y='79' width='18' height='2' rx='1' fill='%2394A3B8'/%3E%3C/svg%3E";
  const ORGACIA_VITAMIND3_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 120'%3E%3Cdefs%3E%3ClinearGradient id='oc-dgrad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23064E3B'/%3E%3Cstop offset='100%25' stop-color='%23022C22'/%3E%3C/linearGradient%3E%3ClinearGradient id='oc-ygrad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23FBBF24'/%3E%3Cstop offset='100%25' stop-color='%23D97706'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect x='34' y='6' width='32' height='14' rx='3' fill='%231E293B'/%3E%3Crect x='30' y='18' width='40' height='6' rx='2' fill='%2310B981'/%3E%3Cpath d='M26 28 C26 24 32 24 36 24 L64 24 C68 24 74 24 74 28 L78 48 C78 52 78 106 74 110 C70 114 30 114 26 110 C22 106 22 52 22 48 Z' fill='url(%23oc-dgrad)'/%3E%3Crect x='28' y='46' width='44' height='48' rx='4' fill='%23ECFDF5'/%3E%3Ccircle cx='50' cy='58' r='5' fill='url(%23oc-ygrad)'/%3E%3Crect x='34' y='68' width='32' height='3' rx='1.5' fill='%23059669'/%3E%3Crect x='38' y='74' width='24' height='2' rx='1' fill='%2310B981'/%3E%3Crect x='41' y='79' width='18' height='2' rx='1' fill='%2394A3B8'/%3E%3C/svg%3E";

  function getValidImageUrl(imgSrc, fallback) {
    const defaultFallback = (window.ORGACIA_SETTINGS && window.ORGACIA_SETTINGS.assetsFallback && window.ORGACIA_SETTINGS.assetsFallback.bottleB) || ORGACIA_DEFAULT_PRODUCT_SVG;
    const fb = fallback || defaultFallback;
    if (!imgSrc || typeof imgSrc !== 'string') return fb;
    const clean = imgSrc.trim();
    if (!clean || clean === 'null' || clean === 'undefined' || clean.includes('placeholder') || clean.includes('snowboard_')) return fb;
    return clean;
  }

  function escapeHTML(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Default Configuration
  const DEFAULT_CONFIG = {
    currencySymbol: '₹',
    currencyCode: 'INR',
    roundDecimals: false,
    title: 'Your Cart',
    position: 'right', // 'right' or 'left'
    showFloatingBubble: true,
    stickyAtc: {
      enabled: true,
      position: 'right', // 'right' | 'left' | 'center'
      bottomOffset: 24, // in px
      sideOffset: 24, // in px
      centerIcon: true,
      size: 56,
      bgColor: '#0D5C3A',
      iconColor: '#FFFFFF',
      badgeBg: '#111827',
      badgeText: '#FFFFFF'
    },

    // Urgency Timer
    timer: {
      enabled: true,
      prefixText: '⌛ Hurry Up! Offers Expire In:',
      durationMinutes: 7,
      durationSeconds: 29
    },

    // Announcement Bar Carousel
    announcement: {
      enabled: true,
      autoplay: true,
      interval: 3500,
      text: 'Welcome to Orgacia Store 👋',
      link: '',
      bgColor: '#000000',
      textColor: '#FFFFFF',
      slides: [
        {
          text: 'Welcome to Orgacia Store 👋',
          link: '',
          bgColor: '#000000',
          textColor: '#FFFFFF'
        },
        {
          text: '🚚 Free Express Delivery on orders over ₹200',
          link: '',
          bgColor: '#0D5C3A',
          textColor: '#FFFFFF'
        },
        {
          text: '🌿 100% Ayurvedic & Clinically Proven Formulas',
          link: '',
          bgColor: '#1A2E26',
          textColor: '#FDE68A'
        }
      ]
    },

    // Multi-Tier Milestone Rewards
    tiers: {
      enabled: true,
      style: 'classic-glow', // 'classic-glow' (Recommended) | 'pill-capsule' | 'luxury-gold' | 'step-cards' | 'neon-ticker'
      milestoneTopClaimStyle: 'emerald_card', // 'emerald_card' | 'compact_pill' | 'luxury_gold' | 'clean_minimal' | 'none'
      milestoneBottomClaimStyle: 'classic_bar', // 'classic_bar' | 'compact_pill' | 'golden_ticket' | 'cyber_neon' | 'floating_chip' | 'none'
      items: [
        {
          threshold: 200,
          label: 'Free Delivery',
          icon: '🚚',
          unlockedText: 'Free Delivery Unlocked!',
          rewardType: 'shipping',
          discountCode: ''
        },
        {
          threshold: 378,
          label: '5% OFF',
          icon: '%',
          unlockedText: '5% OFF Unlocked!',
          rewardType: 'discount',
          discountPct: 5,
          discountCode: '5OFF'
        },
        {
          threshold: 600,
          label: 'Free Gift',
          icon: '🎁',
          unlockedText: 'Free Gift Unlocked!',
          rewardType: 'gift',
          discountCode: ''
        }
      ]
    },

    // Volume & Pack Discounts
    volumeDiscounts: {
      enabled: true,
      tiers: [
        { qty: 4, discountPct: 10, label: 'Pack of 4 (10% OFF)', discountCode: 'BUNDLE10' },
        { qty: 3, discountPct: 7, label: 'Pack of 3 (7% OFF)', discountCode: 'BUNDLE7' },
        { qty: 2, discountPct: 5, label: 'Pack of 2 (5% OFF)', discountCode: 'BUNDLE5' }
      ]
    },

    // Free Gift Milestone Product Selection
    giftSelection: {
      enabled: true,
      maxGiftsAllowed: 1,
      bannerText: 'You have an unclaimed free gift!',
      bannerBtnText: 'ADD',
      modalTitle: "You've unlocked a free gift! 🎁",
      modalSubtitle: 'You can claim 1 more gift',
      saveBtnText: 'Save',
      products: [
        {
          id: 'gift-tote-black',
          productId: 'gift-tote-black',
          title: 'Tote Bag – Black',
          originalPrice: 3000,
          price: 0,
          badgeText: 'FREE GIFT',
          image: 'https://images.unsplash.com/photo-1544816155-12df9643f363?w=300&auto=format&fit=crop&q=80'
        },
        {
          id: 'gift-tote-brown',
          productId: 'gift-tote-brown',
          title: 'Tote Bag – Brown',
          originalPrice: 2000,
          price: 0,
          badgeText: 'FREE GIFT',
          image: 'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=300&auto=format&fit=crop&q=80'
        },
        {
          id: 'gift-yellow-tshirt',
          productId: 'gift-yellow-tshirt',
          title: 'Yellow T Shirt',
          originalPrice: 500,
          price: 0,
          badgeText: 'FREE GIFT',
          image: 'https://images.unsplash.com/photo-1521572267360-ee0c2909d518?w=300&auto=format&fit=crop&q=80'
        }
      ]
    },

    // Gift Wrap Addon
    giftWrap: {
      enabled: true,
      price: 49,
      text: '🎁 Gift wrap it @49 only',
      variantId: null
    },

    // Available Coupons
    couponsEnabled: true,
    coupons: [
      {
        code: 'GET20',
        title: 'Get 20% Off on Entire Order',
        type: 'percentage',
        value: 20,
        bullets: [
          'Only eligible on 2 or more products',
          'Applicable for all users'
        ],
        minItems: 2,
        minAmount: 0
      },
      {
        code: 'SAVE10',
        title: 'Save ₹100 on Orders Above ₹500',
        type: 'fixed',
        value: 100,
        bullets: [
          'Minimum order value ₹500',
          'Valid for first time buyers'
        ],
        minItems: 1,
        minAmount: 500
      },
      {
        code: 'ORGACIA50',
        title: 'Extra ₹50 Off on First Order',
        type: 'fixed',
        value: 50,
        bullets: [
          'Applicable on any order'
        ],
        minItems: 1,
        minAmount: 0
      },
      {
        code: 'WELLNESS15',
        title: '15% Off on Wellness Kits',
        type: 'percentage',
        value: 15,
        bullets: [
          'Minimum 2 items required'
        ],
        minItems: 2,
        minAmount: 0
      }
    ],

    // Savings Ribbon Style: 'tab' | 'pill' | 'ticker' | 'gold' | 'amber'
    savingsStyle: 'tab',

    // Confetti Celebration Effect
    celebration: {
      enabled: true,
      duration: 2500
    },

    // Upsell Products ("You May Also Like!" / Dynamic Product-Based Upsells)
    upsell: {
      enabled: true,
      title: 'You May Also Like!',
      badgeText: 'RECOMMENDED',
      badgeStyle: 'social_proof', // 'social_proof' | 'frequently_bought' | 'live_demand' | 'recommended'
      showStars: false,
      showBenefits: true,
      enableDuoCombo: false,
      duoComboDiscount: 10,
      duoCombo: {
        enabled: false
      },
      socialProofText: '🔥 87% of customers add this to order',
      layout: 'grid', // 'grid' | 'carousel' | 'compact'
      maxItems: 2,
      rules: {
        // When Product X (Orgacia Ashwagandha) is in cart -> Recommend Y (Magnesium) & Z (Curcumin)
        'orgacia-ashwagandha': [
          {
            id: 'orgacia-magnesium',
            title: 'Magnesium Glycinate',
            fullTitle: 'Organic Magnesium Glycinate (60 Veg Caps)',
            price: 899.00,
            comparePrice: 1199.00,
            discountTag: '25% OFF',
            image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=200&auto=format&fit=crop&q=80'
          },
          {
            id: 'orgacia-curcumin',
            title: 'Bio-Curcumin 95%',
            fullTitle: 'Bio-Enhanced Curcumin 95% + Piperine',
            price: 799.00,
            comparePrice: 999.00,
            discountTag: '20% OFF',
            image: 'https://images.unsplash.com/photo-1584017911766-d451b3d0e843?w=200&auto=format&fit=crop&q=80'
          }
        ],
        // When Prod 1 is in cart -> Recommend Vitamin D3 & Bio-Curcumin
        'prod-1': [
          {
            id: 'upsell-vitd3',
            title: 'Vitamin D3+K2 Drops',
            fullTitle: 'Liquid Vitamin D3 + K2 (1000 IU/Drop)',
            price: 499.00,
            comparePrice: 699.00,
            discountTag: '28% OFF',
            image: ORGACIA_VITAMIND3_SVG
          },
          {
            id: 'upsell-curcumin',
            title: 'Bio-Curcumin 95%',
            fullTitle: 'Bio-Enhanced Curcumin 95% + Piperine',
            price: 799.00,
            comparePrice: 999.00,
            discountTag: '20% OFF',
            image: ORGACIA_CURCUMIN_SVG
          }
        ],
        // When Prod 2 is in cart -> Recommend Magnesium & Vitamin B-Complex
        'prod-2': [
          {
            id: 'upsell-magnesium',
            title: 'Magnesium Glycinate',
            fullTitle: 'Organic Magnesium Glycinate (60 Veg Caps)',
            price: 899.00,
            comparePrice: 1199.00,
            discountTag: '25% OFF',
            image: ORGACIA_MAGNESIUM_SVG
          },
          {
            id: 'upsell-vitb',
            title: 'Vitamin B-Complex',
            fullTitle: 'Bioactive Vitamin B-Complex Active',
            price: 599.00,
            comparePrice: 899.00,
            discountTag: '33% OFF',
            image: ORGACIA_DEFAULT_PRODUCT_SVG
          }
        ],
        'default': [
          {
            id: 'orgacia-magnesium',
            title: 'Magnesium Glycinate',
            fullTitle: 'Organic Magnesium Glycinate (60 Veg Caps)',
            price: 899.00,
            comparePrice: 1199.00,
            discountTag: '25% OFF',
            image: ORGACIA_MAGNESIUM_SVG
          },
          {
            id: 'orgacia-curcumin',
            title: 'Bio-Curcumin 95%',
            fullTitle: 'Bio-Enhanced Curcumin 95% + Piperine',
            price: 799.00,
            comparePrice: 999.00,
            discountTag: '20% OFF',
            image: ORGACIA_CURCUMIN_SVG
          }
        ]
      },
      catalogPool: [
        {
          id: 'orgacia-magnesium',
          title: 'Magnesium Glycinate',
          fullTitle: 'Organic Magnesium Glycinate (60 Veg Caps)',
          price: 899.00,
          comparePrice: 1199.00,
          discountTag: '25% OFF',
          image: ORGACIA_MAGNESIUM_SVG
        },
        {
          id: 'orgacia-curcumin',
          title: 'Bio-Curcumin 95%',
          fullTitle: 'Bio-Enhanced Curcumin 95% + Piperine',
          price: 799.00,
          comparePrice: 999.00,
          discountTag: '20% OFF',
          image: ORGACIA_CURCUMIN_SVG
        },
        {
          id: 'orgacia-vitd3',
          title: 'Vitamin D3+K2 Drops',
          fullTitle: 'Liquid Vitamin D3 + K2 (1000 IU/Drop)',
          price: 499.00,
          comparePrice: 699.00,
          discountTag: '28% OFF',
          image: ORGACIA_VITAMIND3_SVG
        },
        {
          id: 'orgacia-ashwagandha',
          title: 'Ashwagandha KSM-66',
          fullTitle: 'Organic Ashwagandha + KSM-66® Root Extract',
          price: 699.00,
          comparePrice: 899.00,
          discountTag: '22% OFF',
          image: ORGACIA_DEFAULT_PRODUCT_SVG
        },
        {
          id: 'orgacia-vitb',
          title: 'Vitamin B-Complex',
          fullTitle: 'Bioactive Vitamin B-Complex Active',
          price: 599.00,
          comparePrice: 899.00,
          discountTag: '33% OFF',
          image: ORGACIA_DEFAULT_PRODUCT_SVG
        }
      ]
    },

    // Dynamic Bundle Tiers Configuration (Empty by default; populated only when bundle widget is active)
    bundleTiers: [],

    // Product Bundle Rules Configuration
    bundleRules: {
      'orgacia-ashwagandha': {
        name: 'Orgacia Ashwagandha Supply Bundle',
        tiers: [
          { minQty: 1, discountPct: 0 },
          { minQty: 2, discountPct: 15, label: '15% Off (2 Months Supply)' },
          { minQty: 3, discountPct: 25, label: '25% Off (3 Months Supply)', gift: { id: 'gift-vit-d3', title: '🎁 FREE Vitamin D3 + K2 Liquid Drops', price: 0, originalPrice: 499, image: ORGACIA_VITAMIND3_SVG } }
        ]
      },
      'prod-1': {
        name: 'Vitality Wellness Pack Bundle',
        tiers: [
          { minQty: 1, discountPct: 0 },
          { minQty: 2, discountPct: 15, label: '15% Volume Discount' },
          { minQty: 3, discountPct: 25, label: '25% Volume Discount', gift: { id: 'gift-curc', title: '🎁 FREE Bio-Curcumin 95%', price: 0, originalPrice: 499, image: ORGACIA_CURCUMIN_SVG } }
        ]
      },
      'prod-3': {
        name: 'Immunity Boost Tier Bundle',
        tiers: [
          { minQty: 1, discountPct: 0 },
          { minQty: 2, discountPct: 10, label: '10% Pro Discount' },
          { minQty: 3, discountPct: 20, label: '20% Pro Discount', gift: { id: 'gift-mag', title: '🎁 FREE Magnesium Glycinate', price: 0, originalPrice: 450, image: ORGACIA_MAGNESIUM_SVG } }
        ]
      },
      'prod-5': {
        name: 'Active Lifestyle Multi-Set',
        tiers: [
          { minQty: 1, discountPct: 0 },
          { minQty: 2, discountPct: 12, label: '12% Multi-Set Savings' },
          { minQty: 3, discountPct: 22, label: '22% Multi-Set Savings', gift: { id: 'gift-vit-d3', title: '🎁 FREE Vitamin D3 + K2 Liquid Drops', price: 0, originalPrice: 499, image: ORGACIA_VITAMIND3_SVG } }
        ]
      },
      'prod-7': {
        name: 'Family Wellness Bundle',
        tiers: [
          { minQty: 1, discountPct: 0 },
          { minQty: 2, discountPct: 15, label: '15% Family Safety Discount', gift: { id: 'gift-vitb', title: '🎁 FREE Vitamin B-Complex Active', price: 0, originalPrice: 399, image: ORGACIA_DEFAULT_PRODUCT_SVG } }
        ]
      },
      'prod-10': {
        name: 'Merino Thermal Multi-Pack',
        tiers: [
          { minQty: 1, discountPct: 0 },
          { minQty: 2, discountPct: 15, label: '15% Off 2-Pack' },
          { minQty: 3, discountPct: 25, label: '25% Off 3-Pack' }
        ]
      }
    },

    // Special Instructions / Order Notes
    instructions: {
      enabled: true,
      label: 'Add Special Instructions',
      placeholder: 'Special delivery, gift message, or packaging notes for this order...',
      defaultOpen: false
    },

    // Total Savings Callout Banner
    savingsCallout: {
      enabled: true,
      style: 'tab', // 'tab' (3D folded ribbon) | 'pill' (2026 emerald shimmer) | 'gold' | 'glass' | 'amber' | 'ticker'
      template: '{amount} Saved so far!'
    },

    // Smart Bundle Tier Upgrade / Swap Configuration
    tierUpgrade: {
      enabled: true,
      targetMode: 'all_bundle_products', // 'all_bundle_products' | 'specific_products' | 'product_tag'
      targetProductIds: [],
      targetProductHandles: [],
      targetTag: 'bundle-swap',
      rules: {},
      style: 'option_2', // 'option_2' (Nested Ledge - Default) | 'option_1' (Smart Dock) | 'option_3' (Micro-Chip) | 'option_4' (Step-Up Strip)
      palette: 'fresh_lime', // 'fresh_lime' | 'orgacia_forest' | 'turkish_teal' | 'royal_amber' | 'midnight_dark' | 'custom'
      fontSize: 12,
      btnFontSize: 11,
      btnPaddingY: 4,
      btnPaddingX: 9,
      badgeText: 'Upgrade for better value',
      buttonText: 'Upgrade',
      discountFormat: 'save_amount', // 'save_amount' (Default: e.g. "SAVE RS. 44.90") | 'percent_off' (e.g. "5% OFF") | 'both_amount_percent' (e.g. "SAVE RS. 44.90 (5% OFF)") | 'both_percent_amount' (e.g. "5% OFF (SAVE RS. 44.90)")
      maxSwapLevels: 0, // 0 = unlimited (always show next tier), 1 = one swap only, 2 = two swaps max, etc.
      customColors: {}
    },



    // Footer & Checkout
    footer: {
      termsRequired: false,
      termsText: 'T&C',
      termsUrl: '/policies/terms-of-service',
      checkoutBtnText: 'Checkout Now',
      showTrustBadges: true,
      trustBadges: [
        { text: 'Free Returns', icon: '✓', color: '' },
        { text: 'Free Exchanges', icon: '✓', color: '' },
        { text: 'Secure Payments', icon: '✓', color: '' }
      ]
    }
  };

  // State
  let config = JSON.parse(JSON.stringify(DEFAULT_CONFIG));
  let cartState = {
    items: [],
    item_count: 0,
    total_price: 0,
    original_total_price: 0,
    appliedCoupon: null,
    discountAmount: 0,
    milestoneDiscountCode: null,
    bundleDiscountCode: null,
    isGiftWrapAdded: false,
    note: '',
    termsAccepted: true
  };

  let timerInterval = null;
  let remainingSeconds = (config.timer.durationMinutes * 60) + config.timer.durationSeconds;
  let isShopifyEnv = (typeof window.Shopify !== 'undefined') || 
    (typeof window !== 'undefined' && window.location && (window.location.hostname.includes('myshopify.com') || window.location.pathname.includes('/cart')));

  // Format Currency (Preserves exact 2-decimal values by default; rounds to whole number only if config.roundDecimals is true)
  function formatMoney(centsOrAmount, isCents = false) {
    const raw = isCents ? centsOrAmount / 100 : Number(centsOrAmount || 0);
    if (config.roundDecimals) {
      const amount = Math.round(raw);
      return `${config.currencySymbol}${amount.toLocaleString('en-IN')}`;
    }
    const amount = Math.round(raw * 100) / 100;
    return `${config.currencySymbol}${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  // Bulletproof Price Normalizer: converts incoming prices into exact currency values without unwanted decimal rounding
  function normalizePriceAmount(val, isFromDom = false) {
    if (val === undefined || val === null) return 0;
    const num = Number(val);
    if (isNaN(num) || num <= 0) return 0;

    const roundVal = (v) => config.roundDecimals ? Math.round(v) : (Math.round(v * 100) / 100);

    // Handle DOM / Widget input (values already in currency units)
    if (isFromDom) {
      // If accidentally passed in 100x paise from a server object (e.g. 49900 or 89900)
      if (num >= 10000) {
        return roundVal(num / 100);
      }
      return roundVal(num);
    }

    // Handle Shopify API / Server input (/cart.js, /cart/add.js, initialCart)
    // In Shopify, ALL Cart API amounts are integers in cents/paise (e.g. 500 = ₹5.00, 5000 = ₹50.00, 49900 = ₹499.00, 89900 = ₹899.00)
    if (isShopifyEnv) {
      return roundVal(num / 100);
    }

    // Standalone demo / preview fallback (where prices may already be in currency units like 449 or 1499)
    if (num >= 10000) {
      return roundVal(num / 100);
    }
    return roundVal(num);
  }

  // Product Page Pricing Registry & Session Cache
  const pageProductPricingCache = {};

  function setCachedProductPricing(pricing) {
    if (!pricing || typeof pricing !== 'object') return;
    const vId = String(pricing.variantId || '').trim();
    const pId = String(pricing.productId || '').trim();
    const title = String(pricing.title || '').trim().toLowerCase();
    const handle = String(pricing.handle || '').trim().toLowerCase();

    if (vId) pageProductPricingCache[vId] = pricing;
    if (pId) pageProductPricingCache[pId] = pricing;
    if (title) pageProductPricingCache[title] = pricing;
    if (handle) pageProductPricingCache[handle] = pricing;

    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        const payload = JSON.stringify(pricing);
        if (vId) window.sessionStorage.setItem('orgacia_prod_price_' + vId, payload);
        if (handle) window.sessionStorage.setItem('orgacia_prod_price_' + handle, payload);
      } catch(e) {}
    }
  }

  function getCachedProductPricing(item) {
    if (!item) return null;
    const vId = String(item.variantId || item.id || '').trim();
    const pId = String(item.productId || '').trim();
    const title = String(item.title || item.product_title || '').trim().toLowerCase();
    const handle = String(item.handle || '').trim().toLowerCase();

    if (vId && pageProductPricingCache[vId]) return pageProductPricingCache[vId];
    if (pId && pageProductPricingCache[pId]) return pageProductPricingCache[pId];
    if (title && pageProductPricingCache[title]) return pageProductPricingCache[title];
    if (handle && pageProductPricingCache[handle]) return pageProductPricingCache[handle];

    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        if (vId) {
          const stored = window.sessionStorage.getItem('orgacia_prod_price_' + vId);
          if (stored) return JSON.parse(stored);
        }
        if (handle) {
          const stored = window.sessionStorage.getItem('orgacia_prod_price_' + handle);
          if (stored) return JSON.parse(stored);
        }
      } catch(e) {}
    }
    return null;
  }

  // Read authoritative pricing directly from current product page (supports Minimog, Dawn, Prestige, Sense, OS 2.0)
  let lastScrapedPagePricing = null;
  let lastScrapedTime = 0;

  function readProductPagePricing(targetItem) {
    if (typeof window === 'undefined' || typeof document === 'undefined') return null;

    if (targetItem) {
      const cached = getCachedProductPricing(targetItem);
      if (cached && cached.sellingPrice > 0) return cached;
    }

    const now = Date.now();
    if (lastScrapedPagePricing && (now - lastScrapedTime < 3000)) {
      if (!targetItem) return lastScrapedPagePricing;
      const tVId = String(targetItem.variantId || targetItem.id || '').trim();
      if (!tVId || !lastScrapedPagePricing.variantId || tVId === lastScrapedPagePricing.variantId) {
        return lastScrapedPagePricing;
      }
    }

    const currentPath = (window.location && window.location.pathname) ? window.location.pathname.toLowerCase() : '';
    const isProductPage = currentPath.includes('/products/') || !!document.querySelector('form[action*="/cart/add"], .shopify-product-form, product-form');
    if (!isProductPage) {
      return targetItem ? getCachedProductPricing(targetItem) : null;
    }

    const pageVariantInput = document.querySelector('form[action*="/cart/add"] [name="id"], form[action*="/cart/add.js"] [name="id"], product-form [name="id"], [name="id"]');
    const pageVariantId = pageVariantInput ? String(pageVariantInput.value).trim() : null;

    const pageTitleEl = document.querySelector('h1.product__title, h1.product-title, .product__title, .product-single__title, .sf__product-title, h1');
    const pageTitle = (pageTitleEl ? pageTitleEl.textContent : '').toLowerCase().trim();

    let pageHandle = '';
    const handleMatch = currentPath.match(/\/products\/([a-zA-Z0-9\-_]+)/);
    if (handleMatch && handleMatch[1]) pageHandle = handleMatch[1];

    let pageProductId = '';
    const pIdEl = document.querySelector('[data-product-id]');
    if (pIdEl) pageProductId = pIdEl.getAttribute('data-product-id');
    if (!pageProductId && window.ShopifyAnalytics?.meta?.product?.id) {
      pageProductId = String(window.ShopifyAnalytics.meta.product.id);
    }

    // If targetItem is provided, verify whether it matches the product page
    if (targetItem) {
      const itemVId = String(targetItem.variantId || targetItem.id || '').trim();
      const itemPId = String(targetItem.productId || '').trim();
      const itemTitle = (targetItem.title || targetItem.product_title || '').toLowerCase().trim();
      let isMatch = false;

      if (pageVariantId && itemVId && itemVId === pageVariantId) isMatch = true;
      if (!isMatch && itemPId && pageProductId && itemPId === pageProductId) isMatch = true;
      if (!isMatch && targetItem.url && currentPath.includes(targetItem.url.split('?')[0].toLowerCase().replace('/products/', ''))) isMatch = true;
      if (!isMatch && targetItem.handle && pageHandle && (pageHandle === targetItem.handle.toLowerCase() || currentPath.includes(targetItem.handle.toLowerCase()))) isMatch = true;

      if (!isMatch && pageTitle && itemTitle) {
        if (pageTitle === itemTitle || pageTitle.includes(itemTitle) || itemTitle.includes(pageTitle)) isMatch = true;
        const wWords = pageTitle.replace(/[^a-z0-9]/g, ' ').split(/\s+/).filter(w => w.length > 2);
        const iWords = itemTitle.replace(/[^a-z0-9]/g, ' ').split(/\s+/).filter(w => w.length > 2);
        if (wWords.filter(w => iWords.includes(w)).length >= 2) isMatch = true;
      }

      if (!isMatch) {
        return getCachedProductPricing(targetItem);
      }
    }

    let sellingPrice = 0;
    let comparePrice = 0;

    // A. Parse from DOM price elements (Matches what customer sees on screen)
    // 1. Compare-At / Original Price (strikethrough / regular price on sale)
    const compSelectors = [
      '.sf__product-price .sf-currency-money.line-through',
      '.sf__product-price .line-through',
      '.sf__product-price .price-item--compare',
      '.prod__price .compare-price',
      '.price-item--compare',
      '.price__sale s',
      '.price__sale del',
      '.price__sale .price-item--regular',
      's.price-item--regular',
      'del.price-item--regular',
      '.product__compare-at-price',
      '[data-compare-price]',
      'del .money',
      's .money',
      '.compare-at-price'
    ];
    for (const sel of compSelectors) {
      const el = document.querySelector(sel);
      if (el && el.textContent) {
        const m = el.textContent.replace(/,/g, '').match(/\d+(?:\.\d+)?/);
        if (m) {
          const val = parseFloat(m[0]);
          if (val > 0) {
            comparePrice = val;
            break;
          }
        }
      }
    }

    // 2. Active Selling Price (excluding any strikethrough or compare elements)
    const saleSelectors = [
      '.sf__product-price .sf-currency-money:not(.line-through):not(s *):not(del *)',
      '.sf__product-price .price-item--sale',
      '.sf__product-price .price-sale',
      '.price__sale .price-item--sale',
      '.price-item--sale',
      '.price-item--last',
      '.prod__price .money:not(s *):not(del *):not(.line-through)',
      '.product-price .money:not(s *):not(del *):not(.line-through)',
      '[data-product-price]',
      '.price-item--regular:not(s *):not(del *):not(.line-through)',
      '.sf__product-price .money:not(.line-through):not(s *):not(del *)',
      '.product-price:not(s):not(del)',
      '.price:not(s):not(del) .money:not(s *):not(del *)'
    ];
    for (const sel of saleSelectors) {
      const el = document.querySelector(sel);
      if (el && el.textContent) {
        const m = el.textContent.replace(/,/g, '').match(/\d+(?:\.\d+)?/);
        if (m) {
          const val = parseFloat(m[0]);
          if (val > 0) {
            sellingPrice = val;
            break;
          }
        }
      }
    }

    // 3. Fallback: Parse whole price container if individual selectors were ambiguous
    if (sellingPrice === 0 || comparePrice === 0) {
      const containerSelectors = ['.sf__product-price', '.prod__price', '.product-price', '.price', '.product__price'];
      for (const sel of containerSelectors) {
        const container = document.querySelector(sel);
        if (container && container.textContent) {
          const matches = container.textContent.replace(/,/g, '').match(/\d+(?:\.\d+)?/g);
          if (matches && matches.length >= 2) {
            const vals = matches.map(v => parseFloat(v)).filter(v => v > 0);
            if (vals.length >= 2) {
              if (sellingPrice === 0) sellingPrice = Math.min(vals[0], vals[1]);
              if (comparePrice === 0) comparePrice = Math.max(vals[0], vals[1]);
              break;
            }
          } else if (matches && matches.length === 1 && sellingPrice === 0) {
            sellingPrice = parseFloat(matches[0]);
          }
        }
      }
    }

    // B. Cross-check / fallback with ShopifyAnalytics or Product JSON scripts
    if (sellingPrice === 0 && window.ShopifyAnalytics?.meta?.product) {
      const prodMeta = window.ShopifyAnalytics.meta.product;
      const curVar = (prodMeta.variants || []).find(v => String(v.id) === String(pageVariantId)) || prodMeta.variants?.[0];
      if (curVar && curVar.price) {
        const rawP = Number(curVar.price) || 0;
        sellingPrice = rawP > 0 ? (config.roundDecimals ? Math.round(rawP / 100) : (Math.round(rawP) / 100)) : 0;
      }
    }

    // If compare price is still 0, check product JSON scripts (Minimog / Dawn)
    if (comparePrice === 0) {
      try {
        const jsonEl = document.querySelector('#ProductJson, [data-product-json], script[type="application/json"][id*="Product"]');
        if (jsonEl && jsonEl.textContent) {
          const data = JSON.parse(jsonEl.textContent);
          const curVar = (data.variants || []).find(v => String(v.id) === String(pageVariantId)) || data.variants?.[0];
          if (curVar) {
            if (sellingPrice === 0 && curVar.price) {
              sellingPrice = curVar.price > 100 ? (curVar.price / 100) : curVar.price;
            }
            if (curVar.compare_at_price) {
              comparePrice = curVar.compare_at_price > 100 ? (curVar.compare_at_price / 100) : curVar.compare_at_price;
            }
          }
        }
      } catch(e) {}
    }

    if (sellingPrice > 0) {
      if (comparePrice <= sellingPrice) {
        comparePrice = 0;
      }
      const result = {
        sellingPrice: config.roundDecimals ? Math.round(sellingPrice) : (Math.round(sellingPrice * 100) / 100),
        comparePrice: comparePrice > sellingPrice ? (config.roundDecimals ? Math.round(comparePrice) : (Math.round(comparePrice * 100) / 100)) : 0,
        variantId: pageVariantId,
        productId: pageProductId,
        title: pageTitle,
        handle: pageHandle
      };
      setCachedProductPricing(result);
      lastScrapedPagePricing = result;
      lastScrapedTime = Date.now();
      return result;
    }

    return targetItem ? getCachedProductPricing(targetItem) : null;
  }

  // Build UI HTML Structure
  function createDrawerDOM() {
    let existingOverlay = document.getElementById('oc-drawer-overlay');
    if (existingOverlay) existingOverlay.remove();
    let existingDrawer = document.getElementById('oc-drawer');
    if (existingDrawer) existingDrawer.remove();
    let existingBubble = document.getElementById('oc-floating-cart-trigger');
    if (existingBubble) existingBubble.remove();

    // Backdrop
    const overlay = document.createElement('div');
    overlay.id = 'oc-drawer-overlay';
    overlay.className = 'oc-drawer-overlay';
    document.body.appendChild(overlay);

    // Main Drawer
    const drawer = document.createElement('div');
    drawer.id = 'oc-drawer';
    drawer.className = `oc-drawer ${config.position === 'left' ? 'oc-drawer--left' : ''}`;
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.innerHTML = `
      <!-- Confetti Canvas -->
      <canvas id="oc-confetti-canvas" class="oc-confetti-canvas"></canvas>

      <!-- Header -->
      <div class="oc-header" id="oc-header">
        <div class="oc-header__title-wrap">
          <button class="oc-header__back-btn" id="oc-header-back-btn" aria-label="Go Back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
          </button>
          <h2 class="oc-header__title" id="oc-header-title">${config.title} (0 items)</h2>
        </div>
        <button class="oc-header__close" id="oc-close-btn" aria-label="Close Cart">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
      </div>

      <!-- Urgency Countdown Timer -->
      <div class="oc-urgency-bar" id="oc-urgency-bar" style="${config.timer.enabled ? '' : 'display:none;'}">
        <span class="oc-urgency-prefix" id="oc-urgency-prefix">${config.timer.prefixText}</span>
        <span class="oc-timer-pill" id="oc-timer-min">07m</span>
        <span class="oc-timer-sep">:</span>
        <span class="oc-timer-pill" id="oc-timer-sec">29s</span>
      </div>

      <!-- Announcement Bar Carousel -->
      <div class="oc-announcement-bar" id="oc-announcement-bar" style="${config.announcement && config.announcement.enabled ? '' : 'display:none;'}">
        <div class="oc-announcement-track" id="oc-announcement-track">
          <!-- Populated by initAnnouncementCarousel -->
        </div>
      </div>

      <!-- Multi-Tier Milestone Progress Bar -->
      <div class="oc-progress-section" id="oc-progress-section" style="${config.tiers.enabled ? '' : 'display:none;'}">
        <div class="oc-progress-title" id="oc-progress-title">Add items to unlock rewards!</div>
        <div class="oc-progress-track-wrapper">
          <div class="oc-progress-track">
            <div class="oc-progress-fill" id="oc-progress-fill"></div>
          </div>
          <div class="oc-milestones" id="oc-milestones">
            <!-- Dynamic Milestones with Pin + Label -->
          </div>
        </div>
      </div>

      <!-- Animated Indeterminate Loader Line (Exact Match to User Screenshot) -->
      <div class="oc-drawer-loader" id="oc-drawer-loader" aria-hidden="true">
        <div class="oc-drawer-loader__bar"></div>
      </div>

      <!-- Scrollable Container with 2 Views (Cart View & Discount View) -->
      <div class="oc-drawer__views-container">
        
        <!-- View 1: Main Cart View -->
        <div class="oc-drawer__view oc-view-cart" id="oc-view-cart">
          <!-- Milestone Unlocked Coupon Banner (User Flow Match) -->
          <div class="oc-milestone-coupon-banner" id="oc-milestone-coupon-banner" style="display: none;">
            <div class="oc-milestone-coupon-left">
              <span class="oc-milestone-coupon-icon">🏷️</span>
              <span class="oc-milestone-coupon-text" id="oc-milestone-coupon-text">5% OFF Unlocked! Use code <strong>5OFF</strong></span>
            </div>
            <button class="oc-milestone-coupon-btn" id="oc-milestone-coupon-btn" type="button">APPLY</button>
          </div>

          <!-- Unclaimed Free Gift Milestone Banner (Screenshot 1 Match) -->
          <div class="oc-unclaimed-gift-banner" id="oc-unclaimed-gift-banner" style="display: none;" onclick="OrgaciaCart.openGiftModal(event)">
            <div class="oc-unclaimed-gift-left">
              <span class="oc-unclaimed-gift-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"></polyline><rect x="2" y="7" width="20" height="5"></rect><line x1="12" y1="22" x2="12" y2="7"></line><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path></svg>
              </span>
              <span class="oc-unclaimed-gift-text" id="oc-unclaimed-gift-text">${(config.giftSelection && config.giftSelection.bannerText) ? config.giftSelection.bannerText : 'You have an unclaimed free gift!'}</span>
            </div>
            <button class="oc-unclaimed-gift-btn" id="oc-unclaimed-gift-btn" type="button" onclick="OrgaciaCart.openGiftModal(event)">
              ${(config.giftSelection && config.giftSelection.bannerBtnText) ? config.giftSelection.bannerBtnText : 'ADD'}
            </button>
          </div>

          <!-- Cart Line Items -->
          <div class="oc-cart-items" id="oc-cart-items">
            <!-- Dynamic Line Items -->
          </div>

          <!-- Empty State (Image 2 Match) -->
          <div class="oc-empty-state ${cartState.items.length === 0 ? 'oc-show' : ''}" id="oc-empty-state" style="${cartState.items.length === 0 ? 'display: flex;' : 'display: none;'}">
            <div class="oc-empty-title">Your cart is empty!</div>
            <button class="oc-empty-btn" id="oc-empty-btn" type="button" onclick="OrgaciaCart.close()">Continue Shopping</button>
          </div>

          <!-- Gift Wrap Addon -->
          <div class="oc-addon-card" id="oc-giftwrap-card" style="${config.giftWrap.enabled ? '' : 'display:none;'}">
            <input type="checkbox" id="oc-giftwrap-checkbox">
            <label for="oc-giftwrap-checkbox" class="oc-addon-text" id="oc-giftwrap-text">
              ${config.giftWrap.text}
            </label>
          </div>

          <!-- Coupon Input & Trigger Card -->
          <div class="oc-coupon-card" id="oc-coupon-card" style="${config.couponsEnabled === false ? 'display:none;' : ''}">
            <div class="oc-coupon-input-wrap">
              <span class="oc-coupon-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>
              </span>
              <input type="text" class="oc-coupon-input" id="oc-coupon-input" placeholder="Enter Coupon Code" autocomplete="off" spellcheck="false">
              <button class="oc-coupon-apply-btn" id="oc-coupon-apply-btn">Apply</button>
            </div>

            <!-- Ineligible Coupon Validation Message -->
            <div class="oc-coupon-error" id="oc-coupon-error"></div>
            
            <div class="oc-applied-coupon-tag" id="oc-applied-coupon-tag">
              <span id="oc-applied-coupon-text">🏷️ CODE applied</span>
              <button class="oc-remove-coupon-btn" id="oc-remove-coupon-btn" title="Remove Coupon">✕</button>
            </div>

            <div class="oc-coupon-footer" id="oc-coupon-footer">
              <span class="oc-coupon-available-text" id="oc-coupon-count-text">${config.coupons.length} coupon available</span>
              <a class="oc-coupon-view-btn" id="oc-view-coupons-btn">
                <span>View Coupons</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
              </a>
            </div>
          </div>



          <!-- Order Special Instructions -->
          <div class="oc-instructions-section" id="oc-instructions-section" style="${(config.instructions && config.instructions.enabled === false) ? 'display:none;' : ''}">
            <div class="oc-instructions-toggle ${(config.instructions && config.instructions.defaultOpen) ? 'active' : ''}" id="oc-instructions-toggle" role="button" tabindex="0">
              <span class="oc-instructions-toggle-text">${(config.instructions && config.instructions.label) ? config.instructions.label : 'Add Special Instructions'}</span>
              <span class="oc-instructions-toggle-icon">▾</span>
            </div>
            <div class="oc-instructions-body ${(config.instructions && config.instructions.defaultOpen) ? 'oc-show active' : ''}" id="oc-instructions-body">
              <textarea class="oc-instructions-textarea" id="oc-instructions-textarea" placeholder="${(config.instructions && config.instructions.placeholder) ? config.instructions.placeholder : 'Special delivery, gift message, or packaging notes for this order...'}"></textarea>
            </div>
          </div>

          <!-- Upsell / "You May Also Like!" Section (Dynamic Product-Based Upsells) -->
          <div class="oc-upsell-section" id="oc-upsell-section" style="${config.upsell.enabled ? '' : 'display:none;'}">
            <div class="oc-upsell-header" id="oc-upsell-header">
              <span class="oc-upsell-title-heading" id="oc-upsell-title">${config.upsell.title || 'You May Also Like!'}</span>
              <span class="oc-upsell-badge-pill" id="oc-upsell-badge">${config.upsell.badgeText || 'RECOMMENDED'}</span>
            </div>
            <div class="oc-upsell-grid oc-upsell-layout--${config.upsell.layout || 'grid'}" id="oc-upsell-grid">
              <!-- Dynamic Upsell cards -->
            </div>
          </div>
        </div>

        <!-- View 2: Discounts Subpage (Image 2 Match) -->
        <div class="oc-drawer__view oc-view-discounts" id="oc-view-discounts">
          <div class="oc-coupon-input-wrap">
            <span class="oc-coupon-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>
            </span>
            <input type="text" class="oc-coupon-input" id="oc-subpage-coupon-input" placeholder="Enter Coupon Code" autocomplete="off" spellcheck="false">
            <button class="oc-coupon-apply-btn" id="oc-subpage-apply-btn">Apply</button>
          </div>

          <!-- Subpage Ineligible Validation Error -->
          <div class="oc-coupon-error" id="oc-subpage-coupon-error"></div>

          <!-- Important Application Note on Coupons / Offers Page -->
          <div class="oc-offers-note-card">
            <span class="oc-offers-note-icon">📌</span>
            <div class="oc-offers-note-content">
              <strong>Important Note:</strong> Coupon codes must be entered in the discount box at <strong>checkout</strong> to redeem your discount and view your final savings.
            </div>
          </div>

          <div class="oc-offers-container">
            <h3 class="oc-offers-title">Available Offers</h3>
            <div id="oc-offers-list">
              <!-- Injected Offers -->
            </div>
          </div>
        </div>

      </div>

      <!-- Toast Notification (Top Positioned) -->
      <div class="oc-toast-banner" id="oc-toast-banner" role="alert" aria-live="assertive">
        <span class="oc-toast-msg" id="oc-toast-msg">Removed Gift Wrap from cart</span>
        <button type="button" class="oc-toast-close" id="oc-toast-close" aria-label="Dismiss">&times;</button>
      </div>

      <!-- Sticky Footer with Perched Savings Banner -->
      <div class="oc-footer" id="oc-footer">
        <!-- Perched Auto-Savings Banner (Integrated on Top of Estimated Total Card) -->
        <div class="oc-turkish-savings-ribbon oc-savings-style--${config.savingsStyle || 'tab'}" id="oc-turkish-savings-ribbon" style="display:none;">₹150.00 Saved so far!</div>

        <div class="oc-total-row">
          <div class="oc-total-label-wrap">
            <span class="oc-total-icon-badge">${config.currencySymbol}</span>
            <div class="oc-total-label-text-group">
              <span class="oc-total-label-text">Estimated Total</span>
              <span class="oc-total-savings-summary" id="oc-total-savings-summary" style="display:none;">Saved ₹0.00</span>
            </div>
          </div>
          <div class="oc-total-amount-wrap">
            <div class="oc-total-prices-inline">
              <span class="oc-total-compare-price" id="oc-total-compare-price" style="display:none;">₹828.95</span>
              <div class="oc-total-amount" id="oc-total-amount">₹0.00</div>
            </div>
            <div class="oc-total-discount-tag" id="oc-total-discount-tag" style="display:none;">(18% OFF)</div>
          </div>
        </div>

        <div class="oc-tc-row" id="oc-tc-row" style="${config.footer && config.footer.termsRequired ? 'display:flex;' : 'display:none;'}">
          <input type="checkbox" id="oc-tc-checkbox" ${config.footer && config.footer.termsRequired ? 'checked' : ''}>
          <label for="oc-tc-checkbox">
            ${(config.footer && config.footer.termsText) || 'T&C'}
          </label>
        </div>

        <!-- Reminder banner above checkout button when coupon is active -->
        <div class="oc-checkout-coupon-notice" id="oc-checkout-coupon-notice" style="display: none;">
          <div class="oc-checkout-coupon-notice-inner">
            <span class="oc-checkout-notice-icon">🏷️</span>
            <span class="oc-checkout-notice-text">
              Apply coupon <strong id="oc-checkout-notice-code"></strong> at checkout
            </span>
            <button type="button" class="oc-checkout-notice-copy-btn" id="oc-checkout-notice-copy-btn" title="Copy code">
              Copy
            </button>
          </div>
        </div>

        <button class="oc-checkout-btn" id="oc-checkout-btn">
          <span>${(config.footer && config.footer.checkoutBtnText) || 'Checkout Now'}</span>
        </button>

        <div class="oc-trust-badges" id="oc-trust-badges" style="${config.footer && config.footer.showTrustBadges !== false ? '' : 'display:none;'}">
          ${renderTrustBadgesHtml()}
        </div>
      </div>

      <!-- Free Gift Selection Bottom Sheet / Modal (Screenshot 2 Match) -->
      <div class="oc-gift-modal-backdrop" id="oc-gift-modal-backdrop"></div>
      <div class="oc-gift-modal" id="oc-gift-modal" role="dialog" aria-modal="true" aria-label="Free Gift Selection">
        <div class="oc-gift-modal__drag-handle"></div>
        <div class="oc-gift-modal__header">
          <h3 class="oc-gift-modal__title" id="oc-gift-modal-title">${(config.giftSelection && config.giftSelection.modalTitle) ? config.giftSelection.modalTitle : "You've unlocked a free gift! 🎁"}</h3>
          <button class="oc-gift-modal__close" id="oc-gift-modal-close" type="button" aria-label="Close Free Gift Selection">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>
        <div class="oc-gift-modal__pill-banner" id="oc-gift-modal-pill-banner">
          <span class="oc-gift-modal__pill-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"></polyline><rect x="2" y="7" width="20" height="5"></rect><line x1="12" y1="22" x2="12" y2="7"></line><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path></svg>
          </span>
          <span id="oc-gift-modal-subtitle">${(config.giftSelection && config.giftSelection.modalSubtitle) ? config.giftSelection.modalSubtitle : 'You can claim 1 more gift'}</span>
        </div>
        <div class="oc-gift-modal__products" id="oc-gift-modal-products">
          <!-- Populated dynamically with gift products -->
        </div>
        <div class="oc-gift-modal__footer">
          <button class="oc-gift-modal__save-btn" id="oc-gift-modal-save-btn" type="button">
            ${(config.giftSelection && config.giftSelection.saveBtnText) ? config.giftSelection.saveBtnText : 'Save'}
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(drawer);

    // Floating Bubble / Sticky Add-to-Cart Button
    const isStickyEnabled = config.showFloatingBubble && (!config.stickyAtc || config.stickyAtc.enabled !== false);
    if (isStickyEnabled) {
      const bubble = document.createElement('button');
      bubble.id = 'oc-floating-cart-trigger';
      bubble.className = 'oc-floating-cart-trigger';
      bubble.setAttribute('aria-label', 'Open Cart');

      if (config.stickyAtc) {
        const atc = config.stickyAtc;
        if (atc.bottomOffset !== undefined) bubble.style.bottom = `${atc.bottomOffset}px`;
        if (atc.size !== undefined) {
          bubble.style.width = `${atc.size}px`;
          bubble.style.height = `${atc.size}px`;
        }
        if (atc.bgColor) bubble.style.background = atc.bgColor;
        if (atc.iconColor) bubble.style.color = atc.iconColor;

        if (atc.position === 'left') {
          bubble.style.left = `${atc.sideOffset !== undefined ? atc.sideOffset : 24}px`;
          bubble.style.right = 'auto';
        } else if (atc.position === 'center') {
          bubble.style.left = '50%';
          bubble.style.right = 'auto';
          bubble.style.transform = 'translateX(-50%)';
        } else {
          bubble.style.right = `${atc.sideOffset !== undefined ? atc.sideOffset : 24}px`;
          bubble.style.left = 'auto';
        }
      }

      bubble.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin: auto; display: block;"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
        <span class="oc-floating-cart-count" id="oc-floating-cart-count">0</span>
      `;
      document.body.appendChild(bubble);
      bubble.addEventListener('click', () => OrgaciaCart.open());
    }

    bindEvents();
    renderMilestones();
    renderUpsells();
    renderOffers();
    startCountdown();
    initAnnouncementCarousel();
  }

  // Render Trust Badges markup (supporting string badges or structured { text, icon, color } objects)
  function renderTrustBadgesHtml() {
    if (!config.footer || config.footer.showTrustBadges === false) return '';
    const badges = config.footer.trustBadges || [];
    if (!Array.isArray(badges) || badges.length === 0) return '';

    return badges.map(badge => {
      if (typeof badge === 'string') {
        return `<span>${badge}</span>`;
      }
      const icon = badge.icon !== undefined ? badge.icon : '✓';
      const text = badge.text || '';
      const customColor = badge.color ? `style="color: ${badge.color};"` : '';
      const iconMarkup = icon ? `<span class="oc-trust-badge-icon">${icon}</span>` : '';
      return `<span ${customColor}>${iconMarkup}<span>${text}</span></span>`;
    }).join('');
  }

  // Announcement Bar Carousel State
  let announcementCarouselTimer = null;
  let activeAnnouncementIndex = 0;

  // Initialize and rotate Announcement Bar Carousel with individual slide backgrounds and text colors
  function initAnnouncementCarousel() {
    if (announcementCarouselTimer) {
      clearInterval(announcementCarouselTimer);
      announcementCarouselTimer = null;
    }

    const barEl = document.getElementById('oc-announcement-bar');
    const trackEl = document.getElementById('oc-announcement-track');
    if (!barEl || !trackEl) return;

    if (!config.announcement || config.announcement.enabled === false) {
      barEl.style.display = 'none';
      return;
    }

    barEl.style.display = '';

    // Prepare slides array
    let slides = [];
    if (Array.isArray(config.announcement.slides) && config.announcement.slides.length > 0) {
      slides = config.announcement.slides;
    } else if (config.announcement.text) {
      slides = [{
        text: config.announcement.text,
        link: config.announcement.link || '',
        bgColor: config.announcement.bgColor || '#000000',
        textColor: config.announcement.textColor || '#FFFFFF'
      }];
    }

    if (slides.length === 0) {
      barEl.style.display = 'none';
      return;
    }

    activeAnnouncementIndex = 0;

    // Render slides HTML
    trackEl.innerHTML = slides.map((slide, idx) => {
      const isFirst = idx === 0;
      const content = slide.link
        ? `<a href="${slide.link}" class="oc-announcement-link" style="color: inherit; text-decoration: none;">${slide.text}</a>`
        : `<span>${slide.text}</span>`;

      return `
        <div class="oc-announcement-slide ${isFirst ? 'oc-active' : ''}" 
             data-slide-index="${idx}"
             style="background-color: ${slide.bgColor || '#000000'}; color: ${slide.textColor || '#FFFFFF'};">
          ${content}
        </div>
      `;
    }).join('');

    // Apply active slide style to container for seamless edge-to-edge aesthetics
    const initialSlide = slides[0];
    barEl.style.backgroundColor = initialSlide.bgColor || '#000000';
    barEl.style.color = initialSlide.textColor || '#FFFFFF';

    // Pause rotation when hovering over announcement bar if pauseOnHover is enabled
    if (config.announcement.pauseOnHover !== false) {
      barEl.addEventListener('mouseenter', pauseAnnouncementCarousel);
      barEl.addEventListener('mouseleave', () => {
        const drawer = document.getElementById('oc-drawer');
        if (drawer && drawer.classList.contains('oc-active')) {
          startAnnouncementCarousel(slides);
        }
      });
    }

    // Start auto-rotation only if cart drawer is currently open
    const drawerEl = document.getElementById('oc-drawer');
    if (drawerEl && drawerEl.classList.contains('oc-active')) {
      startAnnouncementCarousel(slides);
    }
  }

  // Start rotation timer (with zero overhead)
  function startAnnouncementCarousel(slides) {
    pauseAnnouncementCarousel();
    const slidesList = slides || (config.announcement && config.announcement.slides) || [];
    if (!slidesList || slidesList.length <= 1 || (config.announcement && config.announcement.autoplay === false)) return;

    const intervalMs = Math.max(1500, parseInt(config.announcement.interval, 10) || 4000);
    announcementCarouselTimer = setInterval(() => {
      // Safety check: if drawer is not open or document is hidden, do nothing (0ms CPU impact)
      if (document.hidden) return;
      const drawer = document.getElementById('oc-drawer');
      if (drawer && !drawer.classList.contains('oc-active')) return;

      rotateAnnouncementSlide(slidesList);
    }, intervalMs);
  }

  // Pause rotation timer completely (0 timer ticks while cart drawer is closed)
  function pauseAnnouncementCarousel() {
    if (announcementCarouselTimer) {
      clearInterval(announcementCarouselTimer);
      announcementCarouselTimer = null;
    }
  }

  // Rotate to next announcement slide with smooth crossfade
  function rotateAnnouncementSlide(slides) {
    if (!slides || slides.length <= 1) return;
    const barEl = document.getElementById('oc-announcement-bar');
    const trackEl = document.getElementById('oc-announcement-track');
    if (!barEl || !trackEl) return;

    const slideElements = trackEl.querySelectorAll('.oc-announcement-slide');
    if (slideElements.length === 0) return;

    // Fade out current slide
    slideElements[activeAnnouncementIndex].classList.remove('oc-active');

    // Advance index
    activeAnnouncementIndex = (activeAnnouncementIndex + 1) % slides.length;

    // Fade in new slide
    const nextSlideEl = slideElements[activeAnnouncementIndex];
    const nextSlideData = slides[activeAnnouncementIndex];

    if (nextSlideEl) {
      nextSlideEl.classList.add('oc-active');
    }

    // Smoothly update bar background and text color to match slide's individual colors
    if (nextSlideData) {
      barEl.style.backgroundColor = nextSlideData.bgColor || '#000000';
      barEl.style.color = nextSlideData.textColor || '#FFFFFF';
    }
  }

  // Get icon markup for milestone pin (supports %, gift 🎁, truck 🚚, custom SVG or emoji)
  function getMilestoneIconHtml(icon, label) {
    const iconStr = (icon || '').trim();
    const labelStr = (label || '').toLowerCase();

    // 1. Free Gift detection (if label contains 'gift' or icon is gift)
    if (
      iconStr === '🎁' ||
      iconStr.toLowerCase() === 'gift' ||
      iconStr.toLowerCase() === 'free gift' ||
      labelStr.includes('gift')
    ) {
      return '🎁';
    }

    // 2. Percentage / Discount detection (if label contains '%' or 'off' or 'discount', or icon is '%' or '🏷️' / 'tag')
    if (
      iconStr === '%' ||
      iconStr.toLowerCase() === 'percent' ||
      iconStr.toLowerCase() === 'percentage' ||
      iconStr === '🏷️' ||
      iconStr.toLowerCase() === 'tag' ||
      labelStr.includes('%') ||
      labelStr.includes('off') ||
      labelStr.includes('discount')
    ) {
      return '<svg class="oc-pin-percent-svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="5" x2="5" y2="19"></line><circle cx="6.5" cy="6.5" r="2.5"></circle><circle cx="17.5" cy="17.5" r="2.5"></circle></svg>';
    }

    // 3. Free Shipping / Delivery detection
    if (
      iconStr === '🚚' ||
      iconStr.toLowerCase() === 'truck' ||
      iconStr.toLowerCase() === 'delivery' ||
      iconStr.toLowerCase() === 'shipping' ||
      labelStr.includes('deliver') ||
      labelStr.includes('shipping')
    ) {
      return '🚚';
    }

    return iconStr || '🎁';
  }

  // Render Milestones on Progress Track (Equidistant Milestone Placement + 50% Centering)
  function renderMilestones() {
    const milestonesEl = document.getElementById('oc-milestones');
    const progressSection = document.getElementById('oc-progress-section');
    if (!milestonesEl) return;

    if (progressSection) {
      const activeStyle = (config.tiers && config.tiers.style) || 'classic-glow';
      progressSection.className = `oc-progress-section oc-tiers-style--${activeStyle}`;
    }

    milestonesEl.innerHTML = '';

    const rawTiers = (config.tiers && config.tiers.items) || [];
    const tiers = rawTiers.filter(t => t && t.threshold !== undefined && t.enabled !== false);
    if (!tiers || tiers.length === 0) {
      if (progressSection) progressSection.style.display = 'none';
      return;
    }
    const currentAmount = cartState.original_total_price;
    const numTiers = tiers.length;

    // Equal distance calculation:
    // Lead-in inset of 14% on each end so pins and labels never clip the container edges.
    // For 3 tiers (Free Delivery, 5% OFF, Free Gift):
    // Index 0 = 14%, Index 1 = 50% (EXACT CENTER), Index 2 = 86%.
    // Distance from Tier 0 to Tier 1 is exactly 36%, and from Tier 1 to Tier 2 is exactly 36%.
    const inset = numTiers > 2 ? 14 : (numTiers === 2 ? 20 : 50);
    const span = 100 - (2 * inset);

    tiers.forEach((tier, index) => {
      const percentage = numTiers > 1 ? inset + (index / (numTiers - 1)) * span : 50;
      const isUnlocked = currentAmount >= tier.threshold;

      let edgeClass = 'oc-milestone--center';
      if (index === 0) {
        edgeClass = 'oc-milestone--start';
      } else if (index === numTiers - 1) {
        edgeClass = 'oc-milestone--end';
      }

      // Atomic Milestone Node (Pin + Label physically joined together)
      const node = document.createElement('div');
      node.className = `oc-milestone ${edgeClass} ${isUnlocked ? 'oc-unlocked' : ''}`;
      node.id = `oc-milestone-${index}`;
      node.style.left = `${percentage}%`;
      node.innerHTML = `
        <div class="oc-milestone-pin" data-tier-index="${index}">
          ${isUnlocked ? '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>' : getMilestoneIconHtml(tier.icon, tier.label)}
        </div>
        <div class="oc-milestone-label">
          <span class="oc-milestone-reward-text">${tier.label}</span>
        </div>
      `;

      if (tier.rewardType === 'gift' || tier.icon === '🎁' || (tier.label && tier.label.toLowerCase().includes('gift'))) {
        node.style.cursor = 'pointer';
        node.setAttribute('title', 'Click to claim or choose free gift');
        node.addEventListener('click', (e) => {
          e.stopPropagation();
          if (isGiftEligible()) {
            openGiftModal();
          } else {
            const current = cartState.original_total_price || 0;
            const remaining = Math.max(0, tier.threshold - current);
            showToast(`Add ₹${remaining.toFixed(0)} more to unlock your free gift! 🎁`);
          }
        });
      }

      milestonesEl.appendChild(node);
    });
  }

  // ================================================================
  // Free Gift Milestone Selection Engine (Slot-Based Multi/Single Selection)
  // ================================================================
  // Tracks selection by unique slot keys (e.g., 'gift_slot_0', 'gift_slot_1') so products
  // with identical fallback IDs never mirror or double-select each other.
  let selectedGiftSlotKeys = [];

  // Image cache set to prevent redundant network instantiation
  const preloadedImageCache = new Set();

  function preloadImage(url) {
    if (!url || typeof url !== 'string' || url.startsWith('data:') || preloadedImageCache.has(url)) return;
    if (url.includes('snowboard_') || url.includes('placeholder')) return;
    preloadedImageCache.add(url);
    try {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    } catch (e) {}
  }

  // Preload all drawer images (gifts, upsells, swap tier thumbnails, cart items) into memory for 0ms instantaneous rendering
  function preloadAllDrawerAssets() {
    // 1. Gift selection products
    const giftProducts = (config.giftSelection && config.giftSelection.products) || [];
    giftProducts.forEach(p => {
      if (p && p.image) preloadImage(p.image);
    });

    // 2. Candidate upsell items
    try {
      if (typeof getCandidateUpsells === 'function') {
        const upsells = getCandidateUpsells();
        if (Array.isArray(upsells)) {
          upsells.forEach(u => {
            if (u && u.image) preloadImage(u.image);
          });
        }
      }
    } catch (e) {}

    // 3. Upsell catalogPool and dynamic rules
    if (config.upsell) {
      if (Array.isArray(config.upsell.items)) {
        config.upsell.items.forEach(u => u && u.image && preloadImage(u.image));
      }
      if (Array.isArray(config.upsell.catalogPool)) {
        config.upsell.catalogPool.forEach(u => u && u.image && preloadImage(u.image));
      }
      if (config.upsell.dynamicRules && typeof config.upsell.dynamicRules === 'object') {
        Object.values(config.upsell.dynamicRules).forEach(arr => {
          if (Array.isArray(arr)) {
            arr.forEach(u => u && u.image && preloadImage(u.image));
          }
        });
      }
    }

    // 4. Cart items and dynamic bundle tier upgrades
    if (cartState && Array.isArray(cartState.items)) {
      cartState.items.forEach(item => {
        if (item && item.image) preloadImage(item.image);
        try {
          if (typeof getDynamicBundleTiers === 'function') {
            const tiers = getDynamicBundleTiers(item);
            if (Array.isArray(tiers)) {
              tiers.forEach(t => {
                if (t && t.image) preloadImage(t.image);
                if (t && t.gift && t.gift.image) preloadImage(t.gift.image);
              });
            }
          }
        } catch (e) {}
      });
    }

    // 5. Configured asset fallbacks
    if (config.assetsFallback) {
      if (config.assetsFallback.bottleB) preloadImage(config.assetsFallback.bottleB);
      if (config.assetsFallback.bottleD) preloadImage(config.assetsFallback.bottleD);
    }
  }

  function preloadGiftImages() {
    preloadAllDrawerAssets();
  }

  // Helper to calculate total amount spent strictly on regular non-gift, non-giftwrap products
  function getRegularSubtotal() {
    if (!cartState.items || cartState.items.length === 0) return 0;
    return cartState.items
      .filter(i => !i.isGift && !i.isGiftWrap)
      .reduce((sum, itm) => {
        const lineQty = Math.max(1, parseInt(itm.quantity, 10) || 1);
        const unitPrice = config.roundDecimals 
          ? Math.round(Number(itm.calculatedPrice !== undefined ? itm.calculatedPrice : itm.price) || 0)
          : (Number(itm.calculatedPrice !== undefined ? itm.calculatedPrice : itm.price) || 0);
        return sum + (unitPrice * lineQty);
      }, 0);
  }

  // Get max allowed gifts based on the achieved milestone tier or bundle tier
  function getMaxAllowedGifts() {
    if (!config.giftSelection || config.giftSelection.enabled === false) return 0;
    const currentAmount = getRegularSubtotal();
    const tiers = (config.tiers && config.tiers.items) || [];
    // Find highest achieved gift tier
    const achievedGiftTiers = tiers.filter(t => (t.rewardType === 'gift' || t.icon === '🎁' || (t.label && t.label.toLowerCase().includes('gift'))) && currentAmount >= t.threshold);
    if (achievedGiftTiers.length > 0) {
      const topTier = achievedGiftTiers[achievedGiftTiers.length - 1];
      if (topTier.maxGifts) return Math.max(1, parseInt(topTier.maxGifts, 10) || 1);
      return (config.giftSelection && Math.max(1, parseInt(config.giftSelection.maxGiftsAllowed, 10))) || 1;
    }
    // Check if an item in cart has an active bundle tier granting a gift
    const hasBundleGift = cartState.items && cartState.items.some(i => {
      if (i.isGift || i.isGiftWrap) return false;
      const qty = Math.max(1, parseInt(i.quantity, 10) || 1);
      const bTiers = getDynamicBundleTiers(i);
      const matched = bTiers.find(t => qty >= (t.minQty || t.qty || 1));
      return matched && (matched.hasGift === true || matched.gift === true);
    });
    if (hasBundleGift) {
      return (config.giftSelection && Math.max(1, parseInt(config.giftSelection.maxGiftsAllowed, 10))) || 1;
    }
    return 0;
  }

  // Check if Gift Milestone has been completed / unlocked
  function isGiftMilestoneUnlocked(overrideAmount) {
    if (!config.giftSelection || config.giftSelection.enabled === false) return false;
    const tiers = (config.tiers && config.tiers.items) || [];
    if (tiers.length === 0) return false;
    // Must have at least one main non-gift product in cart
    const hasMainProducts = cartState.items && cartState.items.some(i => !i.isGift && !i.isGiftWrap);
    if (!hasMainProducts) return false;

    const currentAmount = typeof overrideAmount === 'number' ? overrideAmount : getRegularSubtotal();
    // Strictly look for an active milestone tier explicitly designated as Free Gift
    const giftTier = tiers.find(t => (t.rewardType === 'gift' || t.icon === '🎁' || (t.label && t.label.toLowerCase().includes('gift'))) && t.enabled !== false);
    if (giftTier) {
      return currentAmount >= giftTier.threshold;
    }
    // If NO tier is designated as a Free Gift, the gift milestone is NOT active
    return false;
  }

  // Check overall gift eligibility: either milestone amount is reached or an active bundle tier includes a gift
  function isGiftEligible(overrideAmount) {
    if (!config.giftSelection || config.giftSelection.enabled === false) return false;
    const hasMainProducts = cartState.items && cartState.items.some(i => !i.isGift && !i.isGiftWrap);
    if (!hasMainProducts) return false;

    if (isGiftMilestoneUnlocked(overrideAmount)) {
      return true;
    }

    const hasBundleGift = cartState.items.some(i => {
      if (i.isGift || i.isGiftWrap) return false;
      const qty = Math.max(1, parseInt(i.quantity, 10) || 1);
      const tiers = getDynamicBundleTiers(i);
      const matched = tiers.find(t => qty >= (t.minQty || t.qty || 1));
      return matched && (matched.hasGift === true || matched.gift === true);
    });

    return !!hasBundleGift;
  }

  // Open Free Gift Selection Bottom Sheet Modal
  function openGiftModal(e) {
    if (e) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
    }
    const modal = document.getElementById('oc-gift-modal');
    const backdrop = document.getElementById('oc-gift-modal-backdrop');
    if (!modal || !backdrop) return;

    // Immediately trigger image cache warmup
    preloadGiftImages();

    const maxGifts = getMaxAllowedGifts() || 1;
    const products = (config.giftSelection && Array.isArray(config.giftSelection.products) && config.giftSelection.products.length > 0)
      ? config.giftSelection.products
      : (DEFAULT_CONFIG.giftSelection.products || []);

    // Pre-populate selection: map claimed gifts currently in cart back to slot keys
    const existingGifts = cartState.items.filter(i => i.isGift);
    selectedGiftSlotKeys = [];
    if (existingGifts.length > 0) {
      existingGifts.forEach(g => {
        const slotIdx = products.findIndex(p => String(p.id) === String(g.id) || p.title === g.title);
        if (slotIdx > -1) {
          const k = 'gift_slot_' + slotIdx;
          if (!selectedGiftSlotKeys.includes(k) && selectedGiftSlotKeys.length < maxGifts) {
            selectedGiftSlotKeys.push(k);
          }
        }
      });
    }

    renderGiftModalProducts();
    backdrop.style.display = 'block';
    modal.style.display = 'flex';
    void modal.offsetWidth;
    backdrop.classList.add('oc-active');
    modal.classList.add('oc-active');
  }

  // Close Free Gift Selection Modal
  function closeGiftModal() {
    const modal = document.getElementById('oc-gift-modal');
    const backdrop = document.getElementById('oc-gift-modal-backdrop');
    if (modal) {
      modal.classList.remove('oc-active');
      setTimeout(() => {
        if (!modal.classList.contains('oc-active')) modal.style.display = 'none';
      }, 300);
    }
    if (backdrop) {
      backdrop.classList.remove('oc-active');
      setTimeout(() => {
        if (!backdrop.classList.contains('oc-active')) backdrop.style.display = 'none';
      }, 300);
    }
  }

  // Render Product Cards inside Gift Modal (Immediate Image Loading + Unique Slots)
  function renderGiftModalProducts() {
    const container = document.getElementById('oc-gift-modal-products');
    const subtitleEl = document.getElementById('oc-gift-modal-subtitle');
    if (!container) return;

    const products = (config.giftSelection && Array.isArray(config.giftSelection.products) && config.giftSelection.products.length > 0)
      ? config.giftSelection.products
      : (DEFAULT_CONFIG.giftSelection.products || []);

    if (products.length === 0) {
      container.innerHTML = '<div style="padding:24px;text-align:center;color:#6B7280;font-size:13px;">No gift products currently available.</div>';
      return;
    }

    const maxGifts = getMaxAllowedGifts() || 1;
    const remainingToPick = Math.max(0, maxGifts - selectedGiftSlotKeys.length);

    // Dynamically update subtitle banner
    if (subtitleEl) {
      if (remainingToPick > 0) {
        subtitleEl.textContent = `You can claim ${remainingToPick} more ${remainingToPick === 1 ? 'gift' : 'gifts'}`;
      } else {
        subtitleEl.textContent = `All ${maxGifts} free gifts selected!`;
      }
    }

    container.innerHTML = products.map((prod, index) => {
      const slotKey = 'gift_slot_' + index;
      const isSelected = selectedGiftSlotKeys.includes(slotKey);
      const isClaimedInCart = cartState.items.some(i => i.isGift && (String(i.id) === String(prod.id) || i.giftSlotKey === slotKey));
      const isCardSelected = isSelected || isClaimedInCart;
      const imgSrc = getValidImageUrl(prod.image);
      const prodOriginalPrice = parseFloat(prod.originalPrice || prod.price || 0);

      return `
        <div class="oc-gift-product-card ${isCardSelected ? 'oc-selected' : ''}" data-gift-slot="${slotKey}" data-slot-index="${index}" data-gift-id="${prod.id}">
          <div class="oc-gift-product-card__img-wrap">
            <img src="${imgSrc}" alt="${escapeHTML(prod.title || 'Gift Product')}" loading="eager" decoding="async" fetchpriority="high" onerror="this.onerror=null; this.src='${ORGACIA_DEFAULT_PRODUCT_SVG}';">
          </div>
          <div class="oc-gift-product-card__info">
            <div class="oc-gift-product-card__title" title="${prod.title || ''}">${prod.title || 'Gift Product'}</div>
            <div class="oc-gift-product-card__price">
              <span class="oc-gift-price-free" style="color: #059669; font-weight: 700; font-size: 13px;">FREE</span>
              ${prodOriginalPrice > 0 ? `<span class="oc-gift-price-compare" style="text-decoration: line-through; color: #9CA3AF; margin-left: 6px; font-size: 11px;">${formatMoney(prodOriginalPrice)}</span>` : ''}
            </div>
          </div>
          <button class="oc-gift-product-card__add-btn ${isCardSelected ? 'oc-added' : ''}" type="button" data-gift-slot="${slotKey}" data-slot-index="${index}" data-gift-id="${prod.id}">
            ${isCardSelected ? '✓ Selected' : '+ Quick Add'}
          </button>
        </div>
      `;
    }).join('');

    updateGiftSaveButtonState();
  }

  // Update Save button state based on current selection
  function updateGiftSaveButtonState() {
    const saveBtn = document.getElementById('oc-gift-modal-save-btn');
    if (!saveBtn) return;
    const btnLabel = (config.giftSelection && config.giftSelection.saveBtnText) ? config.giftSelection.saveBtnText : 'Save';
    if (selectedGiftSlotKeys.length > 0) {
      saveBtn.classList.add('oc-gift-modal__save-btn--active');
      saveBtn.textContent = btnLabel;
    } else {
      saveBtn.classList.remove('oc-gift-modal__save-btn--active');
      saveBtn.textContent = btnLabel;
    }
  }

  // Toggle selection of a gift product (Strict auto-deselect replacement for max=1, 2, 3...)
  function toggleGiftProduct(slotIdentifier) {
    const slotKey = String(slotIdentifier);
    const maxGifts = getMaxAllowedGifts();
    const idx = selectedGiftSlotKeys.indexOf(slotKey);

    if (idx > -1) {
      // Deselect clicked product
      selectedGiftSlotKeys.splice(idx, 1);
    } else {
      // Select clicked product
      if (maxGifts === 1) {
        // When max is 1, selecting a product automatically deselects any previously chosen product
        selectedGiftSlotKeys = [slotKey];
      } else {
        // When max is 2, 3, 4, etc.
        if (selectedGiftSlotKeys.length < maxGifts) {
          selectedGiftSlotKeys.push(slotKey);
        } else {
          // At maximum: automatically deselect the oldest chosen product and add the new one
          selectedGiftSlotKeys.shift();
          selectedGiftSlotKeys.push(slotKey);
        }
      }
    }

    renderGiftModalProducts();
  }

  // Remove gift items from Shopify backend cart
  function removeGiftFromShopify(gifts) {
    if (!gifts || gifts.length === 0) return;
    gifts.forEach(g => {
      tombstoneItem(g);
    });
    if (!isShopifyEnv) return;
    const updates = {};
    gifts.forEach(g => {
      const k = g.key || g.variantId || g.id;
      if (k) updates[k] = 0;
    });
    if (Object.keys(updates).length > 0) {
      const fetchFn = rawFetch || window.fetch;
      fetchFn('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      }).catch(() => {});
    }
  }

  // Confirm and save the claimed gift product(s)
  async function saveGiftClaim() {
    const products = (config.giftSelection && config.giftSelection.products) || [];
    const maxGifts = getMaxAllowedGifts();

    if (selectedGiftSlotKeys.length === 0) {
      if (products.length > 0) {
        selectedGiftSlotKeys = ['gift_slot_0'];
      } else {
        closeGiftModal();
        return;
      }
    }

    // Capture previously claimed gifts to remove if replacing selection
    const oldGifts = cartState.items.filter(i => i.isGift);

    // Prepare new gift items
    const newGiftItems = [];
    const shopifyItemsToAdd = [];

    selectedGiftSlotKeys.slice(0, maxGifts).forEach(slotKey => {
      const slotIndex = parseInt(slotKey.replace('gift_slot_', ''), 10);
      const chosenProduct = products[slotIndex] || products.find(p => String(p.id) === slotKey);
      if (chosenProduct) {
        const giftObj = {
          id: chosenProduct.id || ('gift_' + slotIndex),
          variantId: chosenProduct.id || ('gift_' + slotIndex),
          productId: chosenProduct.productId || chosenProduct.id,
          title: chosenProduct.title,
          price: 0,
          basePrice: 0,
          calculatedPrice: 0,
          originalPrice: chosenProduct.originalPrice || chosenProduct.price || 2000,
          compare_at_price: null,
          image: chosenProduct.image,
          badgeText: chosenProduct.badgeText || 'FREE GIFT',
          isGift: true,
          giftSlotKey: slotKey,
          quantity: 1
        };
        newGiftItems.push(giftObj);

        const vId = Number(chosenProduct.id);
        if (!isNaN(vId) && vId > 0) {
          shopifyItemsToAdd.push({
            id: vId,
            quantity: 1,
            properties: {
              '_is_free_gift': 'true',
              '_gift_slot': slotKey,
              'Promotion': 'Free Gift Reward'
            }
          });
        }
      }
    });

    // Optimistically update local cart items
    cartState.items = cartState.items.filter(i => !i.isGift);
    newGiftItems.forEach(g => cartState.items.unshift(g));

    closeGiftModal();
    updateCartCalculations();
    triggerConfetti();
    if (newGiftItems.length > 0) {
      showToast('🎉 Free gift added to your cart!');
    }

    // Sync with Shopify backend cart
    if (isShopifyEnv) {
      showLoader();
      try {
        const fetchFn = rawFetch || window.fetch;
        if (oldGifts.length > 0) {
          const updates = {};
          oldGifts.forEach(og => {
            const k = og.key || og.variantId || og.id;
            if (k) updates[k] = 0;
          });
          if (Object.keys(updates).length > 0) {
            await fetchFn('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates })
            }).catch(() => {});
          }
        }

        if (shopifyItemsToAdd.length > 0) {
          const addRes = await fetchFn('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: shopifyItemsToAdd })
          });
          if (addRes.ok) {
            const freshCart = await fetchFn(`/cart.js?_t=${Date.now()}`, { cache: 'no-store' }).then(r => r.json());
            OrgaciaCart.syncWithCart(freshCart);
          }
        }
      } catch (err) {
        console.warn('[Orgacia] Error syncing free gift with Shopify backend:', err);
      } finally {
        hideLoader(true);
      }
    }
  }

  // Dynamic Upsell Resolver: Evaluates trigger products in cart and maps to upsells
  function getActiveUpsells() {
    if (!config.upsell || config.upsell.enabled === false) return [];
    if (!cartState.items || cartState.items.length === 0) return [];

    const activeRegularItems = cartState.items.filter(i => !i.isGift && !i.isGiftWrap);
    const dynamicRules = (config.upsell && config.upsell.rules) || {};
    let candidateUpsells = [];

    // 1. Check if any product in cart has a mapped upsell rule (exact or fuzzy/keyword match)
    for (const item of activeRegularItems) {
      const itemIdStr = String(item.id || '').toLowerCase();
      const itemTitleStr = String(item.title || '').toLowerCase();
      const itemHandleStr = String(item.handle || '').toLowerCase();

      // Direct ID / handle matches
      if (dynamicRules[item.id] && Array.isArray(dynamicRules[item.id]) && dynamicRules[item.id].length > 0) {
        candidateUpsells.push(...dynamicRules[item.id]);
      } else if (item.variant_id && dynamicRules[item.variant_id]) {
        candidateUpsells.push(...dynamicRules[item.variant_id]);
      } else if (item.product_id && dynamicRules[item.product_id]) {
        candidateUpsells.push(...dynamicRules[item.product_id]);
      } else if (item.handle && dynamicRules[item.handle]) {
        candidateUpsells.push(...dynamicRules[item.handle]);
      }

      // Keyword / category fuzzy matches
      if (itemIdStr.includes('ashwa') || itemTitleStr.includes('ashwa')) {
        if (dynamicRules['orgacia-ashwagandha']) {
          candidateUpsells.push(...dynamicRules['orgacia-ashwagandha']);
        }
      } else if (itemIdStr.includes('magnesium') || itemTitleStr.includes('magnesium')) {
        if (dynamicRules['orgacia-magnesium']) {
          candidateUpsells.push(...dynamicRules['orgacia-magnesium']);
        }
      } else if (itemIdStr.includes('curcumin') || itemTitleStr.includes('curcumin')) {
        if (dynamicRules['orgacia-curcumin']) {
          candidateUpsells.push(...dynamicRules['orgacia-curcumin']);
        }
      } else if (itemIdStr.includes('snowboard') || itemTitleStr.includes('snowboard') || itemIdStr.includes('prod-')) {
        if (dynamicRules['prod-1']) {
          candidateUpsells.push(...dynamicRules['prod-1']);
        }
      }
    }

    // 2. If no rule matched, use default rule items or liquid configured items
    if (candidateUpsells.length === 0 && dynamicRules['default'] && dynamicRules['default'].length > 0) {
      candidateUpsells.push(...dynamicRules['default']);
    } else if (candidateUpsells.length === 0 && config.upsell.items && config.upsell.items.length > 0) {
      candidateUpsells.push(...config.upsell.items);
    }

    // 3. Fallback catalog pool so upsells NEVER fail to load
    if (candidateUpsells.length < (config.upsell.maxItems || 2)) {
      const fallbackPool = config.upsell.catalogPool && config.upsell.catalogPool.length > 0 ? config.upsell.catalogPool : [
        {
          id: 'orgacia-magnesium',
          title: 'Magnesium Glycinate',
          fullTitle: 'Organic Magnesium Glycinate (60 Veg Caps)',
          price: 899.00,
          comparePrice: 1199.00,
          discountTag: '25% OFF',
          image: ORGACIA_MAGNESIUM_SVG
        },
        {
          id: 'orgacia-curcumin',
          title: 'Bio-Curcumin 95%',
          fullTitle: 'Bio-Enhanced Curcumin 95% + Piperine',
          price: 799.00,
          comparePrice: 999.00,
          discountTag: '20% OFF',
          image: ORGACIA_CURCUMIN_SVG
        },
        {
          id: 'orgacia-vitamind3',
          title: 'Vitamin D3 + K2',
          fullTitle: 'Plant-Based Vitamin D3 + K2 Drops',
          price: 649.00,
          comparePrice: 849.00,
          discountTag: '24% OFF',
          image: ORGACIA_VITAMIND3_SVG
        }
      ];
      candidateUpsells.push(...fallbackPool);
    }

    // 4. Deduplicate candidates
    const uniqueCandidates = [];
    const seenIds = new Set();
    candidateUpsells.forEach(item => {
      if (item && item.id && !seenIds.has(String(item.id))) {
        seenIds.add(String(item.id));
        uniqueCandidates.push(item);
      }
    });

    // 5. Filter out products that are ALREADY in the customer's cart
    const cartIds = new Set(cartState.items.map(i => String(i.id || '')));
    const cartTitles = new Set(cartState.items.map(i => (i.title || '').toLowerCase()));
    const finalUpsells = uniqueCandidates.filter(item => {
      const idMatch = cartIds.has(String(item.id));
      const titleMatch = cartTitles.has((item.fullTitle || item.title || '').toLowerCase());
      return !idMatch && !titleMatch;
    });

    return finalUpsells.slice(0, config.upsell.maxItems || 2);
  }

  function getBenefitChipForItem(item) {
    if (!item) return '🌿 Clinically Formulated';
    
    // 1. Check custom configured product benefits from Theme Editor or API
    if (config.upsell && Array.isArray(config.upsell.productBenefits) && config.upsell.productBenefits.length > 0) {
      const pId = String(item.productId || item.id || '');
      const handle = String(item.handle || '').toLowerCase().trim();
      const title = String(item.fullTitle || item.title || '').toLowerCase().trim();

      const matched = config.upsell.productBenefits.find(b => {
        if (!b) return false;
        if (b.productId && pId && String(b.productId) === pId) return true;
        if (b.handle && handle && String(b.handle).toLowerCase().trim() === handle) return true;
        if (b.title && title) {
          const bTitle = String(b.title).toLowerCase().trim();
          if (title === bTitle || title.includes(bTitle) || bTitle.includes(title)) return true;
        }
        return false;
      });

      if (matched && matched.tag) {
        const bgStyle = matched.bg ? `background:${matched.bg};` : '';
        const colorStyle = matched.color ? `color:${matched.color};` : '';
        if (bgStyle || colorStyle) {
          return `<span class="oc-custom-tag" style="${bgStyle} ${colorStyle}">${matched.tag}</span>`;
        }
        return matched.tag;
      }
    }

    // 2. Smart formula fallback based on wellness ingredient keywords
    const t = (item.fullTitle || item.title || '').toLowerCase();
    if (t.includes('b-complex') || t.includes('vitamin b')) return '⚡ Energy & Focus';
    if (t.includes('d3') || t.includes('k2')) return '🦴 Bone & Immunity Boost';
    if (t.includes('ashwagandha')) return '🧘 Stress & Sleep Support';
    if (t.includes('magnesium')) return '💤 Deep Sleep & Recovery';
    if (t.includes('curcumin') || t.includes('turmeric')) return '🛡️ Anti-Inflammatory';
    if (t.includes('wax')) return '❄️ Speed & Glide Tuning';
    if (t.includes('glove')) return '🧤 Waterproof Thermal';
    return '🌿 Clinically Formulated';
  }

  function getRatingSnippetForItem(item) {
    const reviews = item.reviewsCount || '1.2k+';
    return `
      <div class="oc-upsell-rating">
        <span class="oc-upsell-stars">★★★★★</span>
        <span class="oc-upsell-rating-val">4.9</span>
        <span class="oc-upsell-review-count">(${reviews})</span>
      </div>
    `;
  }

  // Render Dynamic Upsells with Selected Layout (Grid, Carousel, Compact) & 2026 Social Proof
  function renderUpsells() {
    const section = document.getElementById('oc-upsell-section');
    const grid = document.getElementById('oc-upsell-grid');
    const titleEl = document.getElementById('oc-upsell-title');
    const badgeEl = document.getElementById('oc-upsell-badge');
    if (!grid || !section) return;

    if (!config.upsell || config.upsell.enabled === false || cartState.items.length === 0) {
      section.style.display = 'none';
      return;
    }

    const activeUpsells = getActiveUpsells();
    if (activeUpsells.length === 0) {
      section.style.display = 'none';
      return;
    }

    section.style.display = 'block';
    if (titleEl) titleEl.textContent = config.upsell.title || 'You May Also Like!';
    
    // 2026 Fresh Social Proof Badges with Pulsing Live Dot
    if (badgeEl) {
      const style = (config.upsell && config.upsell.badgeStyle) || 'social_proof';
      badgeEl.className = `oc-upsell-badge-pill oc-badge-style--${style}`;
      if (style === 'social_proof') {
        badgeEl.innerHTML = `<span class="oc-pulse-dot"></span> 🔥 87% ADD THIS`;
      } else if (style === 'frequently_bought') {
        badgeEl.innerHTML = `<span class="oc-pulse-dot"></span> 👥 FREQUENTLY BOUGHT`;
      } else if (style === 'live_demand') {
        badgeEl.innerHTML = `<span class="oc-pulse-dot"></span> 🟢 42 BOUGHT TODAY`;
      } else {
        badgeEl.innerHTML = (config.upsell && config.upsell.badgeText) || 'RECOMMENDED';
      }
    }

    const layout = config.upsell.layout || 'grid';
    grid.className = `oc-upsell-grid oc-upsell-layout--${layout}`;

    const currentSignature = activeUpsells.map(u => `${u.id}_${u.price}_${u.comparePrice || ''}`).join('|') + '_' + layout;
    if (grid.dataset.renderedSignature === currentSignature && grid.children.length === activeUpsells.length) {
      return;
    }
    grid.dataset.renderedSignature = currentSignature;
    grid.innerHTML = '';

    activeUpsells.forEach(item => {
      const card = document.createElement('div');
      card.className = 'oc-upsell-card';
      const hasDiscount = item.comparePrice && item.comparePrice > item.price;
      const discountTag = item.discountTag || (hasDiscount ? `${Math.round(((item.comparePrice - item.price) / item.comparePrice) * 100)}% OFF` : null);
      const upsellImg = getValidImageUrl(item.image);
      const safeTitle = escapeHTML(item.fullTitle || item.title || 'Product');
      const safeDisplayTitle = escapeHTML(item.title || 'Product');

      if (layout === 'compact') {
        card.innerHTML = `
          ${discountTag ? `<span class="oc-upsell-tag">${discountTag}</span>` : ''}
          <div class="oc-upsell-img-wrap">
            <img src="${upsellImg}" alt="${safeTitle}" class="oc-upsell-img" loading="eager" decoding="async" fetchpriority="high" onerror="this.onerror=null; this.src='${ORGACIA_DEFAULT_PRODUCT_SVG}';">
          </div>
          <div class="oc-upsell-info">
            ${(config.upsell && config.upsell.showStars === true) ? getRatingSnippetForItem(item) : ''}
            <div class="oc-upsell-title" title="${safeTitle}">${safeDisplayTitle}</div>
            ${(config.upsell && config.upsell.showBenefits !== false) ? `<div class="oc-upsell-benefit-chip">${getBenefitChipForItem(item)}</div>` : ''}
            <div class="oc-upsell-pricing-row" style="justify-content: flex-start;">
              <span class="oc-upsell-price">${formatMoney(item.price)}</span>
              ${hasDiscount ? `<span class="oc-upsell-compare-price">${formatMoney(item.comparePrice)}</span>` : ''}
            </div>
          </div>
          <button class="oc-upsell-add-btn" data-id="${item.id}" data-product-id="${item.productId || ''}" data-price="${item.price}" data-compare-price="${item.comparePrice || ''}" data-title="${safeTitle}" data-img="${upsellImg}">
            + Add
          </button>
        `;
      } else {
        card.innerHTML = `
          ${discountTag ? `<span class="oc-upsell-tag">${discountTag}</span>` : ''}
          <div class="oc-upsell-img-wrap">
            <img src="${upsellImg}" alt="${safeTitle}" class="oc-upsell-img" loading="eager" decoding="async" fetchpriority="high" onerror="this.onerror=null; this.src='${ORGACIA_DEFAULT_PRODUCT_SVG}';">
          </div>
          <div class="oc-upsell-info">
            ${(config.upsell && config.upsell.showStars === true) ? getRatingSnippetForItem(item) : ''}
            <div class="oc-upsell-title" title="${safeTitle}">${safeDisplayTitle}</div>
            ${(config.upsell && config.upsell.showBenefits !== false) ? `<div class="oc-upsell-benefit-chip">${getBenefitChipForItem(item)}</div>` : ''}
            <div class="oc-upsell-pricing-row">
              <span class="oc-upsell-price">${formatMoney(item.price)}</span>
              ${hasDiscount ? `<span class="oc-upsell-compare-price">${formatMoney(item.comparePrice)}</span>` : ''}
            </div>
          </div>
          <button class="oc-upsell-add-btn" data-id="${item.id}" data-product-id="${item.productId || ''}" data-price="${item.price}" data-compare-price="${item.comparePrice || ''}" data-title="${safeTitle}" data-img="${upsellImg}">
            + Add
          </button>
        `;
      }
      grid.appendChild(card);
    });

    // 2026 Fresh Conversion Block: 1-Click Duo Routine Combo (Fully Customizable)
    let duoContainer = section.querySelector('.oc-duo-combo-banner');
    const duoConfig = (config.upsell && config.upsell.duoCombo) || {};
    const isDuoEnabled = config.upsell && config.upsell.enableDuoCombo === true && duoConfig.enabled === true;

    if (activeUpsells.length >= 2 && isDuoEnabled) {
      const u1 = activeUpsells[0];
      const u2 = activeUpsells[1];
      const duoTotalPrice = u1.price + u2.price;
      const duoComparePrice = (u1.comparePrice || u1.price) + (u2.comparePrice || u2.price);
      const duoSavings = config.roundDecimals 
        ? Math.round(duoComparePrice - duoTotalPrice) 
        : (Math.round((duoComparePrice - duoTotalPrice) * 100) / 100);

      const duoTitle = duoConfig.title || 'Complete Routine Combo';
      let duoSub = duoConfig.subtitleTemplate || '{products} (Save {savings})';
      duoSub = duoSub
        .replace('{products}', `${u1.title} + ${u2.title}`)
        .replace('{savings}', duoSavings > 0 ? formatMoney(duoSavings) : '')
        .replace('(Save )', '')
        .replace('Save ₹0', '')
        .trim();

      let duoBtnText = duoConfig.btnText || '+ Add Both ({price})';
      duoBtnText = duoBtnText.replace('{price}', formatMoney(duoTotalPrice));

      if (!duoContainer) {
        duoContainer = document.createElement('div');
        duoContainer.className = 'oc-duo-combo-banner';
        section.appendChild(duoContainer);
      }
      duoContainer.style.display = 'flex';
      if (duoConfig.bg) duoContainer.style.background = duoConfig.bg;
      if (duoConfig.border) duoContainer.style.borderColor = duoConfig.border;

      const titleColor = duoConfig.titleColor ? `style="color:${duoConfig.titleColor};"` : '';
      const subColor = duoConfig.subtitleColor ? `style="color:${duoConfig.subtitleColor};"` : '';
      const btnStyles = [
        duoConfig.btnBg ? `background:${duoConfig.btnBg}` : '',
        duoConfig.btnTextColor ? `color:${duoConfig.btnTextColor}` : ''
      ].filter(Boolean).join(';');

      duoContainer.innerHTML = `
        <div class="oc-duo-combo-left">
          <span class="oc-duo-combo-icon">✨</span>
          <div class="oc-duo-combo-info">
            <strong ${titleColor}>${escapeHTML(duoTitle)}</strong>
            <span ${subColor}>${escapeHTML(duoSub)}</span>
          </div>
        </div>
        <button type="button" class="oc-duo-combo-btn" id="oc-duo-combo-btn" ${btnStyles ? `style="${btnStyles}"` : ''}>
          ${escapeHTML(duoBtnText)}
        </button>
      `;

      const duoBtn = duoContainer.querySelector('#oc-duo-combo-btn');
      if (duoBtn) {
        duoBtn.addEventListener('click', () => {
          duoBtn.innerHTML = '<span>✓ Both Added!</span>';
          duoBtn.classList.add('oc-added');
          duoBtn.disabled = true;

          // Ingest both items instantly (0ms)
          OrgaciaCart.addItem({
            id: u1.id,
            variantId: u1.id,
            productId: u1.productId || null,
            title: u1.title,
            price: u1.price,
            basePrice: u1.price,
            compare_at_price: u1.comparePrice > u1.price ? u1.comparePrice : null,
            image: u1.image,
            quantity: 1
          });
          OrgaciaCart.addItem({
            id: u2.id,
            variantId: u2.id,
            productId: u2.productId || null,
            title: u2.title,
            price: u2.price,
            basePrice: u2.price,
            compare_at_price: u2.comparePrice > u2.price ? u2.comparePrice : null,
            image: u2.image,
            quantity: 1
          });

          setTimeout(() => {
            if (duoBtn) {
              duoBtn.innerHTML = escapeHTML(duoBtnText);
              duoBtn.classList.remove('oc-added');
              duoBtn.disabled = false;
            }
          }, 1500);
        });
      }
    } else if (duoContainer) {
      duoContainer.style.display = 'none';
    }
  }

  // Render Discounts in Subpage (Supports Creating & Removing Coupons Dynamically & Luxury Best Price State)
  function renderOffers(force = false) {
    const offersList = document.getElementById('oc-offers-list');
    const couponCountText = document.getElementById('oc-coupon-count-text');
    const drawer = document.getElementById('oc-drawer');
    if (!offersList) return;

    const isSubpageOpen = drawer && drawer.classList.contains('oc-show-discounts');
    const activeCoupons = (config.coupons || []).filter(c => c && c.enabled !== false);

    // If milestone discount tier is unlocked, include it as an available coupon offer!
    if (cartState.unlockedMilestoneCode && cartState.unlockedMilestonePct > 0) {
      const exists = activeCoupons.some(c => c.code.toUpperCase() === cartState.unlockedMilestoneCode.toUpperCase());
      if (!exists) {
        activeCoupons.unshift({
          code: cartState.unlockedMilestoneCode,
          title: `🎉 ${cartState.unlockedMilestonePct}% OFF Milestone Reward`,
          type: 'percentage',
          value: cartState.unlockedMilestonePct,
          bullets: [
            `Unlocked milestone discount for your order!`,
            'Must be applied in the discount box at checkout'
          ],
          minItems: 1,
          minAmount: 0
        });
      }
    }

    if (couponCountText) {
      couponCountText.textContent = `${activeCoupons.length} coupon${activeCoupons.length === 1 ? '' : 's'} available`;
    }

    if (!force && !isSubpageOpen && offersList.children.length > 0) {
      return;
    }

    offersList.innerHTML = '';

    if (activeCoupons.length === 0) {
      offersList.innerHTML = `
        <div class="oc-empty-coupons-hero">
          <div class="oc-empty-hero-icon-wrap">
            <span class="oc-empty-hero-shield">🛡️</span>
          </div>
          <h4 class="oc-empty-hero-title">${(config.noCoupons && config.noCoupons.guaranteeTitle) || "You're Already Getting Our Best Price!"}</h4>
          <p class="oc-empty-hero-desc">
            ${(config.noCoupons && config.noCoupons.guaranteeSubtitle) || "At Orgacia, we don't artificially inflate prices just to offer discount codes. All our pure formulas are priced directly at factory-direct lab rates — giving you up to 50% built-in savings with zero coupon hunting required."}
          </p>
          
          <div class="oc-empty-trust-grid">
            <div class="oc-empty-trust-card">
              <span class="oc-empty-trust-icon">🏷️</span>
              <div class="oc-empty-trust-text">
                <strong>${(config.noCoupons && config.noCoupons.badge1) || 'Direct-from-Lab Pricing'}</strong>
                <span>Transparent wholesale rates applied automatically to your cart items.</span>
              </div>
            </div>
            <div class="oc-empty-trust-card">
              <span class="oc-empty-trust-icon">🚚</span>
              <div class="oc-empty-trust-text">
                <strong>${(config.noCoupons && config.noCoupons.badge2) || 'Free Express Delivery'}</strong>
                <span>Automatically unlocked on all orders over ₹200.</span>
              </div>
            </div>
            <div class="oc-empty-trust-card">
              <span class="oc-empty-trust-icon">🎁</span>
              <div class="oc-empty-trust-text">
                <strong>${(config.noCoupons && config.noCoupons.badge3) || 'Complimentary Tier Rewards'}</strong>
                <span>Free wellness products auto-rewarded as your cart reaches milestones.</span>
              </div>
            </div>
          </div>

          <div class="oc-empty-vip-section">
            <div class="oc-empty-vip-label">Have an exclusive VIP, Creator or Partner code?</div>
            <div class="oc-empty-vip-input-wrap">
              <input type="text" id="oc-empty-vip-input" placeholder="Enter VIP / Partner Code" autocomplete="off" spellcheck="false">
              <button type="button" id="oc-empty-vip-btn">Apply</button>
            </div>
            <div class="oc-coupon-error" id="oc-empty-vip-error"></div>
          </div>
        </div>
      `;

      const emptyVipInput = document.getElementById('oc-empty-vip-input');
      const emptyVipBtn = document.getElementById('oc-empty-vip-btn');
      if (emptyVipBtn && emptyVipInput) {
        const handleApply = () => {
          const val = emptyVipInput.value.trim();
          if (val) {
            OrgaciaCart.applyCoupon(val);
          } else {
            emptyVipInput.focus();
          }
        };
        emptyVipBtn.addEventListener('click', handleApply);
        emptyVipInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleApply();
          }
        });
      }
      return;
    }

    activeCoupons.forEach(coupon => {
      const isApplied = cartState.appliedCoupon && cartState.appliedCoupon.code.toUpperCase() === coupon.code.toUpperCase();
      const item = document.createElement('div');
      item.className = 'oc-offer-item';
      item.innerHTML = `
        <div class="oc-offer-header">
          <div class="oc-offer-badge-wrap">
            <span class="oc-offer-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect width="24" height="24" rx="12" fill="#D1FAE5"/><path d="M8 8.5H8.01M16 15.5H16.01M16.5 8L7.5 16" stroke="#059669" stroke-width="2.2" stroke-linecap="round"/><circle cx="8" cy="8.5" r="1.2" fill="#059669"/><circle cx="16" cy="15.5" r="1.2" fill="#059669"/></svg>
            </span>
            <span class="oc-offer-code">${coupon.code}</span>
          </div>
          <button class="oc-offer-apply-btn ${isApplied ? 'oc-applied' : ''}" data-code="${coupon.code}">
            ${isApplied ? '✓ Applied' : 'Apply'}
          </button>
        </div>
        <div class="oc-offer-desc">${coupon.title}</div>
        <ul class="oc-offer-bullets">
          ${(coupon.bullets || []).map(b => `<li>${b}</li>`).join('')}
        </ul>
      `;
      offersList.appendChild(item);
    });
  }

  // Countdown timer
  function startCountdown() {
    if (timerInterval) clearInterval(timerInterval);
    const minEl = document.getElementById('oc-timer-min');
    const secEl = document.getElementById('oc-timer-sec');
    if (!minEl || !secEl) return;

    timerInterval = setInterval(() => {
      if (remainingSeconds <= 0) {
        remainingSeconds = (config.timer.durationMinutes * 60) + config.timer.durationSeconds;
      } else {
        remainingSeconds--;
      }

      const m = Math.floor(remainingSeconds / 60);
      const s = remainingSeconds % 60;
      minEl.textContent = `${String(m).padStart(2, '0')}m`;
      secEl.textContent = `${String(s).padStart(2, '0')}s`;
    }, 1000);
  }

  // Extract tier definitions from a bundle widget DOM node
  function extractTiersFromWidget(widget) {
    if (!widget) return [];

    // 1. Always inspect live DOM cards first so changes in Theme Customizer or DOM update instantly
    const domCards = widget.querySelectorAll ? widget.querySelectorAll('.org-pack-card') : [];
    if (domCards.length > 0) {
      const domTiers = [];
      domCards.forEach(card => {
        const rawQty = (card.dataset && card.dataset.quantity) || (typeof card.getAttribute === 'function' ? card.getAttribute('data-quantity') : null) || '1';
        const qty = parseInt(rawQty, 10) || 1;
        const rawDiscount = (card.dataset && card.dataset.discountPct) || (typeof card.getAttribute === 'function' ? card.getAttribute('data-discount-pct') : null) || '0';
        const discountPct = parseFloat(rawDiscount) || 0;

        const titleEl = card.querySelector ? card.querySelector('[data-field="title"], .org-pack-title') : null;
        const titleTpl = (card.dataset && card.dataset.titleTemplate) || (typeof card.getAttribute === 'function' ? card.getAttribute('data-title-template') : null);
        let title = titleEl ? titleEl.textContent.trim() : '';
        if (!title && titleTpl) {
          title = titleTpl.replace(/\{\{\s*quantity\s*\}\}/g, qty).replace(/\{\{\s*qty\s*\}\}/g, qty);
        }
        if (!title || title.startsWith('{{')) {
          title = `Pack of ${qty}`;
        }

        const hasGiftVal = (card.dataset && card.dataset.hasGift) || (typeof card.getAttribute === 'function' ? card.getAttribute('data-has-gift') : null);
        const discountCodeVal = (card.dataset && card.dataset.discountCode) || (typeof card.getAttribute === 'function' ? card.getAttribute('data-discount-code') : null);
        domTiers.push({
          minQty: qty,
          qty: qty,
          discountPct: discountPct,
          discountCode: discountCodeVal || (discountPct > 0 ? `BUNDLE${discountPct}` : ''),
          title: title,
          label: discountPct > 0 ? `${title} (${discountPct}% OFF)` : null,
          hasGift: hasGiftVal === 'true'
        });
      });
      if (domTiers.length > 0) {
        domTiers.sort((a, b) => (b.minQty || b.qty || 0) - (a.minQty || a.qty || 0));
        widget._bundleTiers = domTiers;
        return domTiers;
      }
    }

    // 2. Fallback to cached or dataset tiers if cards not present directly
    if (Array.isArray(widget._bundleTiers) && widget._bundleTiers.length > 0) {
      return [...widget._bundleTiers].sort((a, b) => (b.minQty || b.qty || 0) - (a.minQty || a.qty || 0));
    }
    if (widget.dataset && widget.dataset.bundleTiers) {
      try {
        const parsed = JSON.parse(widget.dataset.bundleTiers);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return [...parsed].sort((a, b) => (b.minQty || b.qty || 0) - (a.minQty || a.qty || 0));
        }
      } catch(e) {}
    }
    return [];
  }

  // Find the bundle widget associated with a cart line item
  function findMatchingWidgetForProduct(item) {
    if (!item || typeof document === 'undefined') return null;

    const domWidgets = Array.from(document.querySelectorAll('.orgacia-pack-widget'));
    if (domWidgets.length === 0) return null;

    const itemPId = item.productId || item.product_id;
    const itemVId = item.variantId || item.id;
    const itemTitle = (item.title || item.product_title || '').toLowerCase().trim();

    // 1. Match by Product ID
    if (itemPId) {
      const found = domWidgets.find(w => {
        const wPId = (w.dataset && w.dataset.productId) || (typeof w.getAttribute === 'function' ? w.getAttribute('data-product-id') : null);
        return wPId && String(wPId) === String(itemPId);
      });
      if (found) return found;
    }

    // 2. Match by Variant ID
    if (itemVId) {
      const found = domWidgets.find(w => {
        const wVId = (w.dataset && w.dataset.variantId) || (typeof w.getAttribute === 'function' ? w.getAttribute('data-variant-id') : null);
        return wVId && String(wVId) === String(itemVId);
      });
      if (found) return found;
    }

    // 3. Match by Product Title (fuzzy / keyword overlap)
    if (itemTitle) {
      const found = domWidgets.find(w => {
        const wTitle = ((w.dataset && w.dataset.productTitle) || (typeof w.getAttribute === 'function' ? w.getAttribute('data-product-title') : '') || '').toLowerCase().trim();
        if (!wTitle) return false;
        if (wTitle === itemTitle || itemTitle.includes(wTitle) || wTitle.includes(itemTitle)) return true;
        const wWords = wTitle.replace(/[^a-z0-9]/g, ' ').split(/\s+/).filter(w => w.length > 2);
        const iWords = itemTitle.replace(/[^a-z0-9]/g, ' ').split(/\s+/).filter(w => w.length > 2);
        const matches = wWords.filter(w => iWords.includes(w));
        return matches.length >= 2 || (wWords.length === 1 && matches.length === 1);
      });
      if (found) return found;
    }

    // 4. If single widget on page and item belongs to current page
    if (domWidgets.length === 1) {
      const currentPath = (typeof window !== 'undefined' && window.location && window.location.pathname) ? window.location.pathname.toLowerCase() : '';
      const pageTitleEl = document.querySelector('h1, .product__title, .product-title, [data-product-title]');
      const pageTitle = (pageTitleEl ? pageTitleEl.textContent : '').toLowerCase().trim();

      const isUrlMatch = item.url && currentPath.includes(item.url.split('?')[0].toLowerCase().replace('/products/', ''));
      const isHandleMatch = item.handle && currentPath.includes(item.handle.toLowerCase());
      const isTitleMatch = pageTitle && itemTitle && (
        pageTitle.includes(itemTitle) || 
        itemTitle.includes(pageTitle) ||
        (itemTitle.replace(/[^a-z0-9]/g, ' ').split(/\s+/).filter(w => w.length > 2).filter(w => pageTitle.includes(w)).length >= 2)
      );

      if (isUrlMatch || isHandleMatch || isTitleMatch) {
        return domWidgets[0];
      }
    }

    return null;
  }

  // Extract clean product handle from item handle or URL
  function extractProductHandle(item) {
    if (!item) return '';
    if (item.handle) return String(item.handle).toLowerCase().trim();
    if (item.url) {
      const m = String(item.url).match(/\/products\/([a-zA-Z0-9\-_]+)/);
      if (m && m[1]) return m[1].toLowerCase().trim();
    }
    return '';
  }

  // Resolve product-specific upgrade rule from configured blocks, tags, or intelligent product rules
  function getTierUpgradeRuleForProduct(item) {
    if (!item) return null;
    const pId = String(item.productId || item.product_id || '').replace(/\D/g, '');
    const vId = String(item.variantId || item.id || '').replace(/\D/g, '');
    const pHandle = extractProductHandle(item);
    const pTitle = (item.title || item.product_title || '').toLowerCase().trim();

    // 1. Check custom rules dictionary configured in settings / Theme Editor blocks
    const rules = (config.tierUpgrade && config.tierUpgrade.rules) ? config.tierUpgrade.rules : {};
    if (pId && rules[pId]) return rules[pId];
    if (pHandle && rules[pHandle]) return rules[pHandle];
    if (vId && rules[vId]) return rules[vId];
    if (item.title && rules[item.title]) return rules[item.title];
    if (item.product_title && rules[item.product_title]) return rules[item.product_title];

    // Try matching rules by ID, handle, or title (including partial / case-insensitive)
    for (const key of Object.keys(rules)) {
      const r = rules[key];
      if (!r) continue;
      const rId = String(r.productId || '').replace(/\D/g, '');
      if (rId && (pId === rId || vId === rId)) return r;

      const rHandle = (r.handle || '').toLowerCase().trim();
      if (rHandle && pHandle && (pHandle === rHandle || pHandle.includes(rHandle) || rHandle.includes(pHandle))) {
        return r;
      }

      const rTitle = (r.title || '').toLowerCase().trim();
      if (rTitle && pTitle && (pTitle === rTitle || pTitle.includes(rTitle) || rTitle.includes(pTitle))) {
        return r;
      }
    }

    // 2. Check product tags if merchant configured any in Shopify admin:
    // Tag: no-upgrade -> disabled
    // Tag: upgrade-at-2 -> triggerQty = 2
    // Tag: upgrade-at-1 -> triggerQty = 1
    // Tag: upgrade-pack-4 -> targetTierQty = 4
    // Tag: upgrade-pack-2 -> targetTierQty = 2
    const meta = (typeof window !== 'undefined' && window.ORGACIA_CART_PRODUCTS) ? 
      (window.ORGACIA_CART_PRODUCTS[vId] || window.ORGACIA_CART_PRODUCTS[item.key] || (pId && window.ORGACIA_CART_PRODUCTS[pId]) || (pHandle && window.ORGACIA_CART_PRODUCTS[pHandle])) : null;
    let tags = [];
    if (meta && Array.isArray(meta.tags)) tags = meta.tags;
    else if (Array.isArray(item.tags)) tags = item.tags;
    else if (typeof item.tags === 'string') tags = item.tags.split(',').map(s => s.trim());
    
    const tagList = tags.map(t => String(t).toLowerCase().trim());
    if (tagList.includes('no-upgrade')) {
      return { enabled: false };
    }
    let tagTrigger = null;
    let tagTarget = null;
    if (tagList.includes('upgrade-at-1')) tagTrigger = 1;
    else if (tagList.includes('upgrade-at-2')) tagTrigger = 2;
    else if (tagList.includes('upgrade-at-3')) tagTrigger = 3;
    else if (tagList.includes('upgrade-at-4')) tagTrigger = 4;

    if (tagList.includes('upgrade-pack-2')) tagTarget = 2;
    else if (tagList.includes('upgrade-pack-4')) tagTarget = 4;
    else if (tagList.includes('upgrade-pack-3')) tagTarget = 3;

    if (tagTrigger !== null || tagTarget !== null) {
      return {
        enabled: true,
        triggerQty: tagTrigger !== null ? tagTrigger : 'any',
        targetTierQty: tagTarget !== null ? tagTarget : 'auto'
      };
    }

    // Completely dynamic: no hardcoded product names or trigger quantities.
    // If no Theme Editor rule or product tag is present, return null so renderTierUpgradeHTML
    // dynamically evaluates the next available bundle tier based on current line quantity.
    return null;
  }

  // Check if a product is allowed for bundle swap based on targetMode, rules, and settings
  function isProductAllowedForTierSwap(item) {
    if (!item || item.isGift || item.isGiftWrap) return false;
    if (!config.tierUpgrade || config.tierUpgrade.enabled === false) return false;

    // Check custom or built-in product-specific upgrade rules first
    const rule = getTierUpgradeRuleForProduct(item);
    if (rule) {
      if (rule.enabled === false) return false;
      const currentQty = Math.max(1, parseInt(item.quantity, 10) || parseInt(item.properties && item.properties._bundle_tier_qty, 10) || 1);
      if (rule.triggerQty && rule.triggerQty !== 'any') {
        const reqQty = parseInt(rule.triggerQty, 10);
        if (!isNaN(reqQty) && reqQty > 0 && currentQty !== reqQty) {
          return false;
        }
      }
      return true;
    }

    const targetMode = config.tierUpgrade.targetMode || 'all_bundle_products';
    if (targetMode === 'rules_only') return false;

    if (targetMode === 'gift_milestone_only' || config.tierUpgrade.giftOnly === true) {
      const tiers = getDynamicBundleTiers(item);
      const hasGiftTier = Array.isArray(tiers) && tiers.some(t => t.gift === true || t.hasGift === true);
      const subtotal = cartState.original_total_price || 0;
      const qualifiesForGift = hasGiftTier || isGiftMilestoneUnlocked(subtotal);
      if (!qualifiesForGift) return false;
    }

    const pId = String(item.productId || item.product_id || '').replace(/\D/g, '');
    const vId = String(item.variantId || item.id || '').replace(/\D/g, '');
    const pHandle = extractProductHandle(item);
    const pTitle = (item.title || item.product_title || '').toLowerCase().trim();

    // Check window.ORGACIA_CART_PRODUCTS metadata if available
    const meta = (typeof window !== 'undefined' && window.ORGACIA_CART_PRODUCTS) ? 
      (window.ORGACIA_CART_PRODUCTS[vId] || window.ORGACIA_CART_PRODUCTS[item.key] || (pId && window.ORGACIA_CART_PRODUCTS[pId]) || (pHandle && window.ORGACIA_CART_PRODUCTS[pHandle])) : null;

    const resolvedPId = pId || (meta && meta.productId ? String(meta.productId).replace(/\D/g, '') : '');
    const resolvedHandle = pHandle || (meta && meta.handle ? String(meta.handle).toLowerCase().trim() : '');

    if (targetMode === 'specific_products') {
      const allowedIds = (config.tierUpgrade.targetProductIds || []).map(id => String(id).replace(/\D/g, '')).filter(Boolean);
      const allowedHandles = (config.tierUpgrade.targetProductHandles || []).map(h => String(h).toLowerCase().trim()).filter(Boolean);
      const allowedTitles = (config.tierUpgrade.targetProductTitles || []).map(t => String(t).toLowerCase().trim()).filter(Boolean);
      const allowedVariantIds = (config.tierUpgrade.targetVariantIds || []).map(id => String(id).replace(/\D/g, '')).filter(Boolean);

      if (resolvedPId && allowedIds.includes(resolvedPId)) return true;
      if (vId && allowedVariantIds.includes(vId)) return true;
      if (resolvedHandle && allowedHandles.includes(resolvedHandle)) return true;
      if (pTitle && allowedTitles.some(t => t === pTitle || pTitle.includes(t) || t.includes(pTitle))) return true;

      // Also check window.ORGACIA_ALLOWED_SWAP_PRODUCTS
      if (typeof window !== 'undefined' && window.ORGACIA_ALLOWED_SWAP_PRODUCTS) {
        if (resolvedPId && window.ORGACIA_ALLOWED_SWAP_PRODUCTS[resolvedPId]) return true;
        for (const k of Object.keys(window.ORGACIA_ALLOWED_SWAP_PRODUCTS)) {
          const ap = window.ORGACIA_ALLOWED_SWAP_PRODUCTS[k];
          if (!ap) continue;
          if (resolvedPId && String(ap.id).replace(/\D/g, '') === resolvedPId) return true;
          if (resolvedHandle && String(ap.handle).toLowerCase().trim() === resolvedHandle) return true;
          if (vId && Array.isArray(ap.variantIds) && ap.variantIds.map(String).includes(vId)) return true;
          if (pTitle && ap.title && ap.title.toLowerCase().trim() === pTitle) return true;
        }
      }

      return false;
    }

    if (targetMode === 'product_tag') {
      const targetTag = (config.tierUpgrade.targetTag || 'bundle-swap').toLowerCase().trim();
      let tags = [];
      if (meta && Array.isArray(meta.tags)) tags = meta.tags;
      else if (Array.isArray(item.tags)) tags = item.tags;
      else if (typeof item.tags === 'string') tags = item.tags.split(',').map(s => s.trim());

      return tags.some(t => t.toLowerCase().trim() === targetTag);
    }

    return true;
  }

  // Check if current page is the product page for this item
  function isCurrentPageForProduct(item) {
    if (!item || typeof window === 'undefined' || typeof document === 'undefined') return false;

    const currentPath = (window.location && window.location.pathname) ? window.location.pathname.toLowerCase() : '';
    const isProductPage = currentPath.includes('/products/') || !!document.querySelector('form[action*="/cart/add"], .shopify-product-form, product-form');
    if (!isProductPage) return false;

    const pageVariantInput = document.querySelector('form[action*="/cart/add"] [name="id"], form[action*="/cart/add.js"] [name="id"], [name="id"]');
    const pageVariantId = pageVariantInput ? String(pageVariantInput.value).trim() : null;
    const itemVId = String(item.variantId || item.id || '');

    if (pageVariantId && (itemVId === pageVariantId)) return true;
    if (item.url && currentPath.includes(item.url.split('?')[0].toLowerCase().replace('/products/', ''))) return true;
    if (item.handle && currentPath.includes(item.handle.toLowerCase())) return true;

    const pageTitleEl = document.querySelector('h1, .product__title, .product-title, [data-product-title]');
    const pageTitle = (pageTitleEl ? pageTitleEl.textContent : '').toLowerCase().trim();
    const itemTitle = (item.title || item.product_title || '').toLowerCase().trim();

    if (pageTitle && itemTitle) {
      if (pageTitle === itemTitle || pageTitle.includes(itemTitle) || itemTitle.includes(pageTitle)) return true;
      const wWords = pageTitle.replace(/[^a-z0-9]/g, ' ').split(/\s+/).filter(w => w.length > 2);
      const iWords = itemTitle.replace(/[^a-z0-9]/g, ' ').split(/\s+/).filter(w => w.length > 2);
      if (wWords.filter(w => iWords.includes(w)).length >= 2) return true;
    }

    return false;
  }

  // Dynamically resolve bundle tiers for this product without hardcoding
  function getDynamicBundleTiers(item) {
    if (!item) return [];

    // 1. Check if item was explicitly added via bundle widget or swapped and carries bundle properties
    if (item.properties && item.properties._bundle_tiers) {
      try {
        const parsed = typeof item.properties._bundle_tiers === 'string' 
          ? JSON.parse(item.properties._bundle_tiers) 
          : item.properties._bundle_tiers;
        if (Array.isArray(parsed) && parsed.length > 0) {
          return [...parsed].sort((a, b) => (b.minQty || b.qty || 0) - (a.minQty || a.qty || 0));
        }
      } catch (e) {}
    }

    // 2. Read live bundle widget on the page if present and matches this product
    const matchingWidget = findMatchingWidgetForProduct(item);
    if (matchingWidget) {
      const tiers = extractTiersFromWidget(matchingWidget);
      if (Array.isArray(tiers) && tiers.length > 0) {
        return [...tiers].sort((a, b) => (b.minQty || b.qty || 0) - (a.minQty || a.qty || 0));
      }
    }

    // 3. Check window.OrgaciaConfiguredWidgets registered by active widgets
    if (typeof window !== 'undefined' && window.OrgaciaConfiguredWidgets) {
      const pId = String(item.productId || item.product_id || '').replace(/\D/g, '');
      const vId = String(item.variantId || item.id || '').replace(/\D/g, '');
      const pHandle = extractProductHandle(item);
      const tKey = (item.title || item.product_title || '').toLowerCase().trim();

      let foundWidgetTiers = null;
      if (pId && window.OrgaciaConfiguredWidgets[pId]) foundWidgetTiers = window.OrgaciaConfiguredWidgets[pId];
      else if (vId && window.OrgaciaConfiguredWidgets[vId]) foundWidgetTiers = window.OrgaciaConfiguredWidgets[vId];
      else if (pHandle && window.OrgaciaConfiguredWidgets[pHandle]) foundWidgetTiers = window.OrgaciaConfiguredWidgets[pHandle];
      else if (tKey && window.OrgaciaConfiguredWidgets[tKey]) foundWidgetTiers = window.OrgaciaConfiguredWidgets[tKey];
      else {
        for (const k of Object.keys(window.OrgaciaConfiguredWidgets)) {
          if (k.length > 3 && (tKey.includes(k) || k.includes(tKey))) {
            foundWidgetTiers = window.OrgaciaConfiguredWidgets[k];
            break;
          }
        }
      }

      if (Array.isArray(foundWidgetTiers) && foundWidgetTiers.length > 0) {
        return [...foundWidgetTiers].sort((a, b) => (b.minQty || b.qty || 0) - (a.minQty || a.qty || 0));
      }
    }

    // 4. Check product-specific bundleRules configured in settings
    const pId = item.productId || item.product_id || item.id;
    if (pId && config.bundleRules && config.bundleRules[pId] && Array.isArray(config.bundleRules[pId].tiers)) {
      return [...config.bundleRules[pId].tiers].sort((a, b) => (b.minQty || b.qty || 0) - (a.minQty || a.qty || 0));
    }

    // 5. Storewide / Fallback Volume Discount Tiers
    if (config.volumeDiscounts && Array.isArray(config.volumeDiscounts.tiers) && config.volumeDiscounts.tiers.length > 0) {
      const volTiers = config.volumeDiscounts.tiers.map(vt => ({
        minQty: parseInt(vt.qty || vt.minQty || 1, 10),
        qty: parseInt(vt.qty || vt.minQty || 1, 10),
        discountPct: parseFloat(vt.discountPct || 0),
        discountCode: vt.discountCode || (vt.discountPct ? `BUNDLE${vt.discountPct}` : ''),
        title: vt.title || `Pack of ${vt.qty || vt.minQty}`,
        label: vt.label ? vt.label.replace(/\d+%\s*OFF/i, `${parseFloat(vt.discountPct || 0)}% OFF`) : (vt.discountPct ? `Pack of ${vt.qty || vt.minQty} (${vt.discountPct}% OFF)` : null)
      })).filter(t => t.qty > 0);

      if (volTiers.length > 0) {
        return [...volTiers].sort((a, b) => (b.minQty || b.qty || 0) - (a.minQty || a.qty || 0));
      }
    }

    // Default fallback pack tiers (Pack of 4: 12% OFF, Pack of 3: 7% OFF, Pack of 2: 5% OFF) matching Shopify store setup
    return [
      { minQty: 4, qty: 4, discountPct: 12, discountCode: 'BUNDLE12', title: 'Pack of 4', label: 'Pack of 4 (12% OFF)' },
      { minQty: 3, qty: 3, discountPct: 7, discountCode: 'BUNDLE7', title: 'Pack of 3', label: 'Pack of 3 (7% OFF)' },
      { minQty: 2, qty: 2, discountPct: 5, discountCode: 'BUNDLE5', title: 'Pack of 2', label: 'Pack of 2 (5% OFF)' }
    ];
  }

  // Auto-Sync Line Item Prices on Cart Items & Dynamic Bundle Discounts
  function syncBundleDiscountsAndGifts() {
    let hasActiveBundleGiftTier = false;

    cartState.items.forEach(item => {
      if (item.isGift || item.isGiftWrap) return;

      const qty = Math.max(1, parseInt(item.quantity, 10) || 1);

      // Reconcile with authoritative product page pricing ONLY if item has NO server discount and is not already a discounted bundle item
      if (!item.hasServerDiscount) {
        const pagePricing = readProductPagePricing(item) || getCachedProductPricing(item);
        if (pagePricing && pagePricing.sellingPrice > 0) {
          item.basePrice = pagePricing.sellingPrice;
          if (!item.isBundleItem || !item.calculatedPrice) {
            item.price = pagePricing.sellingPrice;
            item.calculatedPrice = pagePricing.sellingPrice;
          }
          if (pagePricing.comparePrice > pagePricing.sellingPrice) {
            item.compare_at_price = pagePricing.comparePrice;
          }
        }
      }

      // Ensure all unit prices are cleanly normalized to exact integer rupees
      if (item.basePrice !== undefined) item.basePrice = normalizePriceAmount(item.basePrice, true);
      if (item.price !== undefined) item.price = normalizePriceAmount(item.price, true);
      if (item.calculatedPrice !== undefined) item.calculatedPrice = normalizePriceAmount(item.calculatedPrice, true);

      // baseUnit: the actual per-unit SELLING price (what customer pays before our bundle discount).
      // MUST NOT be the compare_at_price/MRP — that's only for strikethrough display.
      const rawBase = Number(item.basePrice) || 0;
      const fromPrice = Number(item.price) || 0;
      const fromOriginal = (item.originalPrice && qty > 0) 
        ? (config.roundDecimals ? Math.round(Number(item.originalPrice) / qty) : (Math.round((Number(item.originalPrice) / qty) * 100) / 100)) 
        : 0;
      const fromCompare = Number(item.compare_at_price) || 0;

      // Safety: if basePrice accidentally equals compare_at_price (MRP) and price is valid and lower,
      // the basePrice was contaminated — correct it to the actual selling price.
      let correctedBase = rawBase;
      if (rawBase > 0 && fromPrice > 0 && fromPrice < rawBase && fromCompare > 0 && rawBase === fromCompare) {
        correctedBase = fromPrice;
        item.basePrice = fromPrice;
      }

      // Priority: corrected basePrice > price > originalPrice/qty > 0 (never compare_at_price, never arbitrary default)
      const baseUnit = correctedBase > 0 ? correctedBase : (fromPrice > 0 ? fromPrice : (fromOriginal > 0 ? fromOriginal : 0));
      if (baseUnit > 0 && (!item.basePrice || item.basePrice <= 0)) {
        item.basePrice = baseUnit;
      }
      if (!item.base_variant_title && item.variant_title && !item.variant_title.startsWith('Pack of') && item.variant_title !== 'Default Title' && item.variant_title !== 'Single Jar') {
        item.base_variant_title = item.variant_title;
      }

      // Dynamically resolve bundle tiers for this product without hardcoding
      const tiers = getDynamicBundleTiers(item);
      const sortedTiers = Array.isArray(tiers)
        ? [...tiers].sort((a, b) => (b.minQty || b.qty || 0) - (a.minQty || a.qty || 0))
        : [];

      // Find highest qualifying tier for current item quantity with a discount
      const matchedTier = sortedTiers.find(t => qty >= (t.minQty || t.qty || 1) && (t.discountPct || 0) > 0) || null;

      // Pack size is strictly determined by the selected quantity (1 -> Pack of 1, 2 -> Pack of 2, 3 -> Pack of 3, etc.)
      const baseVariantPrefix = (item.base_variant_title && !item.base_variant_title.startsWith('Pack of') && item.base_variant_title !== 'Default Title' && item.base_variant_title !== 'Single Jar')
        ? `${item.base_variant_title} • `
        : '';
      const packTitle = `${baseVariantPrefix}Pack of ${qty}`;

      let discountPct = 0;
      let calculatedUnit = baseUnit;
      let bundleLabel = null;
      let variantTitle = packTitle;

      // If Shopify server ALREADY discounted this item (e.g. via Automatic Bundle Discount),
      // DO NOT apply client-side discount on top! Respect the server-calculated price to avoid double-discounting.
      const serverDiscPct = (item.serverOriginalPrice > 0 && item.serverSellingPrice > 0 && item.serverSellingPrice < item.serverOriginalPrice)
        ? Math.round((1 - (item.serverSellingPrice / item.serverOriginalPrice)) * 100)
        : (item.hasServerDiscount && item.discountPct ? item.discountPct : 0);

      if (item.hasServerDiscount && (serverDiscPct > 0 || item.serverDiscountTitle)) {
        const effectiveDiscPct = serverDiscPct > 0 ? serverDiscPct : (item.discountPct || 5);
        item.calculatedPrice = item.serverSellingPrice > 0 ? item.serverSellingPrice : item.price;
        item.basePrice = item.serverOriginalPrice > 0 ? item.serverOriginalPrice : (item.basePrice || item.price);
        item.compare_at_price = item.serverOriginalPrice > 0 ? item.serverOriginalPrice : (item.compare_at_price || item.basePrice);
        item.discountPct = effectiveDiscPct;
        item.bundleDiscountPct = effectiveDiscPct;
        item.bundleLabel = item.serverDiscountTitle || (matchedTier && matchedTier.label) || `Pack of ${qty} (${effectiveDiscPct}% OFF)`;
        item.isBundleItem = true;
        item.variant_title = packTitle;
        item.originalPrice = (item.serverOriginalPrice || baseUnit) * qty;
        if (matchedTier && (matchedTier.hasGift === true || matchedTier.gift === true)) {
          hasActiveBundleGiftTier = true;
        }
        return;
      }

      if (matchedTier && (matchedTier.discountPct || 0) > 0 && qty >= (matchedTier.minQty || matchedTier.qty || 1)) {
        discountPct = matchedTier.discountPct;
        calculatedUnit = config.roundDecimals 
          ? Math.round(baseUnit * (1 - discountPct / 100))
          : (Math.round(baseUnit * (1 - discountPct / 100) * 100) / 100);
        let activeLabel = matchedTier.label;
        if (activeLabel) {
          activeLabel = activeLabel.replace(/Pack of \d+/i, `Pack of ${qty}`);
          if (/\d+%\s*OFF/i.test(activeLabel)) {
            activeLabel = activeLabel.replace(/\d+%\s*OFF/i, `${discountPct}% OFF`);
          }
        } else if (discountPct > 0) {
          activeLabel = `Pack of ${qty} (${discountPct}% OFF)`;
        }
        bundleLabel = activeLabel || `${discountPct}% OFF`;
        variantTitle = packTitle;
        if (matchedTier.hasGift === true || matchedTier.gift === true) {
          hasActiveBundleGiftTier = true;
        }
      } else if (item.isBundleItem && item.discountPct > 0 && qty >= 2) {
        // Fallback: preserve existing bundle discount if qty >= 2
        discountPct = item.discountPct;
        calculatedUnit = config.roundDecimals 
          ? Math.round(baseUnit * (1 - discountPct / 100))
          : (Math.round(baseUnit * (1 - discountPct / 100) * 100) / 100);
        bundleLabel = item.bundleLabel ? item.bundleLabel.replace(/Pack of \d+/i, `Pack of ${qty}`) : `Pack of ${qty} (${discountPct}% OFF)`;
        variantTitle = packTitle;
      } else if (qty >= 2) {
        // Automatic pack discount fallback for storewide setup: Pack of 4 -> 12%, Pack of 3 -> 7%, Pack of 2 -> 5%
        discountPct = qty >= 4 ? 12 : (qty >= 3 ? 7 : 5);
        calculatedUnit = config.roundDecimals 
          ? Math.round(baseUnit * (1 - discountPct / 100))
          : (Math.round(baseUnit * (1 - discountPct / 100) * 100) / 100);
        bundleLabel = `Pack of ${qty} (${discountPct}% OFF)`;
        variantTitle = packTitle;
      } else {
        // Quantity does not qualify for any bundle tier discount (regular single price)
        discountPct = 0;
        calculatedUnit = baseUnit;
        bundleLabel = null;
        variantTitle = packTitle;
      }

      // Synchronize item properties dynamically so previous discounts never stick
      if (!item.properties) item.properties = {};
      if (discountPct > 0) {
        item.properties._is_bundle = 'true';
        item.properties._bundle_discount = String(discountPct);
        item.properties._bundle_discount_pct = String(discountPct);
        item.properties._bundle_tier = variantTitle;
        item.properties._bundle_tier_qty = String(qty);
        if (sortedTiers.length > 0) {
          item.properties._bundle_tiers = JSON.stringify(sortedTiers);
        }
      } else {
        item.properties._is_bundle = 'false';
        delete item.properties._bundle_discount;
        delete item.properties._bundle_discount_pct;
        delete item.properties._bundle_tier;
        delete item.properties._bundle_tier_qty;
        delete item.properties._bundle_tiers;
      }

      if (discountPct > 0 && calculatedUnit < baseUnit) {
        item.calculatedPrice = calculatedUnit;
        item.discountPct = discountPct;
        item.bundleDiscountPct = discountPct;
        item.bundleLabel = bundleLabel;
        item.appliedBundleTier = matchedTier || { qty, discountPct, title: variantTitle, label: bundleLabel };
        item.compare_at_price = baseUnit;
        item.isBundleItem = true;
        item.variant_title = variantTitle;
      } else {
        item.calculatedPrice = baseUnit;
        item.bundleLabel = null;
        item.appliedBundleTier = null;
        item.discountPct = 0;
        item.bundleDiscountPct = 0;
        item.compare_at_price = null;
        item.isBundleItem = false;
        item.variant_title = variantTitle;
      }

      item.originalPrice = config.roundDecimals ? Math.round(baseUnit * qty) : (Math.round(baseUnit * qty * 100) / 100);
    });

    // Detect and record active bundle discount code
    let activeBundleDiscountCode = null;
    cartState.items.forEach(item => {
      if (item.discountPct > 0 && !activeBundleDiscountCode) {
        if (item.appliedBundleTier && item.appliedBundleTier.discountCode) {
          activeBundleDiscountCode = String(item.appliedBundleTier.discountCode).trim();
        } else if (item.properties && item.properties._bundle_discount_code) {
          activeBundleDiscountCode = String(item.properties._bundle_discount_code).trim();
        } else if (config.volumeDiscounts && Array.isArray(config.volumeDiscounts.tiers)) {
          const vTier = config.volumeDiscounts.tiers.find(v => (item.quantity || 1) >= (v.qty || 1));
          if (vTier && vTier.discountCode) activeBundleDiscountCode = String(vTier.discountCode).trim();
        }
        if (!activeBundleDiscountCode && item.discountPct > 0) {
          activeBundleDiscountCode = `BUNDLE${item.discountPct}`;
        }
      }
    });
    cartState.bundleDiscountCode = activeBundleDiscountCode;

    return hasActiveBundleGiftTier;
  }

  // Toast Notification Helper (Fast 1.8s auto-dismiss & instant tap-to-dismiss)
  let toastTimeout = null;
  function hideToast() {
    clearTimeout(toastTimeout);
    const toast = document.getElementById('oc-toast-banner');
    if (toast) {
      toast.classList.remove('oc-show');
      toast.classList.remove('oc-toast--warning');
    }
  }

  function showToast(message, type = 'success') {
    const toast = document.getElementById('oc-toast-banner');
    if (!toast) return;

    // Preserve dismiss button and update message text safely
    toast.innerHTML = `<span class="oc-toast-msg">${message}</span><button type="button" class="oc-toast-close" aria-label="Dismiss">&times;</button>`;
    toast.className = 'oc-toast-banner';

    const isWarn = type === 'error' || type === 'warning' || 
                   message.includes('⚠️') || 
                   message.includes('out of stock') || 
                   message.includes('limit') || 
                   message.includes('reached') || 
                   message.includes('availability') ||
                   message.includes('Maximum available');

    if (isWarn) {
      toast.classList.add('oc-toast--warning');
    }
    toast.classList.add('oc-show');

    clearTimeout(toastTimeout);
    // Snappy auto-dismiss: 1800ms for warnings, 1500ms for success
    const duration = isWarn ? 1800 : 1500;
    toastTimeout = setTimeout(() => {
      hideToast();
    }, duration);
  }

  // Resilient Clipboard Copy Helper with ExecCommand Fallback
  function copyToClipboard(text) {
    if (!text) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => {
        fallbackCopyText(text);
      });
    } else {
      fallbackCopyText(text);
    }
  }

  function fallbackCopyText(text) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '-9999px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) {}
  }

  // Indeterminate Loading Progress Bar Helper (Zero-Lag, Instant Auto-Dismiss)
  let activeActionsCount = 0;
  let loaderSafetyTimer = null;

  function showLoader() {
    activeActionsCount++;
    const loader = document.getElementById('oc-drawer-loader');
    if (loader) {
      loader.classList.add('oc-loading');
      loader.setAttribute('aria-hidden', 'false');
    }
    clearTimeout(loaderSafetyTimer);
    // Safety auto-dismiss fallback in case an external request is stalled
    loaderSafetyTimer = setTimeout(() => {
      activeActionsCount = 0;
      const el = document.getElementById('oc-drawer-loader');
      if (el) {
        el.classList.remove('oc-loading');
        el.setAttribute('aria-hidden', 'true');
      }
    }, 1600);
  }

  function hideLoader(force = false) {
    if (force) {
      activeActionsCount = 0;
    } else {
      activeActionsCount = Math.max(0, activeActionsCount - 1);
    }

    if (activeActionsCount === 0) {
      clearTimeout(loaderSafetyTimer);
      const loader = document.getElementById('oc-drawer-loader');
      if (loader) {
        loader.classList.remove('oc-loading');
        loader.setAttribute('aria-hidden', 'true');
      }
    }
  }

  // Reset Checkout CTA & Dismiss Loader State (Guarantees Instant Interactivity)
  function resetCheckoutButton() {
    hideLoader(true);
    const checkoutBtn = document.getElementById('oc-checkout-btn');
    if (checkoutBtn) {
      checkoutBtn.disabled = false;
      checkoutBtn.style.opacity = '1';
      checkoutBtn.removeAttribute('disabled');
      const btnText = (config.footer && config.footer.checkoutBtnText) || 'Checkout Now';
      checkoutBtn.innerHTML = `<span>${btnText}</span>`;
    }
  }

  // Calculate Cart State & Multi-Tier Rewards
  function updateCartCalculations(skipFullDomRebuild = false) {
    const hasActiveBundleGiftTier = syncBundleDiscountsAndGifts();

    let subtotal = 0;
    let totalItems = 0;
    let totalRawPrice = 0;
    let totalItemSavings = 0;
    let bundleSavings = 0;
    let maxBundleDiscountPct = 0;
    const bundleLabels = [];

    cartState.items.forEach(item => {
      const lineQty = Math.max(1, parseInt(item.quantity, 10) || 1);
      const isFreeGift = item.isGift === true;
      const unitPrice = isFreeGift ? 0 : (Number(item.calculatedPrice !== undefined ? item.calculatedPrice : item.price) || 0);
      const lineFinalPrice = config.roundDecimals ? Math.round(unitPrice * lineQty) : (Math.round(unitPrice * lineQty * 100) / 100);

      // Extract accurate unit compare price (must be greater than unit price to count as a discount)
      const rawCompareUnit = item.compare_at_price 
        ? Number(item.compare_at_price) 
        : ((item.serverOriginalPrice && item.serverOriginalPrice > unitPrice) 
            ? Number(item.serverOriginalPrice) 
            : (item.basePrice && item.basePrice > unitPrice ? Number(item.basePrice) : 0));
      const unitComparePrice = rawCompareUnit > unitPrice ? rawCompareUnit : unitPrice;
      const lineRawPrice = config.roundDecimals ? Math.round(unitComparePrice * lineQty) : (Math.round(unitComparePrice * lineQty * 100) / 100);

      if (!isFreeGift) {
        subtotal += lineFinalPrice;
        totalRawPrice += lineRawPrice;
        totalItems += lineQty;

        const itemDiff = lineRawPrice - lineFinalPrice;
        if (itemDiff > 0) {
          totalItemSavings += itemDiff;
          if (item.isBundleItem || item.bundleLabel || item.discountPct > 0 || (item.properties && item.properties._bundle_discount) || item.hasServerDiscount || item.serverDiscountTitle) {
            bundleSavings += itemDiff;
            if (item.discountPct > maxBundleDiscountPct) {
              maxBundleDiscountPct = item.discountPct;
            }
            const label = item.serverDiscountTitle || item.bundleLabel || (item.discountPct > 0 ? `${item.discountPct}% OFF Bundle` : 'Bundle');
            if (!bundleLabels.includes(label)) {
              bundleLabels.push(label);
            }
          }
        }
      }
    });

    subtotal = Math.round(subtotal * 100) / 100;
    totalRawPrice = Math.round(totalRawPrice * 100) / 100;
    totalItemSavings = Math.round(totalItemSavings * 100) / 100;
    bundleSavings = Math.round(bundleSavings * 100) / 100;

    cartState.item_count = totalItems;
    cartState.original_total_price = subtotal;

    // Strict Auto-Removal Rule:
    // If milestone requirement (< 600) is not met and no bundle tier grants a gift,
    // or if all regular products are removed, any free gift product in the cart MUST be automatically removed.
    const regularItems = cartState.items.filter(i => !i.isGift && !i.isGiftWrap);
    const hasMainProducts = regularItems.length > 0;
    const isGiftPermitted = hasMainProducts && isGiftEligible(subtotal);
    const giftItems = cartState.items.filter(i => i.isGift);

    if (giftItems.length > 0 && !isGiftPermitted) {
      const giftsToRemove = [...giftItems];
      cartState.items = cartState.items.filter(i => !i.isGift);
      closeGiftModal();
      showToast('Free gift removed (milestone requirement not met)');
      removeGiftFromShopify(giftsToRemove);
    }

    // Milestone Discount Calculation (e.g. Tier 2: 5% OFF at ₹378)
    let unlockedMilestoneTier = null;
    let unlockedMilestoneCode = null;
    let unlockedMilestonePct = 0;
    let milestoneDiscountLabel = null;
    let milestoneDiscountPct = 0;

    if (config.tiers && config.tiers.enabled !== false && config.tiers.items && config.tiers.items.length > 0) {
      if (hasMainProducts && subtotal > 0 && cartState.items.length > 0) {
        const unlockedDiscountTiers = config.tiers.items.filter(tier => {
          if (subtotal < tier.threshold) return false;
          return tier.rewardType === 'discount' || tier.icon === '%' || (tier.label && /(\d+(?:\.\d+)?\s*%|\bdiscount\b|\boff\b)/i.test(tier.label));
        });

        if (unlockedDiscountTiers.length > 0) {
          const activeTier = unlockedDiscountTiers[unlockedDiscountTiers.length - 1];
          milestoneDiscountLabel = activeTier.label || 'Milestone Discount';

          if (typeof activeTier.discountPct === 'number' && activeTier.discountPct > 0) {
            milestoneDiscountPct = activeTier.discountPct;
          } else if (activeTier.label) {
            const pctMatch = activeTier.label.match(/(\d+(?:\.\d+)?)\s*%/);
            if (pctMatch) {
              milestoneDiscountPct = parseFloat(pctMatch[1]);
            }
          }

          if (activeTier.discountCode) {
            cartState.milestoneDiscountCode = String(activeTier.discountCode).trim();
          } else if (milestoneDiscountPct > 0) {
            cartState.milestoneDiscountCode = `${milestoneDiscountPct}OFF`;
          } else {
            cartState.milestoneDiscountCode = null;
          }

          unlockedMilestoneTier = activeTier;
          unlockedMilestoneCode = cartState.milestoneDiscountCode;
          unlockedMilestonePct = milestoneDiscountPct;
        } else {
          cartState.milestoneDiscountCode = null;
        }
      } else {
        cartState.milestoneDiscountCode = null;
        unlockedMilestoneTier = null;
        unlockedMilestoneCode = null;
        unlockedMilestonePct = 0;
      }
    } else {
      cartState.milestoneDiscountCode = null;
      unlockedMilestoneTier = null;
      unlockedMilestoneCode = null;
      unlockedMilestonePct = 0;
    }

    cartState.unlockedMilestoneTier = unlockedMilestoneTier;
    cartState.unlockedMilestoneCode = unlockedMilestoneCode;
    cartState.unlockedMilestonePct = unlockedMilestonePct;
    // Per user requirement: milestone discount is NOT deducted automatically.
    // It is redeemed via coupon code (from coupons section or milestone banner).
    let milestoneDiscountAmount = 0;

    // Coupon Calculation
    let couponDiscount = 0;
    let couponDiscountLabel = null;
    if (cartState.items.length === 0) {
      cartState.appliedCoupon = null;
    } else if (cartState.appliedCoupon) {
      const c = cartState.appliedCoupon;
      if (totalItems >= (c.minItems || 0) && subtotal >= (c.minAmount || 0)) {
        if (c.type === 'percentage') {
          couponDiscount = Math.round(((subtotal * c.value) / 100) * 100) / 100;
          couponDiscountLabel = `${c.value}% OFF (${c.code})`;
        } else {
          couponDiscount = config.roundDecimals ? Math.round(c.value) : (Math.round(c.value * 100) / 100);
          couponDiscountLabel = `-${formatMoney(couponDiscount)} (${c.code})`;
        }
      } else {
        cartState.appliedCoupon = null;
        couponDiscount = 0;
      }
    }

    const totalSavings = config.roundDecimals 
      ? Math.round(totalItemSavings + couponDiscount) 
      : (Math.round((totalItemSavings + couponDiscount) * 100) / 100);
    const finalPayable = Math.max(0, config.roundDecimals 
      ? Math.round(subtotal - couponDiscount) 
      : (Math.round((subtotal - couponDiscount) * 100) / 100));
    const finalRaw = Math.max(finalPayable + totalSavings, config.roundDecimals 
      ? Math.round(totalRawPrice) 
      : (Math.round(totalRawPrice * 100) / 100));

    cartState.discountAmount = totalSavings;
    cartState.bundleSavings = bundleSavings;
    cartState.milestoneDiscount = 0;
    cartState.milestoneDiscountPct = milestoneDiscountPct;
    cartState.milestoneDiscountLabel = milestoneDiscountLabel;
    cartState.couponDiscount = couponDiscount;
    cartState.couponDiscountLabel = couponDiscountLabel;
    cartState.maxBundleDiscountPct = maxBundleDiscountPct;
    cartState.bundleLabels = bundleLabels;

    cartState.total_price = finalPayable;
    cartState.raw_total_price = finalRaw;

    // Compose Specific Discount Tag
    const specificParts = [];
    if (maxBundleDiscountPct > 0) {
      specificParts.push(`${maxBundleDiscountPct}% Bundle`);
    } else if (bundleSavings > 0) {
      specificParts.push('Bundle');
    }
    if (couponDiscount > 0 && cartState.appliedCoupon) {
      specificParts.push(`${couponDiscountLabel || cartState.appliedCoupon.code}`);
    }

    if (specificParts.length > 0) {
      cartState.specificDiscountText = `(${specificParts.join(' + ')})`;
    } else if (totalSavings > 0 && finalRaw > 0) {
      const overallPct = Math.round((totalSavings / finalRaw) * 100);
      cartState.specificDiscountText = `(${overallPct}% OFF)`;
    } else {
      cartState.specificDiscountText = '';
    }

    renderCartView(skipFullDomRebuild);
  }

  // Render Smart Bundle Tier Upgrade / Swap HTML Component
  function renderTierUpgradeHTML(item, unitPrice, lineQty, displayPrice, rawCompareUnit) {
    try {
      if (!config.tierUpgrade || config.tierUpgrade.enabled === false) return '';
      if (!item || item.isGift || item.isGiftWrap) return '';

    // Product eligibility target check: Allow merchant to restrict swap to specific products or tags
    if (!isProductAllowedForTierSwap(item)) return '';

    // Dynamically query bundle tiers for this product
    const tiers = getDynamicBundleTiers(item);
    if (!Array.isArray(tiers) || tiers.length === 0) return '';

    // Normalize and sort tiers ascending by target quantity
    const sortedTiers = tiers
      .map(t => ({
        qty: parseInt(t.minQty || t.qty, 10) || 1,
        discountPct: parseInt(t.discountPct || 0, 10),
        title: t.title || t.label || '',
        label: t.label || '',
        gift: t.gift || null
      }))
      .filter(t => t.qty > 0)
      .sort((a, b) => a.qty - b.qty);

    if (sortedTiers.length === 0) return '';

    const currentQty = Math.max(1, parseInt(item.quantity, 10) || parseInt(item.properties && item.properties._bundle_tier_qty, 10) || 1);

    const rule = getTierUpgradeRuleForProduct(item);
    let nextTier = null;

    if (rule && rule.targetTierQty && rule.targetTierQty !== 'auto') {
      const wantedQty = parseInt(rule.targetTierQty, 10);
      // If customer has already reached or exceeded the target pack, don't show upgrade
      if (currentQty >= wantedQty) return '';

      nextTier = sortedTiers.find(t => t.qty === wantedQty);
      if (!nextTier && wantedQty > 0) {
        const discountPct = wantedQty >= 4 ? 10 : (wantedQty >= 2 ? 5 : 0);
        nextTier = {
          qty: wantedQty,
          discountPct: discountPct,
          title: `Pack of ${wantedQty}`,
          label: `Pack of ${wantedQty} (${discountPct}% OFF)`
        };
      }
    } else {
      // If NO explicit product rule is set, enforce maxSwapLevels gating
      if (!rule) {
        // maxSwapLevels gating: count how many tier boundaries the current qty has crossed
        // 0 = unlimited (always show next tier), 1 = one swap only, 2 = two swaps max, etc.
        const maxSwapLevels = parseInt(config.tierUpgrade.maxSwapLevels, 10) || 0;
        if (maxSwapLevels > 0) {
          let crossedTiers = 0;
          for (let i = 0; i < sortedTiers.length; i++) {
            if (currentQty >= sortedTiers[i].qty && sortedTiers[i].discountPct > 0) {
              crossedTiers++;
            }
          }
          if (crossedTiers >= maxSwapLevels) return '';
        }
      }
      nextTier = sortedTiers.find(t => t.qty > currentQty);
    }

    // If gift milestone gating is active, only show upgrade if target tier has a gift or gift milestone is active
    if (config.tierUpgrade.giftOnly === true || config.tierUpgrade.targetMode === 'gift_milestone_only') {
      const tierHasGift = nextTier && (nextTier.gift === true || nextTier.hasGift === true);
      const subtotal = cartState.original_total_price || 0;
      const isMilestoneActive = isGiftMilestoneUnlocked(subtotal);
      if (!tierHasGift && !isMilestoneActive) {
        return '';
      }
    }

    const style = config.tierUpgrade.style || 'option_2';
    const palette = config.tierUpgrade.palette || 'fresh_lime';
    let badgeText = ((rule && rule.badgeText) || config.tierUpgrade.badgeText || '').trim();
    if (!badgeText || badgeText === 'Swap for better value') {
      badgeText = 'Upgrade for better value';
    }
    let buttonText = ((rule && rule.buttonText) || config.tierUpgrade.buttonText || '').trim();
    if (!buttonText || buttonText === 'Swap') {
      buttonText = 'Upgrade';
    }
    const itemKey = item.key || item.variantId || item.id;

    // Optional dynamic inline style overrides from JS config
    let inlineStyles = '';
    if (config.tierUpgrade.fontSize && Number(config.tierUpgrade.fontSize) !== 12) {
      inlineStyles += `--oc-tier-upgrade-font-size: ${config.tierUpgrade.fontSize}px; `;
    }
    if (config.tierUpgrade.btnFontSize) {
      inlineStyles += `--oc-tier-btn-size: ${config.tierUpgrade.btnFontSize}px; `;
    }
    if (config.tierUpgrade.btnPaddingY) {
      inlineStyles += `--oc-tier-btn-padding-y: ${config.tierUpgrade.btnPaddingY}px; `;
    }
    if (config.tierUpgrade.btnPaddingX) {
      inlineStyles += `--oc-tier-btn-padding-x: ${config.tierUpgrade.btnPaddingX}px; `;
    }
    if (config.tierUpgrade.customColors && typeof config.tierUpgrade.customColors === 'object') {
      const cc = config.tierUpgrade.customColors;
      if (cc.bg) inlineStyles += `--oc-tier-upgrade-custom-bg: ${cc.bg}; `;
      if (cc.border) inlineStyles += `--oc-tier-upgrade-custom-border: ${cc.border}; `;
      if (cc.text) inlineStyles += `--oc-tier-upgrade-custom-text: ${cc.text}; `;
      if (cc.btnBg) inlineStyles += `--oc-tier-upgrade-custom-btn-bg: ${cc.btnBg}; `;
      if (cc.btnText) inlineStyles += `--oc-tier-upgrade-custom-btn-text: ${cc.btnText}; `;
      if (cc.badgeBg) inlineStyles += `--oc-tier-upgrade-custom-badge-bg: ${cc.badgeBg}; `;
      if (cc.badgeText) inlineStyles += `--oc-tier-upgrade-custom-badge-text: ${cc.badgeText}; `;
    }
    const styleAttr = inlineStyles ? ` style="${inlineStyles}"` : '';

    // Safety guard: If customer already reached or exceeded next tier or highest bundle tier, do not show upgrade
    if (!nextTier || parseInt(nextTier.qty, 10) <= currentQty) {
      const highestTier = sortedTiers[sortedTiers.length - 1];
      if (highestTier && highestTier.discountPct > 0 && currentQty >= highestTier.qty) {
        return `
          <div class="oc-tier-upgrade-slot oc-tier-style--opt2 oc-tier-palette--${palette}"${styleAttr}>
            <div class="oc-tier-max-unlocked">
              <span>✓</span> Highest Tier Unlocked (${highestTier.discountPct}% OFF applied)
            </div>
          </div>
        `;
      }
      return '';
    }

    const targetQty = nextTier.qty;
    const targetDiscountPct = nextTier.discountPct;
    const targetTitle = (nextTier.title && !nextTier.title.startsWith('Discount ('))
      ? nextTier.title
      : `Pack of ${targetQty}`;

    // Base per-unit price (original selling price before tier discount)
    const baseUnit = Number(item.basePrice) > 0 
      ? Number(item.basePrice) 
      : (item.originalPrice && currentQty > 0 ? (config.roundDecimals ? Math.round(Number(item.originalPrice) / currentQty) : (Math.round((Number(item.originalPrice) / currentQty) * 100) / 100)) : (Number(item.price) || 0));

    if (baseUnit <= 0) return '';

    // Unit price and total with upgrade discount applied
    const targetUnit = config.roundDecimals 
      ? Math.round(baseUnit * (1 - targetDiscountPct / 100)) 
      : (Math.round(baseUnit * (1 - targetDiscountPct / 100) * 100) / 100);
    const targetTotal = config.roundDecimals 
      ? Math.round(targetUnit * targetQty) 
      : (Math.round(targetUnit * targetQty * 100) / 100);
    const compareTotal = config.roundDecimals 
      ? Math.round(baseUnit * targetQty) 
      : (Math.round(baseUnit * targetQty * 100) / 100);
    const totalSavings = Math.max(0, config.roundDecimals 
      ? Math.round(compareTotal - targetTotal) 
      : (Math.round((compareTotal - targetTotal) * 100) / 100));
    const escapedTitle = targetTitle.replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    // Map style identifiers to CSS classes
    let styleClass = 'oc-tier-style--opt2';
    if (style === 'option_1' || style === 'smart_dock') styleClass = 'oc-tier-style--opt1';
    else if (style === 'option_3' || style === 'micro_chip') styleClass = 'oc-tier-style--opt3';
    else if (style === 'option_4' || style === 'step_up_strip') styleClass = 'oc-tier-style--opt4';

    const discountFormat = (config.tierUpgrade && config.tierUpgrade.discountFormat) || 'save_amount';
    const formattedAmountVal = Number(totalSavings).toLocaleString('en-IN', {
      minimumFractionDigits: config.roundDecimals ? 0 : 2,
      maximumFractionDigits: config.roundDecimals ? 0 : 2
    });
    const formattedAmountRs = `SAVE RS. ${formattedAmountVal}`;

    let tagLabel = '';
    if (targetDiscountPct > 0 || totalSavings > 0) {
      if (discountFormat === 'percent_off') {
        tagLabel = `${targetDiscountPct}% OFF`;
      } else if (discountFormat === 'both_amount_percent') {
        tagLabel = `${formattedAmountRs} (${targetDiscountPct}% OFF)`;
      } else if (discountFormat === 'both_percent_amount') {
        tagLabel = `${targetDiscountPct}% OFF (${formattedAmountRs})`;
      } else {
        // 'save_amount' (Default as requested)
        tagLabel = totalSavings > 0 ? formattedAmountRs : `${targetDiscountPct}% OFF`;
      }
    }

    let innerHTML = '';

    if (styleClass === 'oc-tier-style--opt1') {
      // Option 1: Smart Dock (Compact)
      innerHTML = `
        <div class="oc-opt1-left">
          <div class="oc-opt1-icon-badge">⚡</div>
          <div class="oc-opt1-text-wrap">
            <div class="oc-opt1-main-label">Upgrade to ${targetTitle} &bull; ${tagLabel || `Save ${targetDiscountPct}%`}</div>
            <div class="oc-opt1-sub-label">Only ${formatMoney(targetUnit)}/ea (Total ${formatMoney(targetTotal)})</div>
          </div>
        </div>
        <button type="button" class="oc-opt1-btn" data-action="tier-swap" data-key="${itemKey}" data-qty="${targetQty}" data-title="${escapedTitle}" onclick="OrgaciaCart.executeTierSwap('${itemKey}', ${targetQty}, '${escapedTitle}')">
          <span class="oc-btn-icon-wrap" aria-hidden="true"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg></span>
          <span>${buttonText}</span>
        </button>
      `;
    } else if (styleClass === 'oc-tier-style--opt3') {
      // Option 3: Micro-Chip (Minimalist)
      innerHTML = `
        <div class="oc-opt3-left">
          <span>💡</span>
          <span>Upgrade to <strong>${targetTitle}</strong> & save <strong>${tagLabel || `${targetDiscountPct}%`}</strong></span>
        </div>
        <button type="button" class="oc-opt3-btn" data-action="tier-swap" data-key="${itemKey}" data-qty="${targetQty}" data-title="${escapedTitle}" onclick="OrgaciaCart.executeTierSwap('${itemKey}', ${targetQty}, '${escapedTitle}')">
          <span class="oc-btn-icon-wrap" aria-hidden="true"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg></span>
          <span>${buttonText}</span>
        </button>
      `;
    } else if (styleClass === 'oc-tier-style--opt4') {
      // Option 4: Step-Up Strip (Progressive)
      const currentTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : `Pack of ${currentQty}`;
      innerHTML = `
        <div class="oc-opt4-stepper">
          <span class="oc-opt4-cur">${currentTitle}</span>
          <span class="oc-opt4-arrow">➔</span>
          <span class="oc-opt4-next">${targetTitle}</span>
          ${tagLabel ? `<span class="oc-opt4-badge">${tagLabel}</span>` : ''}
        </div>
        <button type="button" class="oc-opt4-btn" data-action="tier-swap" data-key="${itemKey}" data-qty="${targetQty}" data-title="${escapedTitle}" onclick="OrgaciaCart.executeTierSwap('${itemKey}', ${targetQty}, '${escapedTitle}')">
          <span class="oc-btn-icon-wrap" aria-hidden="true"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg></span>
          <span>${buttonText}</span>
        </button>
      `;
    } else {
      // Option 2: Nested Ledge (Reference Style - Default)
      const itemImg = getValidImageUrl(nextTier.image || item.image);
      innerHTML = `
        <div class="oc-opt2-badge">${badgeText}</div>
        <div class="oc-opt2-card">
          <div class="oc-opt2-card-left">
            <img src="${itemImg}" alt="${escapedTitle}" class="oc-opt2-thumb" loading="eager" decoding="async" fetchpriority="high" onerror="this.onerror=null; this.src='${ORGACIA_DEFAULT_PRODUCT_SVG}';">
            <div class="oc-opt2-info">
              <div class="oc-opt2-title">${targetTitle}</div>
              <div class="oc-opt2-prices">
                <span class="oc-opt2-price">${formatMoney(targetTotal)}</span>
                ${compareTotal > targetTotal ? `<span class="oc-opt2-compare">${formatMoney(compareTotal)}</span>` : ''}
                ${tagLabel ? `<span class="oc-opt2-tag">${tagLabel}</span>` : ''}
              </div>
            </div>
          </div>
          <button type="button" class="oc-opt2-btn" data-action="tier-swap" data-key="${itemKey}" data-qty="${targetQty}" data-title="${escapedTitle}" onclick="OrgaciaCart.executeTierSwap('${itemKey}', ${targetQty}, '${escapedTitle}')">
            <span class="oc-btn-icon-wrap" aria-hidden="true"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg></span>
            <span>${buttonText}</span>
          </button>
        </div>
      `;
    }

    return `
      <div class="oc-tier-upgrade-slot ${styleClass} oc-tier-palette--${palette}"${styleAttr}>
        ${innerHTML}
      </div>
    `;
    } catch (err) {
      console.warn('Orgacia tier upgrade render error', err);
      return '';
    }
  }

  // Render Full Cart View
  function renderCartView(skipFullDomRebuild = false) {
    const headerTitle = document.getElementById('oc-header-title');
    const itemsContainer = document.getElementById('oc-cart-items');
    const emptyState = document.getElementById('oc-empty-state');
    const floatingCount = document.getElementById('oc-floating-cart-count');
    const totalAmount = document.getElementById('oc-total-amount');
    const comparePriceEl = document.getElementById('oc-total-compare-price');
    const discountTag = document.getElementById('oc-total-discount-tag');
    const turkishRibbon = document.getElementById('oc-turkish-savings-ribbon');
    const progressFill = document.getElementById('oc-progress-fill');
    const progressTitle = document.getElementById('oc-progress-title');
    const appliedTag = document.getElementById('oc-applied-coupon-tag');
    const appliedText = document.getElementById('oc-applied-coupon-text');
    const giftWrapCheckbox = document.getElementById('oc-giftwrap-checkbox');

    if (headerTitle) {
      headerTitle.textContent = `${config.title} (${cartState.item_count} ${cartState.item_count === 1 ? 'item' : 'items'})`;
    }
    if (floatingCount) {
      floatingCount.textContent = cartState.item_count;
    }

    // Sync Gift Wrap checkbox state
    if (giftWrapCheckbox) {
      giftWrapCheckbox.checked = cartState.items.some(i => i.isGiftWrap);
    }

    // Resilient check: Cart is empty if there are 0 items, item_count is 0, or NO regular products remain
    const regularItems = cartState.items.filter(i => !i.isGift && !i.isGiftWrap && (i.quantity > 0));
    const isCartTrulyEmpty = cartState.items.length === 0 || cartState.item_count === 0 || regularItems.length === 0;

    // EMPTY CART STATE: Remove EVERYTHING except announcement bar and clean empty view (Image 2 Match)
    if (isCartTrulyEmpty) {
      // Cleanly clear state when no products remain
      cartState.items = [];
      cartState.item_count = 0;
      cartState.original_total_price = 0;
      cartState.total_price = 0;
      cartState.discountAmount = 0;
      cartState.raw_total_price = 0;
      cartState.isGiftWrapAdded = false;

      if (itemsContainer) {
        itemsContainer.innerHTML = '';
        itemsContainer.style.display = 'none';
      }
      if (emptyState) {
        emptyState.classList.add('oc-show');
        emptyState.style.display = 'flex';
        emptyState.style.visibility = 'visible';
        emptyState.style.opacity = '1';
      }

      // Reset scroll position so empty state is never scrolled out of view
      const viewCart = document.getElementById('oc-view-cart');
      if (viewCart) {
        viewCart.scrollTop = 0;
      }

      closeGiftModal();

      const hideIds = [
        'oc-urgency-bar',
        'oc-progress-section',
        'oc-milestone-coupon-banner',
        'oc-unclaimed-gift-banner',
        'oc-checkout-coupon-notice',
        'oc-giftwrap-card',
        'oc-coupon-card',
        'oc-instructions-section',
        'oc-upsell-section',
        'oc-footer',
        'oc-turkish-savings-ribbon'
      ];
      hideIds.forEach(id => {
        const el = document.getElementById(id);
        if (el) {
          el.style.display = 'none';
          el.classList.add('oc-hidden');
        }
      });
      const emptyFooter = document.getElementById('oc-footer');
      if (emptyFooter) emptyFooter.classList.remove('oc-has-savings-ribbon');

      // Clear milestone and coupon state when cart is empty
      cartState.unlockedMilestoneCode = null;
      cartState.unlockedMilestoneTier = null;
      cartState.unlockedMilestonePct = 0;
      cartState.milestoneDiscountCode = null;
      cartState.appliedCoupon = null;

      resetCheckoutButton();
      return;
    } else {
      // ACTIVE CART STATE: remove empty state and unhide active sections
      if (emptyState) {
        emptyState.classList.remove('oc-show');
        emptyState.style.display = 'none';
      }

      const activeIds = [
        'oc-urgency-bar',
        'oc-progress-section',
        'oc-giftwrap-card',
        'oc-coupon-card',
        'oc-instructions-section',
        'oc-upsell-section',
        'oc-footer',
        'oc-turkish-savings-ribbon'
      ];
      activeIds.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.classList.remove('oc-hidden');
      });

      // Milestone Free Gift Status & Multiple Gifts Unclaimed Banner Toggle
      const regularItems = cartState.items.filter(i => !i.isGift && !i.isGiftWrap);
      const hasMainProducts = regularItems.length > 0;
      const giftUnlocked = hasMainProducts && isGiftEligible(cartState.original_total_price);
      const claimedGifts = cartState.items.filter(i => i.isGift);
      const maxGifts = getMaxAllowedGifts();
      const unclaimedCount = maxGifts - claimedGifts.length;
      const unclaimedGiftBanner = document.getElementById('oc-unclaimed-gift-banner');
      const unclaimedTextEl = document.getElementById('oc-unclaimed-gift-text');

      // If main product is removed or cart amount dropped below gift threshold, auto-remove gift
      if ((!hasMainProducts || !giftUnlocked) && claimedGifts.length > 0) {
        const giftsToRemove = [...claimedGifts];
        cartState.items = cartState.items.filter(i => !i.isGift);
        closeGiftModal();
        showToast('Free gift removed (milestone requirement not met)');
        removeGiftFromShopify(giftsToRemove);
        updateCartCalculations();
        return;
      }

      if (unclaimedGiftBanner) {
        if (config.giftSelection && config.giftSelection.enabled && giftUnlocked && unclaimedCount > 0) {
          unclaimedGiftBanner.style.display = 'flex';
          if (unclaimedTextEl) {
            unclaimedTextEl.textContent = unclaimedCount === 1 ? 'You have an unclaimed free gift!' : `You have ${unclaimedCount} unclaimed free gifts!`;
          }
        } else {
          unclaimedGiftBanner.style.display = 'none';
        }
      }

      // Segregated Milestone Claim Display Styles (Top & Bottom)
      const topClaimStyle = config.milestoneTopClaimStyle || (config.tiers && config.tiers.milestoneTopClaimStyle) || 'none';
      const bottomClaimStyle = config.milestoneBottomClaimStyle || (config.tiers && config.tiers.milestoneBottomClaimStyle) || 'classic_bar';

      const shouldShowTop = topClaimStyle !== 'none' && topClaimStyle !== 'hidden';
      const shouldShowBottom = bottomClaimStyle !== 'none' && bottomClaimStyle !== 'hidden';

      // 1. TOP Milestone Unlocked Coupon Banner
      const milestoneCouponBanner = document.getElementById('oc-milestone-coupon-banner');
      const milestoneCouponText = document.getElementById('oc-milestone-coupon-text');
      const milestoneCouponBtn = document.getElementById('oc-milestone-coupon-btn');
      const milestoneCouponIcon = milestoneCouponBanner ? milestoneCouponBanner.querySelector('.oc-milestone-coupon-icon') : null;

      if (milestoneCouponBanner) {
        const isCouponApplied = cartState.appliedCoupon && cartState.unlockedMilestoneCode && cartState.appliedCoupon.code.toUpperCase() === cartState.unlockedMilestoneCode.toUpperCase();
        if (shouldShowTop && cartState.unlockedMilestoneCode && !isCouponApplied && cartState.items.length > 0) {
          let topClass = 'oc-top-claim--emerald';
          if (topClaimStyle === 'compact_pill') topClass = 'oc-top-claim--pill';
          else if (topClaimStyle === 'luxury_gold') topClass = 'oc-top-claim--gold';
          else if (topClaimStyle === 'clean_minimal') topClass = 'oc-top-claim--minimal';

          milestoneCouponBanner.className = `oc-milestone-coupon-banner ${topClass}`;

          if (milestoneCouponIcon) {
            if (topClaimStyle === 'luxury_gold') milestoneCouponIcon.textContent = '👑';
            else if (topClaimStyle === 'compact_pill') milestoneCouponIcon.innerHTML = '<span class="oc-pulsing-green-dot"></span>';
            else if (topClaimStyle === 'clean_minimal') milestoneCouponIcon.textContent = '🏷️';
            else milestoneCouponIcon.textContent = '🎉';
          }

          if (milestoneCouponText) {
            milestoneCouponText.innerHTML = `<strong>${cartState.unlockedMilestonePct}% OFF Unlocked!</strong> Use code <strong>${cartState.unlockedMilestoneCode}</strong>`;
          }
          if (milestoneCouponBtn) {
            milestoneCouponBtn.textContent = 'APPLY';
            milestoneCouponBtn.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              OrgaciaCart.applyCoupon(cartState.unlockedMilestoneCode);
            };
          }
          milestoneCouponBanner.classList.remove('oc-hidden');
          milestoneCouponBanner.style.display = 'flex';
        } else {
          milestoneCouponBanner.style.display = 'none';
          milestoneCouponBanner.classList.add('oc-hidden');
        }
      }

      // 2. BOTTOM Checkout Coupon Notice above Checkout CTA
      const checkoutNotice = document.getElementById('oc-checkout-coupon-notice');
      const checkoutNoticeCode = document.getElementById('oc-checkout-notice-code');
      if (checkoutNotice && checkoutNoticeCode) {
        let milestoneNoticeCode = null;
        if (cartState.unlockedMilestoneCode) {
          milestoneNoticeCode = cartState.unlockedMilestoneCode;
        } else if (cartState.appliedCoupon && cartState.appliedCoupon.code) {
          milestoneNoticeCode = cartState.appliedCoupon.code;
        }

        if (shouldShowBottom && milestoneNoticeCode && cartState.items.length > 0) {
          checkoutNoticeCode.textContent = milestoneNoticeCode;

          let bClass = 'oc-bottom-claim--classic oc-notice-style--notice';
          if (bottomClaimStyle === 'compact_pill') bClass = 'oc-bottom-claim--pill oc-notice-style--pill';
          else if (bottomClaimStyle === 'golden_ticket') bClass = 'oc-bottom-claim--ticket oc-notice-style--ticket';
          else if (bottomClaimStyle === 'cyber_neon') bClass = 'oc-bottom-claim--neon oc-notice-style--neon';
          else if (bottomClaimStyle === 'floating_chip') bClass = 'oc-bottom-claim--chip';

          checkoutNotice.className = `oc-checkout-coupon-notice ${bClass}`;

          const noticeIconEl = checkoutNotice.querySelector('.oc-checkout-notice-icon');
          if (noticeIconEl) {
            if (bottomClaimStyle === 'compact_pill') {
              noticeIconEl.innerHTML = '<span class="oc-pulsing-green-dot"></span>';
            } else if (bottomClaimStyle === 'golden_ticket') {
              noticeIconEl.textContent = '🎟️';
            } else if (bottomClaimStyle === 'cyber_neon') {
              noticeIconEl.textContent = '⚡';
            } else if (bottomClaimStyle === 'floating_chip') {
              noticeIconEl.textContent = '✨';
            } else {
              noticeIconEl.textContent = '🏷️';
            }
          }

          checkoutNotice.classList.remove('oc-hidden');
          checkoutNotice.style.display = 'block';
        } else {
          checkoutNotice.style.display = 'none';
          checkoutNotice.classList.add('oc-hidden');
        }
      }

      const timerEl = document.getElementById('oc-urgency-bar');
      if (timerEl) timerEl.style.display = (config.timer && config.timer.enabled !== false) ? 'flex' : 'none';

      const barEl = document.getElementById('oc-announcement-bar');
      if (barEl) {
        const hasAnnounce = !!(config.announcement && config.announcement.enabled !== false && Array.isArray(config.announcement.slides) && config.announcement.slides.length > 0);
        barEl.style.display = hasAnnounce ? 'flex' : 'none';
      }

      const tiersEl = document.getElementById('oc-progress-section');
      if (tiersEl) tiersEl.style.display = (config.tiers && config.tiers.enabled !== false) ? 'block' : 'none';

      const giftEl = document.getElementById('oc-giftwrap-card');
      if (giftEl) giftEl.style.display = (config.giftWrap && config.giftWrap.enabled !== false) ? 'flex' : 'none';

      const upsellEl = document.getElementById('oc-upsell-section');
      if (upsellEl) upsellEl.style.display = (config.upsell && config.upsell.enabled !== false) ? 'block' : 'none';

      const couponCard = document.getElementById('oc-coupon-card');
      const isCouponsEnabled = config.couponsEnabled !== false;
      const hasConfigCoupons = Array.isArray(config.coupons) && config.coupons.length > 0;

      if (couponCard) {
        if (!isCouponsEnabled) {
          couponCard.style.display = 'none';
        } else {
          couponCard.style.display = 'flex';
          const couponFooter = document.getElementById('oc-coupon-footer');
          if (couponFooter) couponFooter.style.display = hasConfigCoupons ? 'flex' : 'none';
        }
      }
      
      const instructionsSection = document.getElementById('oc-instructions-section');
      if (instructionsSection) {
        instructionsSection.style.display = (config.instructions && config.instructions.enabled === false) ? 'none' : 'block';
      }

      const instructionsToggleText = document.querySelector('.oc-instructions-toggle-text');
      if (instructionsToggleText && config.instructions && config.instructions.label) {
        instructionsToggleText.textContent = config.instructions.label;
      }

      const instructionsTextarea = document.getElementById('oc-instructions-textarea');
      if (instructionsTextarea) {
        if (config.instructions && config.instructions.placeholder) {
          instructionsTextarea.setAttribute('placeholder', config.instructions.placeholder);
        }
        if (cartState.note && !instructionsTextarea.value) {
          instructionsTextarea.value = cartState.note;
        }
      }

      if (document.getElementById('oc-footer')) document.getElementById('oc-footer').style.display = 'flex';

      if (itemsContainer) {
        itemsContainer.style.display = 'flex';
        try {
          itemsContainer.innerHTML = cartState.items.map((item, index) => {
            try {
              const isGift = item.isGift === true;
              const isGiftWrap = item.isGiftWrap === true;
              const lineQty = Math.max(1, parseInt(item.quantity, 10) || 1);
              const unitPrice = Number(item.calculatedPrice !== undefined ? item.calculatedPrice : item.price) || 0;
              const displayPrice = isGift ? 0 : (config.roundDecimals ? Math.round(unitPrice * lineQty) : (Math.round(unitPrice * lineQty * 100) / 100));

              const rawCompareUnit = item.compare_at_price 
                ? Number(item.compare_at_price) 
                : ((item.serverOriginalPrice && item.serverOriginalPrice > unitPrice) 
                    ? Number(item.serverOriginalPrice) 
                    : (item.basePrice && item.basePrice > unitPrice ? Number(item.basePrice) : 0));
              const unitComparePrice = rawCompareUnit > unitPrice ? rawCompareUnit : unitPrice;
              const rawCompare = config.roundDecimals ? Math.round(unitComparePrice * lineQty) : (Math.round(unitComparePrice * lineQty * 100) / 100);
              const badgeLabel = item.serverDiscountTitle || item.bundleLabel || (item.discountPct > 0 ? `${item.discountPct}% OFF` : '');
              const hasDiscount = rawCompare > displayPrice || item.discountPct > 0 || item.hasServerDiscount || !!item.serverDiscountTitle || isGiftWrap;

              const giftComparePrice = item.originalPrice || item.compare_at_price || item.basePrice || 0;
              let tierUpgradeHtml = '';
              try {
                tierUpgradeHtml = renderTierUpgradeHTML(item, unitPrice, lineQty, displayPrice, rawCompareUnit);
              } catch (tErr) {
                tierUpgradeHtml = '';
              }
              const hasTierUpgrade = !!tierUpgradeHtml;

              const cartItemImg = getValidImageUrl(item.image);

              return `
              <div class="oc-cart-item ${isGift ? 'oc-cart-item--gift' : ''} ${hasTierUpgrade ? 'oc-has-tier-upgrade' : ''}" data-index="${index}" data-key="${item.key || item.id}" data-bundle-key="${badgeLabel}_${item.variant_title || ''}_${item.discountPct || 0}_${item.quantity}">
                <div class="oc-cart-item__main">
                  <div class="oc-item__image-wrap">
                    <img src="${cartItemImg}" alt="${escapeHTML(item.title || 'Product')}" class="oc-item__image" loading="eager" decoding="async" fetchpriority="high" onerror="this.onerror=null; this.src='${ORGACIA_DEFAULT_PRODUCT_SVG}';">
                  </div>
                  <div class="oc-item__details">
                    <div class="oc-item__top-row">
                      <div>
                        <a href="${item.url || '#'}" class="oc-item__title">${item.title}</a>
                        ${isGift ? `
                          <div style="display: flex; flex-wrap: wrap; gap: 4px; margin-top: 3px;">
                            <span class="oc-gift-badge">🎁 FREE UNLOCKED GIFT</span>
                            ${badgeLabel ? `<span class="oc-bundle-badge">🏷️ ${escapeHTML(badgeLabel)}</span>` : ''}
                          </div>
                        ` : `
                          ${(hasDiscount || item.hasServerDiscount) && badgeLabel ? `<span class="oc-bundle-badge">🏷️ ${escapeHTML(badgeLabel)}</span>` : ''}
                        `}
                      </div>
                      <div class="oc-item__price-wrap">
                        ${isGift ? `
                          <div class="oc-item__price oc-price-free">FREE</div>
                          ${giftComparePrice > 0 ? `<div class="oc-item__compare-price oc-gift-strikethrough-price">${formatMoney(giftComparePrice)}</div>` : ''}
                        ` : `
                          <div class="oc-item__price">${formatMoney(displayPrice)}</div>
                          ${(hasDiscount || item.hasServerDiscount) && rawCompare > displayPrice ? `<div class="oc-item__compare-price">${formatMoney(rawCompare)}</div>` : ''}
                        `}
                      </div>
                    </div>
                    
                    ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="oc-item__variant">${item.variant_title}</div>` : ''}
                    
                    ${isGift ? `
                      <div class="oc-item__bottom-row" style="justify-content: flex-end;">
                        <button type="button" class="oc-item__delete-btn" data-action="delete" data-index="${index}" data-key="${item.key || item.variantId || item.id}" aria-label="Remove free gift" title="Remove free gift">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                        </button>
                      </div>
                    ` : `
                      <div class="oc-item__bottom-row">
                        <div class="oc-qty-stepper">
                          <button type="button" class="oc-qty-btn" data-action="decrease" data-index="${index}" data-key="${item.key || item.variantId || item.id}" aria-label="Decrease quantity">-</button>
                          <span class="oc-qty-val" data-index="${index}">${item.quantity}</span>
                          <button type="button" class="oc-qty-btn" data-action="increase" data-index="${index}" data-key="${item.key || item.variantId || item.id}" aria-label="Increase quantity">+</button>
                        </div>
                        <button type="button" class="oc-item__delete-btn" data-action="delete" data-index="${index}" data-key="${item.key || item.variantId || item.id}" aria-label="Remove item">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                        </button>
                      </div>
                    `}
                  </div>
                </div>
                ${tierUpgradeHtml}
              </div>
            `;
            } catch (itemErr) {
              console.error('Orgacia cart item render error', itemErr);
              return '';
            }
          }).join('');
        } catch (containerErr) {
          console.error('Orgacia itemsContainer error', containerErr);
        }
      }
    }

    // PERCHED AUTO-SAVINGS RIBBON (Integrated directly on top of Estimated Total card)
    const footerEl = document.getElementById('oc-footer');
    let ribbonEl = document.getElementById('oc-turkish-savings-ribbon');

    if (footerEl) {
      // Remove any stray ribbons that are direct children of oc-drawer (stale/cached DOM)
      const strayRibbons = document.querySelectorAll('.oc-drawer > .oc-turkish-savings-ribbon, #oc-drawer > #oc-turkish-savings-ribbon');
      strayRibbons.forEach(stray => {
        if (stray !== ribbonEl && stray.parentElement !== footerEl) {
          stray.remove();
        }
      });

      // Strict DOM Docking: Ensure ribbon is ALWAYS placed inside #oc-footer, never in drawer root
      if (ribbonEl && ribbonEl.parentElement !== footerEl) {
        footerEl.insertBefore(ribbonEl, footerEl.firstChild);
      } else if (!ribbonEl) {
        ribbonEl = document.createElement('div');
        ribbonEl.className = `oc-turkish-savings-ribbon oc-savings-style--${config.savingsStyle || 'tab'}`;
        ribbonEl.id = 'oc-turkish-savings-ribbon';
        ribbonEl.style.display = 'none';
        footerEl.insertBefore(ribbonEl, footerEl.firstChild);
      }
    }

    if (ribbonEl) {
      const isCalloutEnabled = config.savingsCallout ? config.savingsCallout.enabled !== false : true;
      if (isCalloutEnabled && cartState.discountAmount > 0 && cartState.items.length > 0) {
        ribbonEl.style.display = 'flex';
        ribbonEl.classList.add('oc-show');
        const template = (config.savingsCallout && config.savingsCallout.template) || '{amount} Saved so far!';
        const formattedAmount = formatMoney(cartState.discountAmount);
        ribbonEl.textContent = template.replace('{amount}', formattedAmount);
        if (footerEl) footerEl.classList.add('oc-has-savings-ribbon');
      } else {
        ribbonEl.style.display = 'none';
        ribbonEl.classList.remove('oc-show');
        if (footerEl) footerEl.classList.remove('oc-has-savings-ribbon');
      }
    }

    // Totals in Estimated Total Row
    if (totalAmount) {
      totalAmount.textContent = formatMoney(cartState.total_price);
    }

    const savingsSummaryEl = document.getElementById('oc-total-savings-summary');
    if (savingsSummaryEl) {
      if (cartState.discountAmount > 0 && cartState.items.length > 0) {
        savingsSummaryEl.style.display = 'inline-block';
        savingsSummaryEl.textContent = `You save ${formatMoney(cartState.discountAmount)}!`;
      } else {
        savingsSummaryEl.style.display = 'none';
      }
    }

    if (comparePriceEl && discountTag) {
      if (cartState.discountAmount > 0 && cartState.raw_total_price > cartState.total_price) {
        comparePriceEl.style.display = 'inline';
        comparePriceEl.textContent = formatMoney(cartState.raw_total_price);
        discountTag.style.display = 'inline-block';
        discountTag.textContent = cartState.specificDiscountText || `(${Math.round((cartState.discountAmount / cartState.raw_total_price) * 100)}% OFF)`;
      } else {
        comparePriceEl.style.display = 'none';
        discountTag.style.display = 'none';
      }
    }

    // Coupon Tag
    if (appliedTag && appliedText) {
      if (cartState.appliedCoupon) {
        appliedTag.classList.add('oc-show');
        appliedText.innerHTML = `🏷️ <strong>${cartState.appliedCoupon.code}</strong> applied (-${formatMoney(cartState.discountAmount)})`;
      } else {
        appliedTag.classList.remove('oc-show');
      }
    }

    // Sync Terms & Conditions visibility
    const tcRow = document.getElementById('oc-tc-row');
    if (tcRow) {
      tcRow.style.display = (config.footer && config.footer.termsRequired) ? 'flex' : 'none';
    }

    // Sync Trust Badges visibility
    const trustBadgesEl = document.getElementById('oc-trust-badges');
    if (trustBadgesEl) {
      trustBadgesEl.style.display = (config.footer && config.footer.showTrustBadges !== false && Array.isArray(config.footer.trustBadges) && config.footer.trustBadges.length > 0) ? 'flex' : 'none';
    }

    // Progress Bar & Milestones (Equidistant Milestone Alignment)
    const rawActiveTiers = (config.tiers && config.tiers.items) || [];
    const tiers = rawActiveTiers.filter(t => t && t.threshold !== undefined && t.enabled !== false);
    if (config.tiers && config.tiers.enabled && tiers.length > 0 && progressFill && progressTitle && cartState.items.length > 0) {
      const numTiers = tiers.length;
      const currentAmount = cartState.original_total_price;
      const inset = numTiers > 2 ? 14 : (numTiers === 2 ? 20 : 50);
      const span = 100 - (2 * inset);
      const pinPositions = tiers.map((t, idx) => numTiers > 1 ? inset + (idx / (numTiers - 1)) * span : 50);

      let progressPercent = 0;
      if (currentAmount <= 0) {
        progressPercent = 0;
      } else if (currentAmount >= tiers[numTiers - 1].threshold) {
        progressPercent = 100;
      } else if (currentAmount < tiers[0].threshold) {
        progressPercent = Math.min(pinPositions[0], (currentAmount / tiers[0].threshold) * pinPositions[0]);
      } else {
        for (let i = 0; i < numTiers - 1; i++) {
          if (currentAmount >= tiers[i].threshold && currentAmount < tiers[i + 1].threshold) {
            const segStartThresh = tiers[i].threshold;
            const segEndThresh = tiers[i + 1].threshold;
            const segStartPos = pinPositions[i];
            const segEndPos = pinPositions[i + 1];
            const segRatio = (currentAmount - segStartThresh) / Math.max(1, (segEndThresh - segStartThresh));
            progressPercent = segStartPos + (segRatio * (segEndPos - segStartPos));
            break;
          }
        }
      }

      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(() => {
          progressFill.style.width = `${Math.min(100, Math.max(0, progressPercent))}%`;
        });
      } else {
        progressFill.style.width = `${Math.min(100, Math.max(0, progressPercent))}%`;
      }

      let nextTier = null;
      tiers.forEach((tier, index) => {
        const node = document.getElementById(`oc-milestone-${index}`);
        const isUnlocked = currentAmount >= tier.threshold;

        if (node) {
          const pinInner = node.querySelector('.oc-milestone-pin');
          const wasUnlocked = node.dataset.unlocked === 'true';
          if (isUnlocked) {
            if (!wasUnlocked) {
              node.dataset.unlocked = 'true';
              node.classList.add('oc-unlocked');
              node.classList.remove('oc-pulse-target');
              if (pinInner) pinInner.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
            }
          } else {
            if (wasUnlocked || !node.dataset.unlocked) {
              node.dataset.unlocked = 'false';
              node.classList.remove('oc-unlocked');
              if (pinInner) pinInner.innerHTML = getMilestoneIconHtml(tier.icon, tier.label);
            }
            if (!nextTier) {
              nextTier = tier;
              node.classList.add('oc-pulse-target');
            } else {
              node.classList.remove('oc-pulse-target');
            }
          }
        }
      });

      if (!nextTier) {
        progressTitle.innerHTML = `🎉 <strong>You have unlocked all rewards!</strong>`;
      } else {
        const remaining = nextTier.threshold - currentAmount;
        progressTitle.innerHTML = `Add <strong>${formatMoney(remaining)}</strong> more to get a <strong>${nextTier.label}</strong> on this order`;
      }
    }

    if (!skipFullDomRebuild) {
      renderUpsells();
      renderOffers();
    }
    resetCheckoutButton();
  }

  // Events Binding
  function bindEvents() {
    const drawer = document.getElementById('oc-drawer');
    const overlay = document.getElementById('oc-drawer-overlay');
    const closeBtn = document.getElementById('oc-close-btn');
    const emptyBtn = document.getElementById('oc-empty-btn');
    const backBtn = document.getElementById('oc-header-back-btn');
    const header = document.getElementById('oc-header');
    const viewCouponsBtn = document.getElementById('oc-view-coupons-btn');
    const couponInput = document.getElementById('oc-coupon-input');
    const couponApplyBtn = document.getElementById('oc-coupon-apply-btn');
    const subpageCouponInput = document.getElementById('oc-subpage-coupon-input');
    const subpageApplyBtn = document.getElementById('oc-subpage-apply-btn');
    const removeCouponBtn = document.getElementById('oc-remove-coupon-btn');
    const giftWrapCheckbox = document.getElementById('oc-giftwrap-checkbox');
    const instructionsToggle = document.getElementById('oc-instructions-toggle');
    const instructionsBody = document.getElementById('oc-instructions-body');
    const instructionsTextarea = document.getElementById('oc-instructions-textarea');
    const checkoutBtn = document.getElementById('oc-checkout-btn');
    const tcCheckbox = document.getElementById('oc-tc-checkbox');

    // Close actions
    overlay.addEventListener('click', () => OrgaciaCart.close());
    closeBtn.addEventListener('click', () => OrgaciaCart.close());
    if (emptyBtn) emptyBtn.addEventListener('click', () => OrgaciaCart.close());

    // Switch between Main Cart and Discounts Subpage
    viewCouponsBtn.addEventListener('click', () => {
      drawer.classList.add('oc-show-discounts');
      header.classList.add('oc-header--subpage');
      document.getElementById('oc-header-title').textContent = 'Coupons & Offers';
      renderOffers(true);
    });

    backBtn.addEventListener('click', () => {
      drawer.classList.remove('oc-show-discounts');
      header.classList.remove('oc-header--subpage');
      document.getElementById('oc-header-title').textContent = `${config.title} (${cartState.item_count} items)`;
    });

    // Inline Coupon input & Apply
    const handleCouponApply = () => {
      const code = couponInput.value.trim();
      if (code) {
        OrgaciaCart.applyCoupon(code);
        couponInput.value = '';
      } else {
        couponInput.focus();
      }
    };

    couponApplyBtn.addEventListener('click', handleCouponApply);
    couponInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCouponApply();
      }
    });

    // Best Price Guaranteed VIP Code Toggle & Apply in Main Drawer
    const vipToggleBtn = document.getElementById('oc-vip-toggle-btn');
    const vipDrawer = document.getElementById('oc-vip-input-drawer');
    const vipInput = document.getElementById('oc-vip-coupon-input');
    const vipApplyBtn = document.getElementById('oc-vip-apply-btn');

    if (vipToggleBtn && vipDrawer) {
      vipToggleBtn.addEventListener('click', () => {
        const isHidden = vipDrawer.style.display === 'none';
        vipDrawer.style.display = isHidden ? 'block' : 'none';
        vipToggleBtn.classList.toggle('active', isHidden);
        if (isHidden && vipInput) vipInput.focus();
      });
    }

    if (vipApplyBtn && vipInput) {
      const handleVipApply = () => {
        const code = vipInput.value.trim();
        if (code) {
          OrgaciaCart.applyCoupon(code);
          vipInput.value = '';
        } else {
          vipInput.focus();
        }
      };
      vipApplyBtn.addEventListener('click', handleVipApply);
      vipInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleVipApply();
        }
      });
    }

    // Subpage coupon input & apply
    const handleSubpageCouponApply = () => {
      const code = subpageCouponInput.value.trim();
      if (code) {
        OrgaciaCart.applyCoupon(code);
        subpageCouponInput.value = '';
        backBtn.click();
      } else {
        subpageCouponInput.focus();
      }
    };

    subpageApplyBtn.addEventListener('click', handleSubpageCouponApply);
    subpageCouponInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubpageCouponApply();
      }
    });

    // Offers List apply buttons
    document.getElementById('oc-offers-list').addEventListener('click', (e) => {
      const btn = e.target.closest('.oc-offer-apply-btn');
      if (btn) {
        const code = btn.getAttribute('data-code');
        OrgaciaCart.applyCoupon(code);
        backBtn.click();
      }
    });

    // Remove Coupon
    removeCouponBtn.addEventListener('click', () => {
      OrgaciaCart.removeCoupon();
    });

    // Checkout Coupon Notice Copy Button (Enabled when milestone discount is unlocked)
    const noticeCopyBtn = document.getElementById('oc-checkout-notice-copy-btn');
    if (noticeCopyBtn) {
      noticeCopyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const codeEl = document.getElementById('oc-checkout-notice-code');
        const code = codeEl ? codeEl.textContent.trim() : '';
        if (code) {
          copyToClipboard(code);
          showToast(`✓ Copied coupon code "${code}" to clipboard!`);
          noticeCopyBtn.textContent = 'Copied!';
          setTimeout(() => { noticeCopyBtn.textContent = 'Copy'; }, 1800);
        }
      });
    }

    // Toast Notification Tap / Close to dismiss immediately
    const toastBannerEl = document.getElementById('oc-toast-banner');
    if (toastBannerEl && !toastBannerEl.getAttribute('data-listener-attached')) {
      toastBannerEl.setAttribute('data-listener-attached', 'true');
      toastBannerEl.addEventListener('click', () => {
        hideToast();
      });
    }

    // Quantity Stepper & Delete in Cart Items
    document.getElementById('oc-cart-items').addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      if (!btn) return;
      const rawIndex = parseInt(btn.getAttribute('data-index'), 10);
      const action = btn.getAttribute('data-action');
      const itemKey = btn.getAttribute('data-key');

      // Resilient item resolution (matches index or itemKey if array shifted)
      let item = cartState.items[rawIndex];
      let targetIndex = rawIndex;
      if (itemKey && (!item || String(item.key || item.variantId || item.id) !== String(itemKey))) {
        const foundIdx = cartState.items.findIndex(i => String(i.key || i.variantId || i.id) === String(itemKey));
        if (foundIdx !== -1) {
          item = cartState.items[foundIdx];
          targetIndex = foundIdx;
        }
      }
      if (!item) return;

      if (action === 'increase') {
        OrgaciaCart.updateQuantity(targetIndex, item.quantity + 1);
      } else if (action === 'decrease') {
        const newQty = item.quantity - 1;
        if (newQty <= 0) {
          if (item && item.isGiftWrap) {
            showToast('Removed Gift Wrap from cart');
            if (giftWrapCheckbox) giftWrapCheckbox.checked = false;
          }
          if (item && item.isGift) {
            showToast('Removed Free Gift from cart');
          }
          OrgaciaCart.removeItem(targetIndex);
        } else {
          OrgaciaCart.updateQuantity(targetIndex, newQty);
        }
      } else if (action === 'delete') {
        if (item && item.isGiftWrap) {
          showToast('Removed Gift Wrap from cart');
          if (giftWrapCheckbox) giftWrapCheckbox.checked = false;
        }
        if (item && item.isGift) {
          showToast('Removed Free Gift from cart');
        }
        OrgaciaCart.removeItem(targetIndex);
      } else if (action === 'tier-swap') {
        const targetQty = parseInt(btn.getAttribute('data-qty'), 10);
        const variantTitle = btn.getAttribute('data-title');
        if (targetQty > 0) {
          OrgaciaCart.executeTierSwap(itemKey, targetQty, variantTitle);
        }
      }
    });

    // Gift Wrap Card & Checkbox -> Injects Gift Wrap as a Product Line Item (Screenshot 2 & 3 Match)
    const giftWrapCard = document.getElementById('oc-giftwrap-card');
    if (giftWrapCheckbox) {
      giftWrapCheckbox.addEventListener('change', (e) => {
        if (e.target.checked) {
          if (!cartState.items.some(i => i.isGiftWrap)) {
            cartState.items.unshift({
              id: 'item-giftwrap',
              isGiftWrap: true,
              title: 'Gift Wrap',
              price: config.giftWrap.price || 49.00,
              basePrice: config.giftWrap.price || 49.00,
              compare_at_price: 199.00,
              originalPrice: 199.00,
              image: 'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=200&auto=format&fit=crop&q=80',
              quantity: 1
            });
            showToast('Added Gift Wrap to cart');
          }
        } else {
          cartState.items = cartState.items.filter(i => !i.isGiftWrap);
          showToast('Removed Gift Wrap from cart');
        }
        updateCartCalculations();
      });
    }

    if (giftWrapCard && giftWrapCheckbox) {
      giftWrapCard.addEventListener('click', (e) => {
        if (e.target !== giftWrapCheckbox && !e.target.closest('input')) {
          e.preventDefault();
          giftWrapCheckbox.checked = !giftWrapCheckbox.checked;
          giftWrapCheckbox.dispatchEvent(new Event('change'));
        }
      });
    }

    if (emptyBtn) {
      emptyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        OrgaciaCart.close();
      });
    }

    // Special Instructions Dropdown
    if (instructionsToggle && instructionsBody) {
      const toggleInstructions = (e) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        const isOpen = instructionsBody.classList.contains('oc-show') || instructionsBody.classList.contains('active');
        if (isOpen) {
          instructionsToggle.classList.remove('active');
          instructionsBody.classList.remove('oc-show', 'active', 'is-active');
        } else {
          instructionsToggle.classList.add('active');
          instructionsBody.classList.add('oc-show', 'active', 'is-active');
          if (instructionsTextarea) {
            setTimeout(() => instructionsTextarea.focus(), 50);
          }
        }
      };

      instructionsToggle.addEventListener('click', toggleInstructions);
      instructionsToggle.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          toggleInstructions(e);
        }
      });
    }

    if (instructionsTextarea) {
      const saveNote = () => {
        cartState.note = instructionsTextarea.value;
        if (isShopifyEnv) {
          syncShopifyCartNote(cartState.note);
        }
      };
      instructionsTextarea.addEventListener('input', saveNote);
      instructionsTextarea.addEventListener('change', saveNote);
      instructionsTextarea.addEventListener('blur', saveNote);
    }

    // Upsell Add to Cart
    const upsellGrid = document.getElementById('oc-upsell-grid');
    if (upsellGrid) {
      upsellGrid.addEventListener('click', (e) => {
        const btn = e.target.closest('.oc-upsell-add-btn');
        if (!btn) return;

        const id = btn.getAttribute('data-id');
        const productId = btn.getAttribute('data-product-id');
        const title = btn.getAttribute('data-title');
        const price = parseFloat(btn.getAttribute('data-price')) || 0;
        const comparePrice = parseFloat(btn.getAttribute('data-compare-price')) || 0;
        const image = btn.getAttribute('data-img');

        btn.textContent = '✓ Added';
        btn.disabled = true;

        OrgaciaCart.addItem({
          id: id,
          variantId: id,
          productId: productId || null,
          title: title,
          price: price,
          basePrice: price,
          compare_at_price: comparePrice > price ? comparePrice : null,
          image: image,
          quantity: 1
        });

        setTimeout(() => {
          btn.textContent = '+ Add';
          btn.disabled = false;
        }, 1200);
      });
    }

    // ================================================================
    // Free Gift Milestone Banner & Selection Modal Events
    // ================================================================
    const unclaimedGiftBanner = document.getElementById('oc-unclaimed-gift-banner');
    const unclaimedGiftBtn = document.getElementById('oc-unclaimed-gift-btn');
    const giftModalClose = document.getElementById('oc-gift-modal-close');
    const giftModalBackdrop = document.getElementById('oc-gift-modal-backdrop');
    const giftModalSaveBtn = document.getElementById('oc-gift-modal-save-btn');
    const giftModalProducts = document.getElementById('oc-gift-modal-products');

    if (unclaimedGiftBanner) {
      unclaimedGiftBanner.addEventListener('click', (e) => {
        e.preventDefault();
        openGiftModal();
      });
    }

    if (unclaimedGiftBtn) {
      unclaimedGiftBtn.addEventListener('click', (e) => {
        openGiftModal(e);
      });
    }

    if (giftModalClose) {
      giftModalClose.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        closeGiftModal();
      });
    }

    if (giftModalBackdrop) {
      giftModalBackdrop.addEventListener('click', (e) => {
        e.preventDefault();
        closeGiftModal();
      });
    }

    if (giftModalProducts) {
      giftModalProducts.addEventListener('click', (e) => {
        const card = e.target.closest('.oc-gift-product-card');
        if (!card) return;
        const slotKey = card.getAttribute('data-gift-slot') || card.getAttribute('data-gift-id');
        if (!slotKey) return;

        // Toggle selection on card or button click
        toggleGiftProduct(slotKey);
      });
    }

    if (giftModalSaveBtn) {
      giftModalSaveBtn.addEventListener('click', (e) => {
        e.preventDefault();
        saveGiftClaim();
      });
    }

    // Checkout CTA (Instant 0ms Fast Purchase with Discount Flow)
    checkoutBtn.addEventListener('click', () => {
      dispatchCheckoutTelemetry();

      if (config.footer.termsRequired && !tcCheckbox.checked) {
        showToast('⚠️ Please accept the Terms & Conditions before checkout.', 'warning');
        return;
      }

      showLoader();
      checkoutBtn.disabled = true;
      checkoutBtn.style.opacity = '0.9';
      checkoutBtn.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="animation: oc-spin 0.8s linear infinite; margin-right: 6px;">
          <circle cx="12" cy="12" r="10" stroke-opacity="0.25"></circle>
          <path d="M12 2a10 10 0 0 1 10 10"></path>
        </svg>
        <span>Redirecting to Checkout...</span>
      `;

      // Auto-restore button if navigation doesn't occur or user stays/returns
      setTimeout(resetCheckoutButton, 6000);

      // Tag checkout click & gift claimed in cart attributes (Zero Server Cost)
      try {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              '_bundle_source': 'product_page_bundle',
              '_drawer_used': 'true',
              '_drawer_checkout_clicked': 'true',
              '_drawer_gift_claimed': (cartState && (cartState.giftClaimed || (cartState.items && cartState.items.some(it => it.isFreeGift)))) ? 'true' : 'false',
              '_drawer_checkout_time': new Date().toISOString()
            }
          })
        }).catch(() => {});
      } catch (e) {}

      if (isShopifyEnv) {
        const codesToApply = [];

        // 1. Unlocked Milestone discount code or applied coupon
        if (cartState.appliedCoupon && cartState.appliedCoupon.code) {
          const aCode = cartState.appliedCoupon.code.trim();
          if (aCode && !codesToApply.includes(aCode)) {
            codesToApply.push(aCode);
          }
        } else if (cartState.unlockedMilestoneCode) {
          const mCode = cartState.unlockedMilestoneCode.trim();
          if (mCode && !codesToApply.includes(mCode)) {
            codesToApply.push(mCode);
          }
        }

        // NOTE: Bundle discount created in Shopify discount section applies automatically as-is.
        // We do NOT inject bundle code in URL so that native automatic bundle discount applies without conflict.

        if (codesToApply.length > 0) {
          // Direct top-level browser redirect via Shopify's native /discount/{CODE}?redirect=/checkout endpoint
          const codeString = codesToApply.map(c => encodeURIComponent(c.trim())).join(',');
          copyToClipboard(codesToApply[0]);
          window.location.href = `/discount/${codeString}?redirect=/checkout`;
          return;
        }

        window.location.href = '/checkout';
      } else {
        setTimeout(() => {
          resetCheckoutButton();
          alert(`Proceeding to Orgacia 1-Click Fast Checkout!\nTotal Payable: ${formatMoney(cartState.total_price)}`);
        }, 400);
      }
    });
  }

  // Shopify AJAX Interceptor (Supports Minimog, Dawn, Prestige, FoxKit, & Custom Themes)
  let refreshDebounceTimer = null;
  function debouncedRefreshAndOpen() {
    clearTimeout(refreshDebounceTimer);
    OrgaciaCart.open();
    refreshDebounceTimer = setTimeout(() => {
      OrgaciaCart.refresh(true);
    }, 60);
  }

  // Extract Product Info from Page Context (Supports Minimog, Dawn, Prestige, Sense, OS 2.0)
  function extractProductInfoFromContext(contextElement) {
    const form = contextElement ? (
      contextElement.form || 
      (contextElement.getAttribute('form') ? document.getElementById(contextElement.getAttribute('form')) : null) || 
      contextElement.closest('form[action*="/cart/add"], form[action*="/cart/add.js"], form[action="/cart/add"], form[data-type="add-to-cart-form"], .shopify-product-form, [data-product-form]') ||
      document.querySelector('form[action*="/cart/add"], form[action*="/cart/add.js"], .shopify-product-form')
    ) : document.querySelector('form[action*="/cart/add"], form[action*="/cart/add.js"], .shopify-product-form');

    const card = contextElement ? contextElement.closest('.product-card, .sf__pcard, .grid__item, .product-item, .card-wrapper, .sf__col-item, .product-wrap, .main-product, [data-section-type="product"]') : null;
    const productScope = form || card || (contextElement ? contextElement.closest('.product, .main-product, [data-section-type="product"], .product-single, .product-section') : null) || document;
    const root = productScope;

    // 1. Variant ID & Quantity
    let variantId = null;
    if (form) {
      const idInput = form.querySelector('[name="id"], select[name="id"], input[name="id"]');
      if (idInput && idInput.value) variantId = idInput.value;
    }
    if (!variantId && card) {
      const cardIdInput = card.querySelector('[name="id"], [data-variant-id], [data-selected-variant-id]');
      if (cardIdInput) variantId = cardIdInput.value || cardIdInput.getAttribute('data-variant-id') || cardIdInput.getAttribute('data-selected-variant-id');
    }
    if (!variantId && window.ShopifyAnalytics?.meta?.selectedVariantId) {
      variantId = String(window.ShopifyAnalytics.meta.selectedVariantId);
    }

    const qtyInput = root.querySelector('[name="quantity"]');
    const quantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

    // 2. Title
    let title = '';
    const titleSelectors = [
      '.sf__product-title',
      '.product-info__title',
      '.product__title',
      '.product-single__title',
      'h1.product-title',
      '.sf__pcard-name',
      '.product-card__title',
      '.card__heading',
      'h1'
    ];
    for (const sel of titleSelectors) {
      const el = root.querySelector(sel) || document.querySelector(sel);
      if (el && el.textContent.trim()) {
        title = el.textContent.trim();
        break;
      }
    }
    if (!title) title = document.title ? document.title.split('–')[0].split('|')[0].trim() : 'Product';

    // 3. Exact Price & Compare Price (Priority to reading authoritative product page pricing directly!)
    let price = 0;
    let comparePrice = 0;

    const pagePricing = readProductPagePricing();
    if (pagePricing && pagePricing.sellingPrice > 0) {
      price = pagePricing.sellingPrice;
      if (pagePricing.comparePrice > pagePricing.sellingPrice) {
        comparePrice = pagePricing.comparePrice;
      }
    }

    if (price === 0 && window.ShopifyAnalytics?.meta?.product) {
      const prodMeta = window.ShopifyAnalytics.meta.product;
      const curVar = (prodMeta.variants || []).find(v => String(v.id) === String(variantId)) || prodMeta.variants?.[0];
      if (curVar && curVar.price) {
        // ShopifyAnalytics prices are in the smallest currency unit (paise/cents)
        // Always divide by 100 to convert to display currency
        const rawPrice = Number(curVar.price) || 0;
        price = rawPrice > 0 ? (config.roundDecimals ? Math.round(rawPrice / 100) : (Math.round(rawPrice) / 100)) : 0;
        if (curVar.compare_at_price) {
          const rawCompare = Number(curVar.compare_at_price) || 0;
          comparePrice = rawCompare > 0 ? (config.roundDecimals ? Math.round(rawCompare / 100) : (Math.round(rawCompare) / 100)) : 0;
        }
      }
    }

    if (price === 0) {
      const saleSelectors = [
        '.sf__product-price .sf-currency-money:not(.line-through):not(s *):not(del *)',
        '.sf__product-price .price-item--sale',
        '.sf__product-price .price-sale',
        '.sf__product-price .money:not(.line-through):not(s *):not(del *)',
        '.price__sale .price-item--sale',
        '.prod__price .sf-currency-money:not(.line-through):not(s *):not(del *)',
        '.prod__price .money:not(s *):not(del *):not(.line-through)',
        '.prod__price',
        '.product-price .money:not(s *):not(del *):not(.line-through)',
        '.product-price:not(s):not(del)',
        '.price-item--sale',
        '.price-item--last',
        '.price-item--regular:not(s *):not(del *):not(.line-through)',
        '.sf__pcard-price .money',
        '.sf__pcard-price',
        '[data-product-price]',
        '.price:not(s):not(del) .money:not(s *):not(del *)',
        '.price'
      ];
      for (const sel of saleSelectors) {
        const el = (card ? card.querySelector(sel) : null) || root.querySelector(sel) || document.querySelector(sel);
        if (el && el.textContent) {
          const match = el.textContent.replace(/,/g, '').match(/\d+(?:\.\d+)?/);
          if (match) {
            const val = parseFloat(match[0]);
            if (val > 0) {
              price = val;
              break;
            }
          }
        }
      }
    }

    if (comparePrice === 0) {
      const compSelectors = [
        '.sf__product-price .sf-currency-money.line-through',
        '.sf__product-price .line-through',
        '.sf__product-price .price-item--compare',
        '.prod__price .compare-price',
        '.price-item--compare',
        '.price__sale s',
        '.price__sale del',
        '.price__sale .price-item--regular',
        's.price-item--regular',
        'del.price-item--regular',
        '.product__compare-at-price',
        '[data-compare-price]',
        'del .money',
        's .money',
        '.compare-at-price'
      ];
      for (const sel of compSelectors) {
        const el = (card ? card.querySelector(sel) : null) || root.querySelector(sel) || document.querySelector(sel);
        if (el && el.textContent) {
          const match = el.textContent.replace(/,/g, '').match(/\d+(?:\.\d+)?/);
          if (match) {
            const val = parseFloat(match[0]);
            if (val > price) {
              comparePrice = val;
              break;
            }
          }
        }
      }
    }

    // 4. Image
    let image = '';
    const imgSelectors = [
      '.sf-prod-media__image',
      '.sf-prod-media img',
      '.swiper-slide-active img',
      '.product__media-item.is-active img',
      '.product__media img',
      '.product-single__media img',
      '.product__featured-media img',
      '.media-gallery img',
      '.sf__product-slider img',
      'img.sf-image',
      '.sf-image img',
      'img[itemprop="image"]',
      '.sf__pcard-image img',
      '.product-card__image img',
      '.card__media img'
    ];
    for (const sel of imgSelectors) {
      const img = (card ? card.querySelector(sel) : null) || root.querySelector(sel) || document.querySelector(sel);
      if (img) {
        const src = img.currentSrc || img.src || img.getAttribute('data-src') || (img.srcset ? img.srcset.split(',')[0].trim().split(' ')[0] : '');
        if (src && !src.includes('data:image') && !src.includes('blank.gif') && !src.includes('placeholder')) {
          image = src;
          break;
        }
      }
    }

    // 5. Check if a bundle pack widget ("Choose Your Pack", Orgacia Bundle, WideBundle, FoxKit, etc.) is active
    let activePackCard = productScope ? productScope.querySelector('.org-pack-card.is-selected, .org-tier-card.org-selected, .bundle-tier.selected, .bundle-tier.active, .pack-option.active, .pack-card.selected, [data-bundle-tier].active, .pack-card.active, .tier-card.selected, [data-pack-option].active, .foxkit-bundle-tier.active, .wb-selected, input[name="bundle_option"]:checked, input[name="pack_qty"]:checked, input[name="bundle_tier"]:checked') : null;
    if (!activePackCard && typeof document !== 'undefined') {
      activePackCard = document.querySelector('.orgacia-pack-widget .org-pack-card.is-selected');
    }
    
    let packQuantity = quantity;
    let packCalculatedPrice = null;
    let packLabel = null;
    let isBundleSelected = false;
    let bundleProperties = {};

    if (activePackCard) {
      const packQtyAttr = activePackCard.getAttribute('data-qty') || activePackCard.getAttribute('data-quantity') || (activePackCard.dataset && activePackCard.dataset.quantity) || activePackCard.getAttribute('value');
      const packDiscountAttr = activePackCard.getAttribute('data-discount-pct') || activePackCard.getAttribute('data-discount-percent') || activePackCard.getAttribute('data-discount') || activePackCard.getAttribute('data-bundle-discount') || (activePackCard.dataset && activePackCard.dataset.discountPct);
      const tierNumAttr = activePackCard.getAttribute('data-tier') || activePackCard.getAttribute('data-tier-index') || (activePackCard.dataset && activePackCard.dataset.tierIndex);
      
      const parsedQty = packQtyAttr ? (parseInt(packQtyAttr, 10) || 1) : 1;
      let parsedDiscount = packDiscountAttr ? (parseFloat(packDiscountAttr) || 0) : 0;
      
      const packTitleEl = activePackCard.querySelector('[data-field="title"], .org-pack-title, .org-tier-name, .tier-title, .pack-title, h4, h3, .name');
      if (packTitleEl && packTitleEl.textContent) packLabel = packTitleEl.textContent.trim();

      const packPriceEl = activePackCard.querySelector('[data-field="price"], .org-pack-current-price, .org-price-current, .tier-price, .pack-price, .price');
      if (packPriceEl && packPriceEl.textContent) {
        const m = packPriceEl.textContent.replace(/,/g, '').match(/\d+(?:\.\d+)?/);
        if (m) {
          const totalPackPrice = parseFloat(m[0]);
          if (totalPackPrice > 0 && parsedQty > 0) {
            packCalculatedPrice = config.roundDecimals 
              ? Math.round(totalPackPrice / parsedQty) 
              : (Math.round((totalPackPrice / parsedQty) * 100) / 100);
            if (price > 0 && packCalculatedPrice < price && parsedDiscount === 0) {
              parsedDiscount = Math.round(((price - packCalculatedPrice) / price) * 100);
            }
          }
        }
      }

      // Tier 1 or single items with 0% discount are standard, NOT a bundle offer
      if (parsedDiscount > 0 && parsedQty > 1) {
        isBundleSelected = true;
        packQuantity = parsedQty;
        bundleProperties = {
          _bundle_selected: 'true',
          _is_bundle: 'true',
          _bundle_tier: packLabel || `Pack of ${packQuantity}`,
          _bundle_tier_qty: String(packQuantity),
          _bundle_discount: String(parsedDiscount),
          _bundle_discount_pct: String(parsedDiscount)
        };
        if (packCalculatedPrice) {
          bundleProperties._bundle_price = String(packCalculatedPrice * packQuantity);
          bundleProperties._bundle_unit_price = String(packCalculatedPrice);
        }
      } else {
        // Single unit or zero discount tier selected (Pack of 1)
        packQuantity = parsedQty || 1;
        packCalculatedPrice = null;
        packLabel = null;
        isBundleSelected = false;
        bundleProperties = {};
      }
    }

    if (!variantId) {
      variantId = 'temp_' + (title ? title.toLowerCase().replace(/[^a-z0-9]/g, '') : 'item') + '_' + Date.now();
    }

    return {
      id: variantId,
      variantId: variantId,
      title: title,
      price: price,
      calculatedPrice: isBundleSelected && packCalculatedPrice ? packCalculatedPrice : (price > 0 ? price : undefined),
      basePrice: price > 0 ? price : 0,
      compare_at_price: comparePrice > price ? comparePrice : null,
      bundleLabel: isBundleSelected ? packLabel : null,
      isBundleItem: isBundleSelected,
      discountPct: isBundleSelected && bundleProperties._bundle_discount ? parseFloat(bundleProperties._bundle_discount) : 0,
      properties: bundleProperties,
      quantity: activePackCard ? packQuantity : (packQuantity || quantity || 1),
      image: image,
      _fromDom: true
    };
  }

  let lastDrawerTelemetryTime = 0;
  function dispatchDrawerOpenTelemetry() {
    const now = Date.now();
    // Anti-spam debounce: discard rapid duplicate clicks within 1 second
    if (now - lastDrawerTelemetryTime < 1000) return;
    lastDrawerTelemetryTime = now;

    let sid = '';
    try {
      sid = sessionStorage.getItem('orgacia_session_id') || '';
      if (!sid) {
        sid = 'sid_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8);
        sessionStorage.setItem('orgacia_session_id', sid);
      }
    } catch(e) {
      sid = 'sid_' + Date.now();
    }

    let isUniqueSessionOpen = false;
    try {
      if (!sessionStorage.getItem('orgacia_session_drawer_counted')) {
        sessionStorage.setItem('orgacia_session_drawer_counted', 'true');
        isUniqueSessionOpen = true;
      }
      sessionStorage.setItem('orgacia_last_drawer_open', String(now));
    } catch(e) {
      isUniqueSessionOpen = true;
    }

    // Only count unique browser sessions!
    if (isUniqueSessionOpen) {
      try {
        const opensKey = 'orgacia_bundle_drawer_opens_total';
        const curr = parseInt(localStorage.getItem(opensKey) || '0', 10);
        localStorage.setItem(opensKey, String(curr + 1));
        localStorage.setItem('orgacia_telemetry_last_ping', JSON.stringify({
          event: 'drawer_open',
          sessionId: sid,
          timestamp: now
        }));
        let evts = JSON.parse(localStorage.getItem('orgacia_telemetry_events') || '[]');
        if (!Array.isArray(evts)) evts = [];
        if (!evts.some(e => e.type === 'drawer_open' && e.sid === sid)) {
          evts.push({ type: 'drawer_open', sid: sid, ts: now });
          if (evts.length > 500) evts.shift();
          localStorage.setItem('orgacia_telemetry_events', JSON.stringify(evts));
        }
      } catch(e) {}

      // Instant cross-tab broadcast for same-browser testing
      try {
        if (typeof BroadcastChannel !== 'undefined') {
          const bc = new BroadcastChannel('orgacia_telemetry');
          bc.postMessage({ event: 'drawer_open', sessionId: sid, isUnique: true, timestamp: now });
          bc.close();
        }
      } catch(e) {}

      // Real-time serverless telemetry beacon to Cloudflare Edge
      try {
        const shop = window.Shopify?.shop || 'demo1243.myshopify.com';
        const isMobile = (window.innerWidth <= 768 || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) ? 'Mobile' : 'Desktop';
        const payload = JSON.stringify({
          event: 'drawer_open',
          shop: shop,
          sessionId: sid,
          device: isMobile,
          timestamp: now
        });
        const endpoint = 'https://orgacia-shopify-store.pages.dev/api/track';

        const sendBeaconAsync = () => {
          try {
            if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
              navigator.sendBeacon(endpoint, payload);
            } else if (typeof fetch !== 'undefined') {
              fetch(endpoint, {
                method: 'POST',
                mode: 'cors',
                headers: { 'Content-Type': 'application/json' },
                body: payload,
                keepalive: true
              }).catch(() => {});
            }
          } catch(e) {}
        };

        if (typeof requestIdleCallback === 'function') {
          requestIdleCallback(sendBeaconAsync, { timeout: 800 });
        } else {
          setTimeout(sendBeaconAsync, 60);
        }
      } catch(e) {}
    }

    try {
      window.dispatchEvent(new CustomEvent('orgacia:drawer_opened', {
        detail: { time: now, isUnique: isUniqueSessionOpen }
      }));
    } catch(e) {}
  }

  let lastCheckoutTelemetryTime = 0;
  function dispatchCheckoutTelemetry() {
    const now = Date.now();
    // Anti-spam debounce: discard rapid duplicate clicks within 1 second
    if (now - lastCheckoutTelemetryTime < 1000) return;
    lastCheckoutTelemetryTime = now;

    let sid = '';
    try {
      sid = sessionStorage.getItem('orgacia_session_id') || '';
      if (!sid) {
        sid = 'sid_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8);
        sessionStorage.setItem('orgacia_session_id', sid);
      }
    } catch(e) {
      sid = 'sid_' + Date.now();
    }

    let isUniqueSessionCheckout = false;
    try {
      if (!sessionStorage.getItem('orgacia_session_checkout_counted')) {
        sessionStorage.setItem('orgacia_session_checkout_counted', 'true');
        isUniqueSessionCheckout = true;
      }
      sessionStorage.setItem('orgacia_last_checkout_click', String(now));
    } catch(e) {
      isUniqueSessionCheckout = true;
    }

    // Only count unique checkout per browser session!
    if (isUniqueSessionCheckout) {
      try {
        const checkoutsKey = 'orgacia_bundle_checkouts_total';
        const curr = parseInt(localStorage.getItem(checkoutsKey) || '0', 10);
        localStorage.setItem(checkoutsKey, String(curr + 1));
        localStorage.setItem('orgacia_telemetry_last_checkout', JSON.stringify({
          event: 'checkout_click',
          sessionId: sid,
          timestamp: now
        }));
        let evts = JSON.parse(localStorage.getItem('orgacia_telemetry_events') || '[]');
        if (!Array.isArray(evts)) evts = [];
        if (!evts.some(e => e.type === 'checkout_click' && e.sid === sid)) {
          evts.push({ type: 'checkout_click', sid: sid, ts: now });
          if (evts.length > 500) evts.shift();
          localStorage.setItem('orgacia_telemetry_events', JSON.stringify(evts));
        }
      } catch(e) {}

      // Instant cross-tab broadcast for same-browser testing
      try {
        if (typeof BroadcastChannel !== 'undefined') {
          const bc = new BroadcastChannel('orgacia_telemetry');
          bc.postMessage({ event: 'checkout_click', sessionId: sid, isUnique: true, timestamp: now });
          bc.close();
        }
      } catch(e) {}

      // Real-time serverless telemetry beacon to Cloudflare Edge
      try {
        const shop = window.Shopify?.shop || 'demo1243.myshopify.com';
        const isMobile = (window.innerWidth <= 768 || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) ? 'Mobile' : 'Desktop';
        const payload = JSON.stringify({
          event: 'checkout_click',
          shop: shop,
          sessionId: sid,
          device: isMobile,
          timestamp: now
        });
        const endpoint = 'https://orgacia-shopify-store.pages.dev/api/track';

        if (typeof fetch !== 'undefined') {
          fetch(endpoint, {
            method: 'POST',
            mode: 'cors',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            keepalive: true
          }).catch(() => {
            if (navigator.sendBeacon) navigator.sendBeacon(endpoint, payload);
          });
        } else if (navigator.sendBeacon) {
          navigator.sendBeacon(endpoint, payload);
        }
      } catch(e) {
        try {
          if (navigator.sendBeacon) {
            navigator.sendBeacon('https://orgacia-shopify-store.pages.dev/api/track', payload);
          }
        } catch(err) {}
      }
    }

    try {
      window.dispatchEvent(new CustomEvent('orgacia:checkout_clicked', {
        detail: { time: now, isUnique: isUniqueSessionCheckout }
      }));
    } catch(e) {}
  }

  function dispatchDrawerEngagementTelemetry(actionType) {
    const now = Date.now();
    let sid = '';
    try {
      sid = sessionStorage.getItem('orgacia_session_id') || ('sid_' + now);
    } catch(e) {
      sid = 'sid_' + now;
    }

    let isUniqueEngage = false;
    try {
      if (!sessionStorage.getItem('orgacia_session_drawer_engaged')) {
        sessionStorage.setItem('orgacia_session_drawer_engaged', 'true');
        isUniqueEngage = true;
      }
    } catch(e) {
      isUniqueEngage = true;
    }

    if (isUniqueEngage) {
      try {
        let evts = JSON.parse(localStorage.getItem('orgacia_telemetry_events') || '[]');
        if (!Array.isArray(evts)) evts = [];
        if (!evts.some(e => (e.type === 'drawer_engagement' || e.type === 'bundle_card_click') && e.sid === sid)) {
          evts.push({ type: 'drawer_engagement', sid: sid, ts: now, action: actionType || 'drawer_interact' });
          if (evts.length > 500) evts.shift();
          localStorage.setItem('orgacia_telemetry_events', JSON.stringify(evts));
        }
      } catch(e) {}

      try {
        if (typeof BroadcastChannel !== 'undefined') {
          const bc = new BroadcastChannel('orgacia_telemetry');
          bc.postMessage({ event: 'drawer_engagement', sessionId: sid, isUnique: true, timestamp: now, action: actionType });
          bc.close();
        }
      } catch(e) {}

      try {
        const shop = window.Shopify?.shop || 'demo1243.myshopify.com';
        const isMobile = (window.innerWidth <= 768 || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) ? 'Mobile' : 'Desktop';
        const payload = JSON.stringify({
          event: 'drawer_engagement',
          shop: shop,
          sessionId: sid,
          device: isMobile,
          timestamp: now
        });
        const endpoint = 'https://orgacia-shopify-store.pages.dev/api/track';
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
          navigator.sendBeacon(endpoint, payload);
        } else if (typeof fetch !== 'undefined') {
          fetch(endpoint, {
            method: 'POST',
            mode: 'cors',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            keepalive: true
          }).catch(() => {});
        }
      } catch(e) {}
    }
  }

  function setupDrawerObserver() {
    const drawer = document.getElementById('oc-drawer');
    if (drawer && typeof MutationObserver !== 'undefined' && !drawer._orgacia_observed) {
      drawer._orgacia_observed = true;
      const obs = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes' && m.attributeName === 'class') {
            if (drawer.classList.contains('oc-active')) {
              dispatchDrawerOpenTelemetry();
            }
          }
        }
      });
      obs.observe(drawer, { attributes: true });

      if (!drawer._orgacia_click_bound) {
        drawer._orgacia_click_bound = true;
        drawer.addEventListener('click', (ev) => {
          if (ev.target.closest('#oc-close, .oc-header__close, #oc-drawer-overlay')) return;
          dispatchDrawerEngagementTelemetry('drawer_interaction');
        }, { passive: true });
      }
    }
  }

  async function handleThirdPartyCheckoutCapture(triggerEl) {
    dispatchCheckoutTelemetry();

    // 1. Read current cart state & sync note_attributes so Shiprocket / Fastrr passes them to Shopify order
    try {
      const isMobile = (window.innerWidth <= 768 || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) ? 'Mobile' : 'Desktop';
      const activeCard = document.querySelector('.org-pack-card.is-selected');
      const tierTitle = activeCard?.querySelector('[data-field="title"]')?.textContent?.trim() || 'Pack of 1 (Standard Unit)';
      const tierQty = activeCard?.getAttribute('data-quantity') || '1';

      let mkt = {};
      try {
        const saved = sessionStorage.getItem('orgacia_attribution');
        if (saved) mkt = JSON.parse(saved);
        else {
          const params = new URLSearchParams(window.location.search);
          const utmSource = params.get('utm_source');
          const fbclid = params.get('fbclid');
          const gclid = params.get('gclid');
          const ref = document.referrer ? document.referrer.toLowerCase() : '';
          let dSrc = utmSource;
          if (!dSrc) {
            if (fbclid || ref.includes('facebook') || ref.includes('instagram')) dSrc = 'meta_ads';
            else if (gclid || ref.includes('google')) dSrc = 'google_ads';
            else if (ref.includes('whatsapp')) dSrc = 'whatsapp';
            else if (ref) dSrc = 'referral';
            else dSrc = 'direct';
          }
          mkt = {
            utm_source: dSrc,
            utm_medium: params.get('utm_medium') || (fbclid ? 'cpc' : (gclid ? 'pmax' : 'direct')),
            utm_campaign: params.get('utm_campaign') || 'storefront_organic',
            _referrer: ref ? ref.slice(0, 100) : 'direct'
          };
          sessionStorage.setItem('orgacia_attribution', JSON.stringify(mkt));
        }
      } catch(e) {}

      const attrs = {
        _bundle_source: 'product_page_bundle',
        _bundle_tier: tierTitle,
        _bundle_tier_qty: tierQty,
        _checkout_provider: 'Shiprocket Fastrr',
        _device_type: isMobile,
        utm_source: mkt.utm_source || 'direct',
        utm_medium: mkt.utm_medium || 'direct',
        utm_campaign: mkt.utm_campaign || 'storefront_organic',
        _referrer: mkt._referrer || 'direct',
        _captured_at: new Date().toISOString()
      };

      // Post attributes to Shopify cart so when the customer actually completes checkout,
      // Shopify/Fastrr natively attaches these bundle & attribution attributes to the order.
      if (typeof fetch !== 'undefined') {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        }).catch(() => {});
      }
    } catch(err) {
      console.warn('[Orgacia] Checkout attributes sync:', err);
    }
  }

  function setupShopifyCartInterceptor() {
    setupDrawerObserver();

    // Helper to send order payload to backend
    const sendOrderTelemetry = (orderData) => {
      try {
        if (!orderData || !orderData.id) return;
        const seenKey = 'orgacia_order_captured_' + orderData.id;
        if (sessionStorage.getItem(seenKey)) return;
        sessionStorage.setItem(seenKey, 'true');

        const isMobile = (window.innerWidth <= 768 || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) ? 'Mobile' : 'Desktop';
        const activeCard = document.querySelector('.org-pack-card.is-selected');
        const currentTier = activeCard?.querySelector('[data-field="title"]')?.textContent?.trim() || 'Pack of 1 (Standard Unit)';

        const payload = {
          action: 'capture_order',
          event: 'order_captured',
          shop: window.Shopify?.shop || 'orgacia.myshopify.com',
          order: {
            id: String(orderData.id),
            name: orderData.name ? (String(orderData.name).startsWith('#') ? String(orderData.name) : ('#' + orderData.name)) : ('#' + (orderData.order_number || orderData.id)),
            order_number: String(orderData.order_number || orderData.name || orderData.id).replace('#', ''),
            created_at: orderData.created_at || new Date().toISOString(),
            financial_status: orderData.financial_status || 'paid',
            gateway: orderData.gateway || 'PayU',
            payment_gateway_names: Array.isArray(orderData.payment_gateway_names) ? orderData.payment_gateway_names : [orderData.gateway || 'PayU', 'Fastrr'],
            total_price: String(orderData.total_price || orderData.amount || '3.00'),
            total_discounts: '0.00',
            currency: orderData.currency || 'INR',
            tags: orderData.tags || (orderData.financial_status === 'pending' ? 'COD, Fastrr' : 'Paid, Fastrr'),
            source_name: orderData.source_name || 'orgacia>>fastrr',
            customer: orderData.customer || {
              first_name: orderData.customer_name || 'Store',
              last_name: 'Customer'
            },
            line_items: orderData.line_items || [],
            note_attributes: [
              { name: '_bundle_source', value: 'product_page_bundle' },
              { name: '_bundle_tier', value: orderData.tier || currentTier },
              { name: '_checkout_provider', value: orderData.source_name?.includes('web') ? 'Shopify Native Checkout' : 'Shiprocket Fastrr' },
              { name: '_device_type', value: isMobile }
            ]
          }
        };

        fetch('https://orgacia-shopify-store.pages.dev/api/track', {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true
        }).catch(() => {});

        fetch('/api/track', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).catch(() => {});
      } catch (err) {
        console.warn('[Orgacia] Order capture error:', err);
      }
    };

    const extractOrderFromEvent = (raw) => {
      if (!raw) return null;
      const d = (typeof raw === 'string') ? (() => { try { return JSON.parse(raw); } catch(e) { return null; } })() : raw;
      if (!d) return null;
      const info = d.data || d.payload || d.order || d.detail || d;
      const evType = String(d.type || d.event || info.type || info.event || '').toLowerCase();
      
      const isSuccess = evType.includes('fastrr') || evType.includes('order_placed') || evType.includes('order_success') ||
                        evType.includes('checkout:success') || evType.includes('shiprocket') ||
                        info.fastrr_order_id || info.order_id || info.order_number;
      if (!isSuccess) return null;

      const orderId = info.order_id || info.fastrr_order_id || info.orderId || info.id || ('sr_' + Date.now());
      const orderNum = info.order_number || info.name || info.orderNumber || orderId;
      const modeStr = String(info.payment_mode || info.payment_method || info.payment_type || info.gateway || (info.is_cod ? 'COD' : '')).toUpperCase();
      const isCod = modeStr.includes('COD') || modeStr.includes('CASH');

      return {
        id: String(orderId),
        name: String(orderNum).startsWith('#') ? String(orderNum) : ('#' + orderNum),
        order_number: String(orderNum).replace('#', ''),
        financial_status: isCod ? 'pending' : 'paid',
        gateway: isCod ? 'Cash on Delivery (COD)' : (info.payment_mode || 'PayU / Fastrr (Prepaid)'),
        payment_gateway_names: [isCod ? 'Cash on Delivery (COD)' : (info.payment_mode || 'PayU'), 'Fastrr'],
        total_price: String(info.total || info.amount || info.total_price || '3.00'),
        currency: 'INR',
        tags: isCod ? 'COD, Fastrr, Shiprocket' : 'Paid, Fastrr, Shiprocket',
        source_name: 'orgacia>>fastrr',
        customer_name: info.customer_name || (info.customer ? `${info.customer.first_name || ''} ${info.customer.last_name || ''}`.trim() : '')
      };
    };

    // Setup window message listener for Fastrr / Shiprocket completed order callbacks
    window.addEventListener('message', (evt) => {
      try {
        const orderData = extractOrderFromEvent(evt.data);
        if (orderData) sendOrderTelemetry(orderData);
      } catch(e) {}
    });

    // CustomEvent listeners for Fastrr / Shiprocket
    const onFastrrEvent = (e) => {
      try {
        const orderData = extractOrderFromEvent(e.detail || e);
        if (orderData) sendOrderTelemetry(orderData);
      } catch(e) {}
    };
    window.addEventListener('FastrrOrderPlaced', onFastrrEvent);
    window.addEventListener('fastrr:order_placed', onFastrrEvent);
    window.addEventListener('fastrr_order_success', onFastrrEvent);
    window.addEventListener('shiprocket:order_placed', onFastrrEvent);
    document.addEventListener('fastrr:order_placed', onFastrrEvent);
    document.addEventListener('FastrrOrderPlaced', onFastrrEvent);

    // Shopify Native Checkout (Checkout Extensibility & Web Pixels)
    if (typeof window !== 'undefined' && window.Shopify && typeof window.Shopify.analytics === 'object' && typeof window.Shopify.analytics.subscribe === 'function') {
      window.Shopify.analytics.subscribe('checkout_completed', (event) => {
        try {
          const chk = event?.data?.checkout || {};
          const order = chk.order || {};
          const orderId = String(order.id || chk.order_id || chk.id || ('sh_native_' + Date.now()));
          const orderNum = String(order.name || chk.order_name || order.order_number || orderId);
          const gwNames = chk.payment_gateway_names || [];
          const isCod = gwNames.some(g => String(g).toLowerCase().includes('cod') || String(g).toLowerCase().includes('cash'));
          
          sendOrderTelemetry({
            id: orderId,
            name: orderNum,
            order_number: orderNum.replace('#', ''),
            financial_status: isCod ? 'pending' : 'paid',
            gateway: isCod ? 'Cash on Delivery (COD)' : (gwNames[0] || 'Shopify Payments (Prepaid)'),
            payment_gateway_names: gwNames.length > 0 ? gwNames : [isCod ? 'Cash on Delivery (COD)' : 'Prepaid'],
            total_price: String(chk.totalPrice?.amount || chk.total_price || '3.00'),
            tags: isCod ? 'COD, Shopify Native' : 'Paid, Shopify Native',
            source_name: 'web'
          });
        } catch(e) {}
      });
    }

    // 1. Click Listener for all Cart triggers, Add to Cart buttons, and 3rd-Party Checkout Buttons
    document.addEventListener('click', (e) => {
      // 1.1. Intercept Third-Party (Shiprocket, Fastrr, GoKwik, etc.) or Native Checkout CTA clicks
      const checkoutTrigger = e.target.closest('#oc-checkout-btn, .oc-checkout-btn, button[name="checkout"], a[href*="/checkout"], [data-checkout], .shiprocket-headless, [data-fastrr-checkout], [data-sr-checkout], .sr-checkout-btn, #headless-button, .fastrr-btn, [data-fastrr-btn], [data-fastrr-trigger], .sr-btn, [data-shiprocket-checkout], .shiprocket-btn, [onclick*="fastrr"], [onclick*="shiprocket"], [data-action="checkout"], input[name="checkout"]');
      if (checkoutTrigger) {
        handleThirdPartyCheckoutCapture(checkoutTrigger);
      }

      const cartTrigger = e.target.closest('a[href="/cart"], a[href*="/cart"], .header__icon--cart, .cart-icon-bubble, [data-cart-drawer-trigger], .js-drawer-open-right, [data-ajax-cart-toggle], .header-cart-btn, #header-cart-btn, .cart-button, [data-cart-trigger], .site-header__cart, .cart-btn, .cart-link, .sf-cart-icon, .sf__cart-icon');
      if (cartTrigger) {
        e.preventDefault();
        e.stopImmediatePropagation();
        dispatchDrawerOpenTelemetry();
        OrgaciaCart.open();
        OrgaciaCart.refresh(Boolean(cartState.items && cartState.items.length > 0));
        return;
      }

      // Instant Drawer Open and Optimistic Ingestion on any Add to Cart button click (0ms response)
      const atcBtn = e.target.closest('button[name="add"], .product-form__submit, [data-add-to-cart], .add-to-cart, .sf__btn-primary[name="add"], [data-atc-btn], .product-form__cart-submit, [data-action="add-to-cart"], .atc-btn, #bundle-atc-btn, [data-atc], .single_add_to_cart_button, .product-form__buttons button');
      if (atcBtn) {
        // If the button belongs to an active bundle widget or has its own dedicated bundle handler, let that handler perform ingestion with the exact pack tier & properties!
        const isBundleWidgetBtn = atcBtn.closest('.orgacia-pack-widget, .org-pack-widget, #OrgaciaPack-demo, [data-bundle-widget]') || 
                                  atcBtn.id === 'bundle-atc-btn' || 
                                  atcBtn.getAttribute('data-action') === 'bundle-atc' ||
                                  atcBtn.hasAttribute('data-orgacia-bundle-hooked') ||
                                  (document.querySelector('.orgacia-pack-widget') && (atcBtn.form?.querySelector('.orgacia-pack-widget') || atcBtn.closest('form')?.parentElement?.querySelector('.orgacia-pack-widget') || document.querySelector('.orgacia-pack-widget .org-pack-card.is-selected')));
        if (isBundleWidgetBtn) {
          OrgaciaCart.open();
          return;
        }

        try {
          const optItem = extractProductInfoFromContext(atcBtn);
          if (optItem) {
            OrgaciaCart.fastIngestAddedItem(optItem);
          }
        } catch(e) {
          console.warn('[Orgacia] Fast ingest note:', e);
        }
        OrgaciaCart.open();
        hideLoader(true);
      }
    }, true);

    // 2. Intercept Standard Form Submissions (Product forms, Pack forms, Bundle forms)
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action*="/cart/add"], form[action*="/cart/add.js"]');
      if (!form) return;

      // Always open drawer instantly (0ms)
      OrgaciaCart.open();

      // If the theme handles form submissions asynchronously via AJAX (Minimog, Dawn, etc.),
      // let fetch/XHR interceptor handle the response to avoid duplicate double-POSTs!
      const hasThemeAjax = form.getAttribute('data-type') === 'add-to-cart-form' || 
                           form.classList.contains('shopify-product-form') || 
                           form.querySelector('[name="add"]');
      if (hasThemeAjax) {
        return;
      }

      if (!form.getAttribute('data-orgacia-intercepted')) {
        e.preventDefault();
        form.setAttribute('data-orgacia-intercepted', 'true');
        showLoader();
        const formData = new FormData(form);
        const fetchFn = rawFetch || window.fetch;
        fetchFn('/cart/add.js', {
          method: 'POST',
          body: formData
        })
          .then(async res => {
            const addedData = await res.json();
            if (!res.ok || addedData.status === 422 || (addedData.message && addedData.message.toLowerCase().includes('error'))) {
              const errorMsg = addedData.description || addedData.message || 'Item is currently out of stock or maximum limit reached.';
              hideLoader(true);
              showToast(`⚠️ ${errorMsg}`, 'warning');
              OrgaciaCart.refresh(true);
              return;
            }
            if (addedData) OrgaciaCart.fastIngestAddedItem(addedData);
            OrgaciaCart.refresh(true);
          })
          .catch(err => {
            console.error('Orgacia add to cart error', err);
            hideLoader(true);
            showToast('⚠️ Item is out of stock or limit reached.', 'warning');
            OrgaciaCart.refresh(true);
          })
          .finally(() => {
            hideLoader(true);
            setTimeout(() => form.removeAttribute('data-orgacia-intercepted'), 600);
          });
      }
    }, false);

    // 3. Intercept Fetch API requests (Minimog, FoxKit, OS 2.0 AJAX)
    if (typeof window.fetch === 'function') {
      const originalFetch = window.fetch;
      window.fetch = function (...args) {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url ? args[0].url : '');
        const isCartAction = url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/clear') || url.includes('/cart/update');
        const isBackgroundAttributeUpdate = url.includes('/cart/update') && args[1] && typeof args[1].body === 'string' && args[1].body.includes('attributes') && !args[1].body.includes('updates');

        if (isCartAction && !isBackgroundAttributeUpdate) {
          showLoader();
          if (url.includes('/cart/add')) {
            OrgaciaCart.open();
          }
        }

        const responsePromise = originalFetch.apply(this, args);

        if (isCartAction) {
          responsePromise.then(response => {
            if (response.ok) {
              try {
                response.clone().json().then(data => {
                  if (url.includes('/cart/add')) {
                    if (data && (data.id || data.items)) {
                      OrgaciaCart.fastIngestAddedItem(data);
                    }
                    OrgaciaCart.refresh(true);
                  } else if (isBackgroundAttributeUpdate) {
                    // Silent background cart attributes tagging: do not flash loader or refetch /cart.js
                  } else {
                    // For /cart/change, /cart/clear, /cart/update:
                    if (data && Array.isArray(data.items)) {
                      OrgaciaCart.syncWithCart(data);
                    } else {
                      OrgaciaCart.refresh(true);
                    }
                  }
                }).catch(() => {
                  if (!isBackgroundAttributeUpdate) OrgaciaCart.refresh(true);
                });
              } catch(e) {
                if (!isBackgroundAttributeUpdate) OrgaciaCart.refresh(true);
              }
            } else if (response.status === 422) {
              hideLoader(true);
              try {
                response.clone().json().then(errData => {
                  if (errData && (errData.description || errData.message)) {
                    const rawMsg = String(errData.description || errData.message);
                    if (!/expected Integer|line parameter|\(?-mix/i.test(rawMsg)) {
                      showToast(`⚠️ ${rawMsg}`, 'warning');
                    }
                    OrgaciaCart.refresh(true);
                  }
                }).catch(() => {});
              } catch(e) {}
            }
          }).finally(() => {
            hideLoader(true);
          });
        }

        return responsePromise;
      };
    }

    // 4. Intercept XMLHttpRequest (XHR) for jQuery / Legacy / ThemeForest themes
    const originalXhrOpen = XMLHttpRequest.prototype.open;
    const originalXhrSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this._orgaciaUrl = url;
      return originalXhrOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      const isCartAction = this._orgaciaUrl && (this._orgaciaUrl.includes('/cart/add') || this._orgaciaUrl.includes('/cart/change') || this._orgaciaUrl.includes('/cart/update') || this._orgaciaUrl.includes('/cart/clear'));
      if (isCartAction) {
        showLoader();
        if (this._orgaciaUrl.includes('/cart/add')) {
          OrgaciaCart.open();
        }
        const onFinish = () => {
          if (this.status >= 200 && this.status < 300) {
            try {
              const data = JSON.parse(this.responseText);
              const isAdd = this._orgaciaUrl && this._orgaciaUrl.includes('/cart/add');
              if (isAdd) {
                if (data && (data.id || data.items)) {
                  OrgaciaCart.fastIngestAddedItem(data);
                }
                OrgaciaCart.refresh(true);
              } else {
                if (data && Array.isArray(data.items)) {
                  OrgaciaCart.syncWithCart(data);
                } else {
                  OrgaciaCart.refresh(true);
                }
              }
            } catch(e) {
              OrgaciaCart.refresh(true);
            }
          } else if (this.status === 422) {
            hideLoader(true);
            try {
              const errData = JSON.parse(this.responseText);
              if (errData && (errData.description || errData.message)) {
                const rawMsg = String(errData.description || errData.message);
                if (!/expected Integer|line parameter|\(?-mix/i.test(rawMsg)) {
                  showToast(`⚠️ ${rawMsg}`, 'warning');
                }
                OrgaciaCart.refresh(true);
              }
            } catch(e) {}
          }
          hideLoader(true);
        };
        this.addEventListener('load', onFinish, { once: true });
        this.addEventListener('error', () => hideLoader(true), { once: true });
        this.addEventListener('abort', () => hideLoader(true), { once: true });
      }
      return originalXhrSend.apply(this, arguments);
    };

    // 5. Universal Shopify & Minimog Theme Custom Events Listener
    const themeEvents = [
      'cart:refresh',
      'cart:updated',
      'cart:item-added',
      'theme:cart:add',
      'theme:cart:change',
      'Minimog:cart:add',
      'Minimog:cart:change',
      'sf:cart-updated',
      'foxkit:bundle:added',
      'foxkit:cart:updated'
    ];

    themeEvents.forEach(evt => {
      window.addEventListener(evt, () => {
        debouncedRefreshAndOpen();
      });
      document.addEventListener(evt, () => {
        debouncedRefreshAndOpen();
      });
    });
  }

  // Sync Cart Notes
  let noteDebounceTimer = null;
  function syncShopifyCartNote(note) {
    clearTimeout(noteDebounceTimer);
    noteDebounceTimer = setTimeout(() => {
      const fetchFn = rawFetch || window.fetch;
      fetchFn('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note })
      }).catch(err => console.warn('Could not sync cart note', err));
    }, 400);
  }

  // Confetti Particle Explosion Engine (Pure Vanilla JS, 60fps)
  let confettiAnimFrame = null;
  function triggerConfetti() {
    if (!config.celebration || !config.celebration.enabled) return;
    const canvas = document.getElementById('oc-confetti-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth || 420;
    canvas.height = canvas.offsetHeight || 650;

    const particles = [];
    const colors = ['#C6E023', '#00A896', '#E2B04A', '#10B981', '#FF6B6B', '#3B82F6', '#8B5CF6'];

    for (let i = 0; i < 70; i++) {
      particles.push({
        x: canvas.width / 2 + (Math.random() - 0.5) * 60,
        y: canvas.height * 0.45,
        vx: (Math.random() - 0.5) * 14,
        vy: (Math.random() - 0.8) * 16,
        size: Math.random() * 8 + 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * 360,
        rotSpeed: (Math.random() - 0.5) * 12,
        gravity: 0.35,
        alpha: 1
      });
    }

    const startTime = Date.now();
    const duration = config.celebration.duration || 2500;

    if (confettiAnimFrame) cancelAnimationFrame(confettiAnimFrame);

    function render() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const elapsed = Date.now() - startTime;
      let activeCount = 0;

      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += p.gravity;
        p.rotation += p.rotSpeed;
        p.alpha = Math.max(0, 1 - (elapsed / duration));

        if (p.alpha > 0 && p.y < canvas.height + 20) {
          activeCount++;
          ctx.save();
          ctx.globalAlpha = p.alpha;
          ctx.translate(p.x, p.y);
          ctx.rotate((p.rotation * Math.PI) / 180);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
          ctx.restore();
        }
      });

      if (activeCount > 0 && elapsed < duration) {
        confettiAnimFrame = requestAnimationFrame(render);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }

    render();
  }

  // Validate and Apply Coupon with Ineligible Criteria Checks
  function validateAndApplyCoupon(code) {
    const errorEl1 = document.getElementById('oc-coupon-error');
    const errorEl2 = document.getElementById('oc-subpage-coupon-error');
    const errorEl3 = document.getElementById('oc-vip-coupon-error');
    const errorEl4 = document.getElementById('oc-empty-vip-error');

    function showError(msg) {
      [errorEl1, errorEl2, errorEl3, errorEl4].forEach(el => {
        if (el) {
          el.textContent = msg;
          el.classList.add('oc-show');
        }
      });
      setTimeout(() => {
        [errorEl1, errorEl2, errorEl3, errorEl4].forEach(el => {
          if (el) el.classList.remove('oc-show');
        });
      }, 5000);
    }

    function clearError() {
      [errorEl1, errorEl2, errorEl3, errorEl4].forEach(el => {
        if (el) el.classList.remove('oc-show');
      });
    }

    const codeUpper = (code || '').trim().toUpperCase();
    let coupon = (config.coupons || []).find(c => c && c.code && c.code.toUpperCase() === codeUpper);

    // If not in static config.coupons, check if it matches an unlocked milestone discount code
    if (!coupon && cartState.unlockedMilestoneCode && cartState.unlockedMilestoneCode.toUpperCase() === codeUpper) {
      coupon = {
        code: cartState.unlockedMilestoneCode,
        title: `${cartState.unlockedMilestonePct}% OFF Milestone Reward`,
        type: 'percentage',
        value: cartState.unlockedMilestonePct,
        minItems: 1,
        minAmount: (cartState.unlockedMilestoneTier && cartState.unlockedMilestoneTier.threshold) || 0,
        bullets: ['Unlocked from milestone achievement!']
      };
    }

    if (!coupon) {
      showError(`⚠️ Coupon "${code}" is invalid or expired.`);
      return false;
    }

    let totalItems = 0;
    let subtotal = 0;
    cartState.items.forEach(i => {
      if (!i.isGift) {
        totalItems += i.quantity;
        subtotal += ((i.calculatedPrice !== undefined ? i.calculatedPrice : i.price) * i.quantity);
      }
    });

    if (coupon.minItems && totalItems < coupon.minItems) {
      showError(`⚠️ "${coupon.code}" requires minimum ${coupon.minItems} items in cart (You currently have ${totalItems}).`);
      return false;
    }

    if (coupon.minAmount && subtotal < coupon.minAmount) {
      showError(`⚠️ "${coupon.code}" requires minimum order value of ${formatMoney(coupon.minAmount)} (Current total is ${formatMoney(subtotal)}).`);
      return false;
    }

    clearError();
    cartState.appliedCoupon = coupon;
    updateCartCalculations();
    copyToClipboard(coupon.code);
    showToast(`🏷️ Code "${coupon.code}" copied! Enter it in the discount box at checkout.`);
    triggerConfetti();
    return true;
  }

  // Helper to extract a strictly valid Shopify Line Key or numeric variant ID
  function getValidShopifyLineId(item) {
    if (!item) return null;
    const k = item.key ? String(item.key).trim() : '';
    // 1. Any Shopify line item key contains a colon ':' separating variant ID and property hash (e.g. "456789123:abc...")
    if (k && k.includes(':')) {
      return k;
    }
    // 2. Purely numeric variantId
    const vId = item.variantId || item.variant_id;
    if (vId && /^\d+$/.test(String(vId).trim())) {
      return Number(String(vId).trim());
    }
    // 3. Purely numeric id
    if (item.id && /^\d+$/.test(String(item.id).trim())) {
      return Number(String(item.id).trim());
    }
    // 4. Purely numeric key
    if (k && /^\d+$/.test(k)) {
      return Number(k);
    }
    return null;
  }

  // Tombstone mechanism to prevent stale asynchronous /cart.js server responses from resurrecting recently removed items
  const recentlyRemovedItems = new Map();
  function tombstoneItem(item) {
    if (!item) return;
    const now = Date.now();
    if (item.key) recentlyRemovedItems.set(String(item.key), now);
    if (item.id) recentlyRemovedItems.set(String(item.id), now);
    if (item.variantId) recentlyRemovedItems.set(String(item.variantId), now);
    if (item.variant_id) recentlyRemovedItems.set(String(item.variant_id), now);
    if (item.productId) recentlyRemovedItems.set(`prod_${item.productId}`, now);
    if (item.product_id) recentlyRemovedItems.set(`prod_${item.product_id}`, now);
    if (Array.isArray(item.allKeys)) {
      item.allKeys.forEach(k => recentlyRemovedItems.set(String(k), now));
    }
  }

  function isItemTombstoned(rawItem) {
    if (!rawItem) return false;
    const now = Date.now();
    const expiryMs = 8000; // 8 seconds protection window against asynchronous replication lag
    const keysToCheck = [
      rawItem.key ? String(rawItem.key) : null,
      rawItem.id ? String(rawItem.id) : null,
      rawItem.variant_id ? String(rawItem.variant_id) : null,
      rawItem.variantId ? String(rawItem.variantId) : null,
      rawItem.product_id ? `prod_${rawItem.product_id}` : null,
      rawItem.productId ? `prod_${rawItem.productId}` : null
    ].filter(Boolean);

    for (const k of keysToCheck) {
      const ts = recentlyRemovedItems.get(k);
      if (ts && (now - ts < expiryMs)) {
        return true;
      }
    }
    return false;
  }

  function untombstoneItem(item) {
    if (!item) return;
    const keysToClear = [
      item.key ? String(item.key) : null,
      item.id ? String(item.id) : null,
      item.variantId ? String(item.variantId) : null,
      item.variant_id ? String(item.variant_id) : null,
      item.product_id ? `prod_${item.product_id}` : null,
      item.productId ? `prod_${item.productId}` : null
    ].filter(Boolean);

    keysToClear.forEach(k => recentlyRemovedItems.delete(k));
    if (Array.isArray(item.allKeys)) {
      item.allKeys.forEach(k => recentlyRemovedItems.delete(String(k)));
    }
  }

  // Sequential FIFO Cart Action Queue to serialize all Shopify network mutations
  let cartActionQueue = Promise.resolve();
  let activeRefreshPromise = null;
  let pendingRefreshTrailing = false;
  let pendingRefreshSilent = true;
  function enqueueCartAction(taskFn) {
    const runTask = () => {
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          console.warn('[Orgacia] Queued cart action safety timeout fired');
          resolve();
        }, 7000);
        try {
          const result = taskFn();
          if (result && typeof result.then === 'function') {
            result.then((val) => {
              clearTimeout(timeout);
              resolve(val);
            }).catch(err => {
              clearTimeout(timeout);
              console.warn('[Orgacia] Queued cart action error:', err);
              resolve();
            });
          } else {
            clearTimeout(timeout);
            resolve(result);
          }
        } catch (err) {
          clearTimeout(timeout);
          console.warn('[Orgacia] Queued cart action exception:', err);
          resolve();
        }
      });
    };

    cartActionQueue = cartActionQueue.then(runTask, runTask);
    return cartActionQueue;
  }

  // Debouncing & concurrency queue state for rapid quantity stepper clicks
  const qtyDebounceTimers = {};
  const qtyInFlight = {};
  const qtyPendingTargets = {};
  const knownStockLimits = {};

  function getKnownStockLimit(item, trackKey) {
    if (!item && !trackKey) return null;
    if (item && typeof item.maxQuantity === 'number') return item.maxQuantity;
    const key = trackKey || (item ? String(item.key || item.variantId || item.id || '') : '');
    if (key && typeof knownStockLimits[key] === 'number') return knownStockLimits[key];
    if (item) {
      const vId = String(item.variantId || item.id || '');
      if (vId && typeof knownStockLimits[vId] === 'number') return knownStockLimits[vId];
      if (item.productId && typeof knownStockLimits[String(item.productId)] === 'number') return knownStockLimits[String(item.productId)];
    }
    return null;
  }

  function setKnownStockLimit(item, trackKey, limit) {
    if (!item && !trackKey) return;
    const numLimit = Math.max(0, parseInt(limit, 10));
    if (isNaN(numLimit)) return;
    if (item) item.maxQuantity = numLimit;
    if (trackKey) knownStockLimits[String(trackKey)] = numLimit;
    if (item) {
      if (item.key) knownStockLimits[String(item.key)] = numLimit;
      if (item.variantId) knownStockLimits[String(item.variantId)] = numLimit;
      if (item.id) knownStockLimits[String(item.id)] = numLimit;
      if (item.productId) knownStockLimits[String(item.productId)] = numLimit;
    }
  }

  // Public Orgacia Cart API
  const OrgaciaCart = {
    init: function (customConfig = {}) {
      if (customConfig) {
        config = {
          ...DEFAULT_CONFIG,
          ...customConfig,
          timer: Object.assign({}, DEFAULT_CONFIG.timer, customConfig.timer || {}),
          announcement: Object.assign({}, DEFAULT_CONFIG.announcement, customConfig.announcement || {}),
          tiers: Object.assign({}, DEFAULT_CONFIG.tiers, customConfig.tiers || {}),
          volumeDiscounts: Object.assign({}, DEFAULT_CONFIG.volumeDiscounts, customConfig.volumeDiscounts || {}),
          giftSelection: Object.assign({}, DEFAULT_CONFIG.giftSelection, customConfig.giftSelection || {}),
          giftWrap: Object.assign({}, DEFAULT_CONFIG.giftWrap, customConfig.giftWrap || {}),
          stickyAtc: Object.assign({}, DEFAULT_CONFIG.stickyAtc, customConfig.stickyAtc || {}),
          instructions: Object.assign({}, DEFAULT_CONFIG.instructions, customConfig.instructions || {}),
          savingsCallout: Object.assign({}, DEFAULT_CONFIG.savingsCallout, customConfig.savingsCallout || {}),
          upsell: Object.assign({}, DEFAULT_CONFIG.upsell, customConfig.upsell || {}),
          celebration: Object.assign({}, DEFAULT_CONFIG.celebration, customConfig.celebration || {}),
          tierUpgrade: Object.assign({}, DEFAULT_CONFIG.tierUpgrade, customConfig.tierUpgrade || {}),
          footer: Object.assign({}, DEFAULT_CONFIG.footer, customConfig.footer || {})
        };
        if (customConfig.tierUpgrade) {
          config.tierUpgrade = Object.assign({}, DEFAULT_CONFIG.tierUpgrade, customConfig.tierUpgrade);
        }
        if (customConfig.coupons) config.coupons = customConfig.coupons;
        if (customConfig.couponsEnabled !== undefined) config.couponsEnabled = customConfig.couponsEnabled;
        if (customConfig.milestoneTopClaimStyle) {
          config.milestoneTopClaimStyle = customConfig.milestoneTopClaimStyle;
        } else if (customConfig.tiers && customConfig.tiers.milestoneTopClaimStyle) {
          config.milestoneTopClaimStyle = customConfig.tiers.milestoneTopClaimStyle;
        }
        if (customConfig.milestoneBottomClaimStyle) {
          config.milestoneBottomClaimStyle = customConfig.milestoneBottomClaimStyle;
        } else if (customConfig.tiers && customConfig.tiers.milestoneBottomClaimStyle) {
          config.milestoneBottomClaimStyle = customConfig.tiers.milestoneBottomClaimStyle;
        }
        if (customConfig.noCoupons) {
          config.noCoupons = Object.assign({}, DEFAULT_CONFIG.noCoupons || {}, customConfig.noCoupons);
        }
        if (customConfig.topClaimColors || (customConfig.tiers && customConfig.tiers.topClaimColors)) {
          this.setTopClaimColors(customConfig.topClaimColors || customConfig.tiers.topClaimColors);
        }
        if (customConfig.bottomClaimColors || (customConfig.tiers && customConfig.tiers.bottomClaimColors)) {
          this.setBottomClaimColors(customConfig.bottomClaimColors || customConfig.tiers.bottomClaimColors);
        }
        if (customConfig.roundDecimals !== undefined) {
          config.roundDecimals = !!customConfig.roundDecimals;
        } else if (typeof window !== 'undefined' && window.ORGACIA_SETTINGS && window.ORGACIA_SETTINGS.roundDecimals !== undefined) {
          config.roundDecimals = !!window.ORGACIA_SETTINGS.roundDecimals;
        }
      }

      // Preload gift product images immediately so they render with zero delay
      preloadGiftImages();

      // If gift wrap is disabled in settings, ensure it is stripped completely
      if (config.giftWrap && config.giftWrap.enabled === false) {
        cartState.isGiftWrapAdded = false;
        cartState.items = cartState.items.filter(i => !i.isGiftWrap);
      }

      // 0ms Instant Cart Priming: Sync with server rendered cart if provided in settings
      if (customConfig && customConfig.initialCart && Array.isArray(customConfig.initialCart.items)) {
        this.syncWithCart(customConfig.initialCart);
      }

      createDrawerDOM();
      preloadAllDrawerAssets();
      setupShopifyCartInterceptor();
      hideLoader(true);

      if (isShopifyEnv) {
        this.refresh(true);
      } else {
        this.open();
      }
    },

    // 0ms Instant Ingestion of items returned from /cart/add.js or DOM
    fastIngestAddedItem: function (data) {
      if (!data) return;
      const rawList = Array.isArray(data.items) ? data.items : [data];
      rawList.forEach(item => {
        if (!item) return;
        const wasRecentlyRemoved = isItemTombstoned(item);
        if (wasRecentlyRemoved) {
          untombstoneItem(item);
        }
        const itemId = item.id || item.variant_id;
        const itemKey = item.key || itemId;

        // Bulletproof price parsing: normalize all incoming price formats (rupees or paise)
        const isFromDom = !!item._fromDom;
        let parsedPrice = 0;
        let parsedBasePrice = 0;
        let parsedComparePrice = 0;

        if (isFromDom) {
          const rawSelling = (item.calculatedPrice !== undefined && item.calculatedPrice !== null)
            ? item.calculatedPrice
            : ((item.price !== undefined && item.price !== null) ? item.price : item.final_price);
          parsedPrice = normalizePriceAmount(rawSelling, true);

          const rawBase = (item.basePrice !== undefined && item.basePrice !== null)
            ? item.basePrice
            : ((item.price !== undefined && item.price !== null) ? item.price : rawSelling);
          parsedBasePrice = normalizePriceAmount(rawBase, true);

          const rawComp = item.compare_at_price || item.original_price || 0;
          parsedComparePrice = normalizePriceAmount(rawComp, true);
        } else {
          const rawSelling = item.final_price !== undefined ? item.final_price : (item.price !== undefined ? item.price : 0);
          parsedPrice = normalizePriceAmount(rawSelling, false);

          const rawBase = item.original_price !== undefined ? item.original_price : (item.basePrice || item.price || rawSelling);
          parsedBasePrice = normalizePriceAmount(rawBase, false);

          const rawComp = item.compare_at_price || 0;
          parsedComparePrice = normalizePriceAmount(rawComp, false);
        }

        // Reconcile with authoritative product page pricing ONLY if item carries no active server discounts
        const itemHasDiscounts = (Array.isArray(item.discounts) && item.discounts.length > 0) ||
                                 (Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length > 0) ||
                                 (item.final_price !== undefined && item.original_price !== undefined && item.final_price < item.original_price);
        if (!itemHasDiscounts) {
          const pagePricing = readProductPagePricing(item) || getCachedProductPricing(item);
          if (pagePricing && pagePricing.sellingPrice > 0) {
            parsedPrice = pagePricing.sellingPrice;
            parsedBasePrice = pagePricing.sellingPrice;
            if (pagePricing.comparePrice > pagePricing.sellingPrice) {
              parsedComparePrice = pagePricing.comparePrice;
            }
          }
        }

        if (parsedBasePrice <= 0 || (parsedPrice > 0 && parsedBasePrice < parsedPrice)) {
          parsedBasePrice = parsedPrice;
        }
        if (parsedComparePrice <= parsedPrice) {
          parsedComparePrice = 0;
        }

        const imgUrl = item.image || (item.featured_image ? (item.featured_image.url || item.featured_image) : '');
        const itemTitle = item.product_title || item.title || 'Product';
        const itemVariantTitle = item.variant_title || '';

        // Match existing item by ID, key, variantId, productId, or exact matching title
        const parsedPId = String(item.product_id || item.productId || '').replace(/\D/g, '');
        const existing = cartState.items.find(i => {
          if (i.isGift || i.isGiftWrap) return false;
          if (itemId && (String(i.id) === String(itemId) || String(i.key) === String(itemKey) || String(i.variantId) === String(itemId))) return true;
          const curPId = String(i.productId || '').replace(/\D/g, '');
          if (parsedPId && curPId && parsedPId === curPId) return true;
          if (itemTitle && itemTitle !== 'Product' && i.title && i.title.toLowerCase().trim() === itemTitle.toLowerCase().trim()) return true;
          return false;
        });

        if (existing) {
          const isRecentOptimistic = existing._lastOptimisticAdd && (Date.now() - existing._lastOptimisticAdd < 4000);
          if (itemId && (String(existing.id || '').startsWith('temp_') || isRecentOptimistic)) {
            existing.id = itemId;
            existing.key = itemKey;
            existing.variantId = itemId;
          }
          const isSameClickBurst = existing._lastOptimisticAdd && (Date.now() - existing._lastOptimisticAdd < 1200);
          const isBundle = item.isBundleItem || (item.properties && (item.properties._is_bundle === 'true' || item.properties._bundle_selected === 'true' || parseFloat(item.properties._bundle_discount) > 0 || item.properties._bundle_tier));
          if (item._fromDom) {
            if (wasRecentlyRemoved) {
              // Item was recently removed: discard any stale quantity and start fresh with exact requested quantity
              existing.quantity = item.quantity || 1;
            } else if (isBundle) {
              // Pack/bundle tier selection: set exact tier pack quantity (e.g. 4 for 4-pack), never double-add
              existing.quantity = item.quantity || 1;
            } else if (isSameClickBurst) {
              existing.quantity = Math.max(existing.quantity || 1, item.quantity || 1);
            } else {
              existing.quantity = (existing.quantity || 0) + (item.quantity || 1);
            }
            existing._lastOptimisticAdd = Date.now();
          } else {
            // Real network response from Shopify (/cart/add.js) takes precedence over any optimistic guess
            if (typeof item.quantity === 'number') {
              existing.quantity = item.quantity;
            }
            existing._lastOptimisticAdd = undefined;
          }
          // Always update prices from authoritative source data (server or DOM)
          if (parsedPrice > 0) {
            existing.price = parsedPrice;
          }
          if (parsedBasePrice > 0) {
            existing.basePrice = parsedBasePrice;
          }
          existing.originalPrice = (existing.basePrice || parsedBasePrice || parsedPrice) * (existing.quantity || 1);
          if (parsedComparePrice > parsedPrice) {
            existing.compare_at_price = parsedComparePrice;
          }
          if (imgUrl && !imgUrl.includes('placeholder')) {
            existing.image = imgUrl;
          }
          if (itemTitle && itemTitle !== 'Product') {
            existing.title = itemTitle;
          }
          if (itemVariantTitle && itemVariantTitle !== 'Default Title') {
            existing.variant_title = itemVariantTitle;
            if (!itemVariantTitle.startsWith('Pack of')) {
              existing.base_variant_title = itemVariantTitle;
            }
          }
          if (item.properties && Object.keys(item.properties).length > 0) {
            existing.properties = Object.assign({}, existing.properties || {}, item.properties);
          }
          if (isBundle) {
            existing.isBundleItem = true;
            const dPct = parseFloat(item.discountPct || (item.properties && (item.properties._bundle_discount || item.properties._bundle_discount_pct)) || 0);
            if (dPct > 0) {
              existing.discountPct = dPct;
              existing.bundleDiscountPct = dPct;
              existing.bundleLabel = item.bundleLabel || (item.properties && item.properties._bundle_tier) || `Bundle (${dPct}% OFF)`;
            } else if (item.bundleLabel !== undefined) {
              existing.bundleLabel = item.bundleLabel;
            }
            if (item.calculatedPrice !== undefined) existing.calculatedPrice = normalizePriceAmount(item.calculatedPrice, isFromDom);
          }
        } else {
          cartState.items.push({
            id: itemId || ('item_' + Date.now()),
            key: itemKey || itemId,
            variantId: item.variant_id || itemId,
            title: itemTitle,
            variant_title: itemVariantTitle,
            base_variant_title: (!itemVariantTitle.startsWith('Pack of') && itemVariantTitle !== 'Default Title') ? itemVariantTitle : undefined,
            price: parsedPrice,
            basePrice: parsedBasePrice,
            originalPrice: parsedBasePrice * (item.quantity || 1),
            compare_at_price: parsedComparePrice > parsedPrice ? parsedComparePrice : (item.compare_at_price ? normalizePriceAmount(item.compare_at_price, isFromDom) : null),
            quantity: item.quantity || 1,
            _lastOptimisticAdd: item._fromDom ? Date.now() : undefined,
            image: getValidImageUrl(imgUrl),
            url: item.url || '#',
            properties: item.properties || {},
            isBundleItem: item.isBundleItem || false,
            bundleLabel: item.bundleLabel || null,
            calculatedPrice: item.calculatedPrice !== undefined ? normalizePriceAmount(item.calculatedPrice, isFromDom) : parsedPrice,
            discountPct: item.discountPct || 0,
            total_discount: item.total_discount || 0,
            discounts: item.discounts || []
          });
        }
      });
      updateCartCalculations();
    },

    dispatchDrawerOpenTelemetry: function () {
      dispatchDrawerOpenTelemetry();
    },

    dispatchCheckoutTelemetry: function () {
      dispatchCheckoutTelemetry();
    },

    open: function () {
      resetCheckoutButton();
      const drawer = document.getElementById('oc-drawer');
      const overlay = document.getElementById('oc-drawer-overlay');
      const footerEl = document.getElementById('oc-footer');
      const ribbonEl = document.getElementById('oc-turkish-savings-ribbon');
      if (footerEl && ribbonEl && ribbonEl.parentElement !== footerEl) {
        footerEl.insertBefore(ribbonEl, footerEl.firstChild);
      }
      if (drawer && overlay) {
        overlay.classList.add('oc-active');
        drawer.classList.add('oc-active');
        document.body.style.overflow = 'hidden';
      }

      // Defer asset preloading so the drawer open animation starts with zero frame drops
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(() => preloadAllDrawerAssets(), { timeout: 800 });
      } else {
        setTimeout(preloadAllDrawerAssets, 80);
      }

      // Reconcile matching cart items with authoritative product page pricing
      try {
        const pagePricing = readProductPagePricing();
        if (pagePricing && pagePricing.sellingPrice > 0 && Array.isArray(cartState.items)) {
          let updated = false;
          cartState.items.forEach(item => {
            if (item.isGift || item.isGiftWrap || item.hasServerDiscount) return;
            if (isCurrentPageForProduct(item)) {
              item.price = pagePricing.sellingPrice;
              item.basePrice = pagePricing.sellingPrice;
              if (!item.isBundleItem || !item.calculatedPrice) {
                item.calculatedPrice = pagePricing.sellingPrice;
              }
              if (pagePricing.comparePrice > pagePricing.sellingPrice) {
                item.compare_at_price = pagePricing.comparePrice;
              }
              updated = true;
            }
          });
        }
      } catch(e) {}

      // Bulletproof real-time drawer open telemetry
      dispatchDrawerOpenTelemetry();

      // Instantly evaluate bundle eligibility for current page configuration
      updateCartCalculations();
      startAnnouncementCarousel();
    },

    close: function () {
      const drawer = document.getElementById('oc-drawer');
      const overlay = document.getElementById('oc-drawer-overlay');
      const header = document.getElementById('oc-header');
      if (drawer && overlay) {
        overlay.classList.remove('oc-active');
        drawer.classList.remove('oc-active');
        drawer.classList.remove('oc-show-discounts');
        if (header) header.classList.remove('oc-header--subpage');
        document.body.style.overflow = '';
      }
      pauseAnnouncementCarousel();
    },

    toggle: function () {
      const drawer = document.getElementById('oc-drawer');
      if (drawer && drawer.classList.contains('oc-active')) {
        this.close();
      } else {
        this.open();
      }
    },

    // Sync state directly with Shopify cart object
    syncWithCart: function (cart) {
      if (!cart || !cart.items) return;

      if (window.ORGACIA_SETTINGS) {
        window.ORGACIA_SETTINGS.initialCart = cart;
      }
      if (typeof config !== 'undefined' && config) {
        config.initialCart = cart;
      }

      const existingGifts = cartState.items.filter(i => i.isGift);

      // Consolidate line items belonging to the same product / variant
      const consolidatedItems = [];

      cart.items.forEach(rawItem => {
        // Prevent stale asynchronous /cart.js server responses from resurrecting recently removed items
        if (isItemTombstoned(rawItem)) {
          return;
        }
        const vId = rawItem.variant_id || rawItem.id;
        const pId = rawItem.product_id;
        const rawTitle = (rawItem.product_title || rawItem.title || '').trim();
        const rawProps = rawItem.properties || {};

        // Detect Free Gift line items in Shopify cart
        const isShopifyGift = rawProps._is_free_gift === 'true' || 
                              rawProps._gift_slot !== undefined || 
                              rawProps.Promotion === 'Free Gift Reward' ||
                              (rawItem.final_price === 0 && rawProps._gift === 'true');

        if (isShopifyGift) {
          const slotKey = rawProps._gift_slot || 'gift_slot_0';
          const giftProducts = (config.giftSelection && config.giftSelection.products) || [];
          const matchedConfigGift = giftProducts.find(p => String(p.id) === String(vId) || p.title === rawTitle);
          const giftItem = {
            id: rawItem.id,
            key: rawItem.key || rawItem.id,
            allKeys: [rawItem.key || rawItem.id],
            variantId: vId,
            productId: pId,
            title: rawTitle,
            variant_title: rawItem.variant_title,
            price: 0,
            basePrice: 0,
            calculatedPrice: 0,
            originalPrice: (rawItem.original_line_price || rawItem.line_price || rawItem.price || 0) / 100,
            compare_at_price: null,
            quantity: rawItem.quantity || 1,
            image: rawItem.image || (rawItem.featured_image ? (rawItem.featured_image.url || rawItem.featured_image) : (matchedConfigGift ? matchedConfigGift.image : '')),
            badgeText: (matchedConfigGift && matchedConfigGift.badgeText) || 'FREE GIFT',
            isGift: true,
            giftSlotKey: slotKey,
            properties: rawProps
          };
          consolidatedItems.unshift(giftItem);
          return;
        }

        // Check if an item for this variant/product already exists in consolidatedItems
        const existing = consolidatedItems.find(ci => 
          !ci.isGift && !ci.isGiftWrap && (
            (vId && String(ci.variantId) === String(vId)) ||
            (rawItem.key && String(ci.key) === String(rawItem.key)) ||
            (pId && ci.productId && String(ci.productId) === String(pId)) ||
            (rawTitle && ci.title && ci.title.toLowerCase() === rawTitle.toLowerCase())
          )
        );

        if (existing) {
          // Attach to existing product: combine quantity!
          existing.quantity += (rawItem.quantity || 1);
          existing.allKeys = existing.allKeys || [existing.key];
          if (rawItem.key && !existing.allKeys.includes(rawItem.key)) {
            existing.allKeys.push(rawItem.key);
          }

          // If this line item has bundle properties, attach them to the consolidated product!
          const rawProps = rawItem.properties || {};
          const isBundle = rawProps._is_bundle === 'true' || rawProps._bundle_selected === 'true' || parseFloat(rawProps._bundle_discount || rawProps._discount_percentage || 0) > 0;
          if (isBundle) {
            existing.properties = Object.assign({}, existing.properties || {}, rawProps);
            existing.isBundleItem = true;
            const dPct = parseFloat(rawProps._bundle_discount || rawProps._bundle_discount_pct || rawProps._discount_percentage || 0);
            if (dPct > 0) {
              existing.discountPct = dPct;
              existing.bundleDiscountPct = dPct;
              existing.bundleLabel = rawProps._bundle_tier || rawProps._bundle_label || `Bundle (${dPct}% OFF)`;
            }
            if (rawItem.total_discount > 0) existing.total_discount = (existing.total_discount || 0) + rawItem.total_discount;
          }
          const stockLmt = getKnownStockLimit(existing, existing.key);
          if (typeof stockLmt === 'number') {
            existing.maxQuantity = stockLmt;
          }
        } else {
          // Parse as fresh item
          // Shopify cart API price fields (all in paise/cents):
          //   price: variant's list price
          //   original_price: price before line-item discounts (= price if no scripts)
          //   final_price: actual per-unit price customer pays (after all discounts)
          //   compare_at_price: the MRP / "was" price (for strikethrough display)
          // basePrice must be the SELLING price, not the MRP!
          const itemQty = Math.max(1, parseInt(rawItem.quantity, 10) || 1);
          let rawSelling = rawItem.final_price !== undefined ? rawItem.final_price : rawItem.price;
          let rawOriginal = rawItem.original_price !== undefined ? rawItem.original_price : rawItem.price;

          const hasLineLevelAlloc = Array.isArray(rawItem.line_level_discount_allocations) && rawItem.line_level_discount_allocations.length > 0;
          const hasDiscountsArr = Array.isArray(rawItem.discounts) && rawItem.discounts.length > 0;
          const hasTotalDiscount = (rawItem.total_discount && rawItem.total_discount > 0);
          const hasPriceDiff = (rawItem.final_price !== undefined && rawItem.original_price !== undefined && rawItem.final_price < rawItem.original_price);
          const hasLinePriceDiff = (rawItem.final_line_price !== undefined && rawItem.original_line_price !== undefined && rawItem.final_line_price < rawItem.original_line_price);

          const hasServerDiscount = hasPriceDiff || hasLinePriceDiff || hasLineLevelAlloc || hasDiscountsArr || hasTotalDiscount;

          if (hasServerDiscount) {
            if (rawItem.final_line_price !== undefined && rawItem.original_line_price !== undefined && rawItem.final_line_price < rawItem.original_line_price) {
              rawSelling = rawItem.final_line_price / itemQty;
              rawOriginal = rawItem.original_line_price / itemQty;
            } else if (hasTotalDiscount) {
              rawSelling = Math.max(0, (rawItem.original_line_price || (rawItem.price * itemQty)) - rawItem.total_discount) / itemQty;
            } else if (hasPriceDiff) {
              rawSelling = rawItem.final_price;
              rawOriginal = rawItem.original_price;
            }
          }

          let unitSellingPrice = normalizePriceAmount(rawSelling, false);
          let unitOriginalPrice = normalizePriceAmount(rawOriginal, false);
          if (unitOriginalPrice <= 0 || unitOriginalPrice < unitSellingPrice) {
            unitOriginalPrice = unitSellingPrice;
          }

          // Reconcile with authoritative product page pricing ONLY if item does NOT have a server discount
          let pagePricing = null;
          if (!hasServerDiscount) {
            pagePricing = readProductPagePricing(rawItem) || getCachedProductPricing(rawItem);
            if (pagePricing && pagePricing.sellingPrice > 0) {
              unitSellingPrice = pagePricing.sellingPrice;
              unitOriginalPrice = pagePricing.sellingPrice;
            }
          }

          let serverDiscountTitle = null;
          if (Array.isArray(rawItem.line_level_discount_allocations) && rawItem.line_level_discount_allocations.length > 0) {
            const alloc = rawItem.line_level_discount_allocations[0];
            serverDiscountTitle = alloc && alloc.discount_application ? alloc.discount_application.title : null;
          } else if (Array.isArray(rawItem.discounts) && rawItem.discounts.length > 0) {
            serverDiscountTitle = rawItem.discounts[0].title || null;
          }
          if (!serverDiscountTitle && Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length > 0) {
            serverDiscountTitle = cart.cart_level_discount_applications[0].title || null;
          }

          let extractedHandle = rawItem.handle || '';
          if (!extractedHandle && rawItem.url) {
            const m = String(rawItem.url).match(/\/products\/([a-zA-Z0-9\-_]+)/);
            if (m && m[1]) extractedHandle = m[1];
          }

          const parsed = {
            id: rawItem.id,
            key: rawItem.key || rawItem.id,
            allKeys: [rawItem.key || rawItem.id],
            variantId: rawItem.variant_id || rawItem.id,
            productId: rawItem.product_id,
            handle: extractedHandle,
            title: rawItem.product_title || rawItem.title,
            variant_title: rawItem.variant_title,
            price: unitSellingPrice,
            basePrice: unitOriginalPrice,
            calculatedPrice: unitSellingPrice,
            hasServerDiscount: hasServerDiscount,
            serverOriginalPrice: unitOriginalPrice,
            serverSellingPrice: unitSellingPrice,
            serverDiscountTitle: serverDiscountTitle,
            originalPrice: (rawItem.final_line_price !== undefined ? normalizePriceAmount(rawItem.final_line_price, false) : (unitSellingPrice * itemQty)),
            compare_at_price: (pagePricing && pagePricing.comparePrice > unitSellingPrice)
              ? pagePricing.comparePrice
              : (rawItem.compare_at_price ? normalizePriceAmount(rawItem.compare_at_price, false) : (unitOriginalPrice > unitSellingPrice ? unitOriginalPrice : null)),
            quantity: rawItem.quantity,
            image: rawItem.image || (rawItem.featured_image ? (rawItem.featured_image.url || rawItem.featured_image) : ''),
            url: rawItem.url,
            properties: rawItem.properties || {},
            total_discount: rawItem.total_discount || 0,
            discounts: rawItem.discounts || []
          };

          if (hasServerDiscount) {
            parsed.isBundleItem = true;
            if (serverDiscountTitle) {
              parsed.serverDiscountTitle = serverDiscountTitle;
              parsed.bundleLabel = serverDiscountTitle;
            }
            if (unitOriginalPrice > unitSellingPrice && unitOriginalPrice > 0) {
              const dPct = Math.round((1 - (unitSellingPrice / unitOriginalPrice)) * 100);
              parsed.discountPct = dPct;
              parsed.bundleDiscountPct = dPct;
              if (!parsed.bundleLabel) {
                parsed.bundleLabel = `${dPct}% OFF`;
              }
            }
          }

          const prevItem = cartState.items.find(i => 
            !i.isGift && (
              String(i.id) === String(rawItem.id) ||
              (i.key && String(i.key) === String(rawItem.key)) ||
              (vId && String(i.variantId) === String(vId)) ||
              (pId && i.productId && String(i.productId) === String(pId)) ||
              (rawTitle && i.title && i.title.toLowerCase().trim() === rawTitle.toLowerCase().trim())
            )
          );
          if (prevItem) {
            if (prevItem.base_variant_title) parsed.base_variant_title = prevItem.base_variant_title;
            if (prevItem.compare_at_price && !parsed.compare_at_price) parsed.compare_at_price = prevItem.compare_at_price;
            // NEVER carry over stale basePrice from a previous state — Shopify's server price is authoritative.
            // The parsed.basePrice from rawItem (original_price / 100) is always correct.
            if (prevItem._lastOptimisticAdd) parsed._lastOptimisticAdd = prevItem._lastOptimisticAdd;
            if (prevItem.properties && Object.keys(prevItem.properties).length > 0) {
              parsed.properties = Object.assign({}, prevItem.properties, parsed.properties || {});
            }
            if (prevItem.isBundleItem || (parsed.properties && parsed.properties._is_bundle === 'true') || hasServerDiscount) {
              parsed.isBundleItem = true;
              if (parsed.discountPct || prevItem.discountPct) parsed.discountPct = parsed.discountPct || prevItem.discountPct;
              if (parsed.bundleDiscountPct || prevItem.bundleDiscountPct) parsed.bundleDiscountPct = parsed.bundleDiscountPct || prevItem.bundleDiscountPct;
              if (serverDiscountTitle) parsed.bundleLabel = serverDiscountTitle;
              else if (parsed.bundleLabel) parsed.bundleLabel = parsed.bundleLabel;
              else if (prevItem.bundleLabel) parsed.bundleLabel = prevItem.bundleLabel;
              if (prevItem.appliedBundleTier) parsed.appliedBundleTier = prevItem.appliedBundleTier;
            }
            const rawQty = Math.max(1, parseInt(rawItem.quantity, 10) || 1);
            const basePrefix = parsed.base_variant_title ? `${parsed.base_variant_title} • ` : '';
            parsed.variant_title = `${basePrefix}Pack of ${rawQty}`;
            if (typeof prevItem.maxQuantity === 'number') {
              parsed.maxQuantity = prevItem.maxQuantity;
            }
          }

          const parsedStockLmt = getKnownStockLimit(parsed, parsed.key);
          if (typeof parsedStockLmt === 'number') {
            parsed.maxQuantity = parsedStockLmt;
          }

          consolidatedItems.push(parsed);
        }
      });
      let parsedItems = consolidatedItems;

      // Check if there are any regular products in cart
      const hasRegularProducts = parsedItems.filter(i => !i.isGift && !i.isGiftWrap).length > 0;

      // Preserve Gift Wrap item ONLY if enabled in settings AND regular products exist
      if (hasRegularProducts && config.giftWrap && config.giftWrap.enabled && cartState.isGiftWrapAdded && !parsedItems.some(i => i.isGiftWrap)) {
        parsedItems.unshift({
          id: 'item-giftwrap',
          isGiftWrap: true,
          title: 'Gift Wrap',
          price: config.giftWrap.price || 49.00,
          basePrice: config.giftWrap.price || 49.00,
          compare_at_price: 199.00,
          originalPrice: 199.00,
          image: 'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=200&auto=format&fit=crop&q=80',
          quantity: 1
        });
      } else if (!hasRegularProducts) {
        cartState.isGiftWrapAdded = false;
        const giftWrapCheckbox = document.getElementById('oc-giftwrap-checkbox');
        if (giftWrapCheckbox) giftWrapCheckbox.checked = false;
      }

      // Preserve claimed free gifts ONLY if gift requirement is still met AND regular products exist
      if (hasRegularProducts && existingGifts.length > 0) {
        const tempSubtotal = parsedItems.filter(i => !i.isGift && !i.isGiftWrap).reduce((s, itm) => s + ((itm.calculatedPrice !== undefined ? itm.calculatedPrice : itm.price) * itm.quantity), 0);
        const giftStillEligible = isGiftEligible(tempSubtotal);
        if (giftStillEligible) {
          existingGifts.forEach(g => {
            if (!parsedItems.some(pi => pi.isGift && (String(pi.id) === String(g.id) || pi.giftSlotKey === g.giftSlotKey))) {
              parsedItems.unshift(g);
            }
          });
        }
      }

      // If no regular products remain, cart is completely empty: purge all orphan gifts/wraps
      if (!hasRegularProducts) {
        parsedItems = [];
      }

      cartState.items = parsedItems;
      cartState.note = cart.note || '';
      updateCartCalculations();
    },

    refresh: function (silent = false) {
      resetCheckoutButton();
      if (!isShopifyEnv) {
        updateCartCalculations(true);
        hideLoader(true);
        return Promise.resolve();
      }
      if (!silent) {
        showLoader();
      }

      // High-performance coalescing: if a /cart.js fetch is already actively in-flight, coalesce with it!
      if (activeRefreshPromise) {
        if (!silent) pendingRefreshSilent = false;
        pendingRefreshTrailing = true;
        return activeRefreshPromise;
      }

      const self = this;
      const executeCartFetch = async (isSilent) => {
        try {
          const fetchFn = (typeof window._orgaciaOriginalFetch === 'function') 
            ? window._orgaciaOriginalFetch 
            : (rawFetch || window.fetch);
          const res = await fetchFn(`/cart.js?_t=${Date.now()}`, { cache: 'no-store' });
          if (res.ok) {
            const cart = await res.json();
            self.syncWithCart(cart);
          }
        } catch (err) {
          console.error('Orgacia cart fetch error', err);
        } finally {
          if (!isSilent) {
            hideLoader(true);
          }
        }
      };

      activeRefreshPromise = enqueueCartAction(async () => {
        await executeCartFetch(silent);
        activeRefreshPromise = null;

        // If another refresh arrived while this was running, execute ONE trailing refresh
        if (pendingRefreshTrailing) {
          pendingRefreshTrailing = false;
          const nextSilent = pendingRefreshSilent;
          pendingRefreshSilent = true;
          activeRefreshPromise = executeCartFetch(nextSilent).finally(() => {
            activeRefreshPromise = null;
          });
          await activeRefreshPromise;
        }
      });

      return activeRefreshPromise;
    },

    addItem: function (item) {
      if (!item) return;

      const wasRecentlyRemoved = isItemTombstoned(item);
      if (wasRecentlyRemoved) {
        untombstoneItem(item);
      }

      // 1. Instant Optimistic UI Add (0ms) - Match existing product by ID, variantId, productId, or exact title
      const targetId = item.variantId || item.id;
      const targetTitle = (item.title || item.product_title || '').toLowerCase().trim();
      const targetPId = String(item.productId || '').replace(/\D/g, '');
      const existing = cartState.items.find(i => {
        if (i.isGift || i.isGiftWrap) return false;
        if (targetId && (String(i.id) === String(targetId) || String(i.variantId) === String(targetId) || String(i.key) === String(targetId))) return true;
        const curPId = String(i.productId || '').replace(/\D/g, '');
        if (targetPId && curPId && targetPId === curPId) return true;
        if (targetTitle && i.title && i.title.toLowerCase().trim() === targetTitle) return true;
        return false;
      });

      const addQty = Math.max(1, parseInt(item.quantity, 10) || 1);

      // Ensure basePrice is always set correctly from item data
      const itemBasePrice = Number(item.basePrice || item.price) || 0;
      const itemPrice = Number(item.price) || itemBasePrice;

      if (existing) {
        const isSameClickBurst = existing._lastOptimisticAdd && (Date.now() - existing._lastOptimisticAdd < 1200);
        existing._lastOptimisticAdd = Date.now();
        const isBundle = item.isBundleItem || (item.properties && (item.properties._is_bundle === 'true' || item.properties._bundle_selected === 'true' || parseFloat(item.properties._bundle_discount) > 0 || item.properties._bundle_tier)) || (item.discountPct && item.discountPct > 0);
        if (wasRecentlyRemoved) {
          // Item was recently removed: set exact requested quantity, do not accumulate stale count
          existing.quantity = addQty;
        } else if (isBundle) {
          // Pack tier selection: set exact pack quantity, do not double-add
          existing.quantity = addQty;
        } else if (isSameClickBurst) {
          existing.quantity = Math.max(existing.quantity || 1, addQty);
        } else {
          existing.quantity = (existing.quantity || 0) + addQty;
        }
        if (item.variant_title) {
          existing.variant_title = item.variant_title;
        }
        // Re-validate basePrice: if existing basePrice is missing or zero, set from incoming item
        if (itemBasePrice > 0 && (!existing.basePrice || existing.basePrice <= 0)) {
          existing.basePrice = itemBasePrice;
        }
        if (itemPrice > 0 && (!existing.price || existing.price <= 0)) {
          existing.price = itemPrice;
        }
        existing.originalPrice = (existing.basePrice || itemBasePrice) * existing.quantity;
        if (item.properties) {
          existing.properties = Object.assign({}, existing.properties || {}, item.properties);
        }
        if (!existing.base_variant_title && existing.variant_title && !existing.variant_title.startsWith('Pack of')) {
          existing.base_variant_title = existing.variant_title;
        }
        if (isBundle) {
          existing.isBundleItem = true;
          if (item.discountPct) existing.discountPct = item.discountPct;
          if (item.bundleDiscountPct) existing.bundleDiscountPct = item.bundleDiscountPct;
          else if (item.discountPct) existing.bundleDiscountPct = item.discountPct;
          if (item.bundleLabel) existing.bundleLabel = item.bundleLabel;
          if (item.calculatedPrice !== undefined) existing.calculatedPrice = item.calculatedPrice;
        }
        // Propagate compare_at_price if provided (e.g. from upsell)
        if (item.compare_at_price && (!existing.compare_at_price || existing.compare_at_price <= 0)) {
          existing.compare_at_price = item.compare_at_price;
        }
      } else {
        const itemVarTitle = item.variant_title || '';
        cartState.items.push({
          id: item.id || item.variantId,
          key: item.key || item.id || item.variantId,
          variantId: item.variantId || item.id,
          title: item.title || 'Product',
          price: itemPrice,
          basePrice: itemBasePrice,
          originalPrice: itemBasePrice * addQty,
          compare_at_price: item.compare_at_price || null,
          image: item.image || '',
          quantity: addQty,
          variant_title: itemVarTitle,
          base_variant_title: (!itemVarTitle.startsWith('Pack of') && itemVarTitle !== 'Default Title') ? itemVarTitle : undefined,
          properties: item.properties || {},
          isBundleItem: item.isBundleItem || false,
          bundleLabel: item.bundleLabel || null,
          calculatedPrice: item.calculatedPrice,
          discountPct: item.discountPct || 0,
          _lastOptimisticAdd: Date.now()
        });
      }
      updateCartCalculations();
      showToast(`Added ${item.title || 'item'} to cart`);

      // 2. If running in Shopify with a numeric variant ID, sync in background
      const isNumericVariant = /^\d+$/.test(String(targetId));

      if (isShopifyEnv && isNumericVariant) {
        enqueueCartAction(async () => {
          const fetchFn = (typeof window._orgaciaOriginalFetch === 'function') 
            ? window._orgaciaOriginalFetch 
            : (rawFetch || window.fetch);
          try {
            const res = await fetchFn('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: Number(targetId), quantity: addQty })
            });
            const data = await res.json();
            if (!res.ok || data.status === 422 || (data.message && data.message.toLowerCase().includes('error'))) {
              const rawMsg = data.description || data.message || '';
              hideLoader(true);
              if (!/expected Integer|line parameter|\(?-mix/i.test(rawMsg)) {
                const errorMsg = rawMsg || `Sorry, ${item.title || 'this item'} is out of stock or maximum limit reached.`;
                showToast(`⚠️ ${errorMsg}`, 'warning');
              }
              this.refresh(true);
              return;
            }
            // Reconcile id / key without double-incrementing quantity
            if (data) {
              const returnedId = data.id || data.variant_id;
              const returnedKey = data.key || returnedId;
              const matchedItem = cartState.items.find(i => 
                (returnedId && (String(i.id) === String(returnedId) || String(i.variantId) === String(returnedId))) ||
                (targetId && (String(i.id) === String(targetId) || String(i.variantId) === String(targetId)))
              );
              if (matchedItem) {
                if (returnedId) {
                  matchedItem.id = returnedId;
                  matchedItem.variantId = returnedId;
                }
                if (returnedKey) matchedItem.key = returnedKey;
                matchedItem._lastOptimisticAdd = Date.now();
                if (data.featured_image || data.image) {
                  const img = data.image || (data.featured_image ? (data.featured_image.url || data.featured_image) : '');
                  if (img && !img.includes('placeholder')) matchedItem.image = img;
                }
                if (data.variant_title && data.variant_title !== 'Default Title' && !data.variant_title.startsWith('Pack of')) {
                  matchedItem.base_variant_title = data.variant_title;
                }
              }
            }
            this.refresh(true);
          } catch (err) {
            console.warn('[Orgacia] Upsell backend add notice:', err);
            hideLoader(true);
            showToast(`⚠️ Could not add item. Stock limit reached.`, 'warning');
            this.refresh(true);
          } finally {
            hideLoader(true);
          }
        });
      } else {
        hideLoader(true);
      }
    },

    updateQuantity: function (index, newQty) {
      let item = cartState.items[index];
      if (!item) return;

      const trackKey = String(item.key || item.variantId || item.id);

      if (newQty <= 0) {
        if (qtyDebounceTimers[trackKey]) {
          clearTimeout(qtyDebounceTimers[trackKey]);
          delete qtyDebounceTimers[trackKey];
        }
        delete qtyPendingTargets[trackKey];
        this.removeItem(index);
        return;
      }

      // Check if increasing beyond a known stock limit (INSTANT 0ms validation)
      if (newQty > item.quantity) {
        const stockLimit = getKnownStockLimit(item, trackKey);
        if (typeof stockLimit === 'number' && newQty > stockLimit) {
          hideLoader(true);
          showToast(`⚠️ Only ${stockLimit} ${stockLimit === 1 ? 'item is' : 'items are'} available in stock for ${item.title || 'this item'}.`, 'warning');
          return;
        }
      }

      // Preserve previous quantity in case server rejects with stock error
      item._prevQuantity = item.quantity;

      // Optimistic Instant UI Update (0ms) - In-place DOM update without destroying/recreating cart items
      item.quantity = newQty;
      updateCartCalculations(true);

      // Virtual items (Gift wrap / free gifts) only live in memory/state
      if (item.isGiftWrap || item.isGift) {
        hideLoader(true);
        return;
      }

      // If mock item or non-Shopify preview, do not dispatch network request
      const validShopifyId = getValidShopifyLineId(item);
      if (!isShopifyEnv || !validShopifyId) {
        hideLoader(true);
        return;
      }

      // Debounce Shopify network sync (snappy 100ms) to coalesce rapid multi-clicks
      qtyPendingTargets[trackKey] = newQty;
      if (qtyDebounceTimers[trackKey]) {
        clearTimeout(qtyDebounceTimers[trackKey]);
      }
      qtyDebounceTimers[trackKey] = setTimeout(() => {
        delete qtyDebounceTimers[trackKey];
        OrgaciaCart._dispatchShopifyQtyUpdate(trackKey);
      }, 100);
    },

    _dispatchShopifyQtyUpdate: function (trackKey) {
      if (!isShopifyEnv) return;

      // Prevent concurrent race conditions on the same line item
      if (qtyInFlight[trackKey]) {
        return;
      }

      const targetQty = qtyPendingTargets[trackKey];
      if (typeof targetQty !== 'number') return;

      const currentItem = cartState.items.find(i => 
        !i.isGift && !i.isGiftWrap && (
          String(i.key || '') === String(trackKey) ||
          String(i.variantId || '') === String(trackKey) ||
          String(i.id || '') === String(trackKey)
        )
      );

      if (!currentItem) {
        delete qtyPendingTargets[trackKey];
        return;
      }

      // Check known stock limit before making network request
      const knownLimit = getKnownStockLimit(currentItem, trackKey);
      if (typeof knownLimit === 'number' && targetQty > knownLimit) {
        currentItem.quantity = knownLimit;
        delete qtyPendingTargets[trackKey];
        updateCartCalculations(true);
        hideLoader(true);
        showToast(`⚠️ Only ${knownLimit} ${knownLimit === 1 ? 'item is' : 'items are'} available in stock for ${currentItem.title || 'this item'}.`, 'warning');
        return;
      }

      const validId = getValidShopifyLineId(currentItem);
      if (!validId) {
        delete qtyPendingTargets[trackKey];
        return;
      }

      qtyInFlight[trackKey] = true;
      showLoader();

      enqueueCartAction(async () => {
        const fetchFn = rawFetch || window.fetch;
        try {
          // If this item was consolidated from multiple Shopify line keys, zero out secondary keys
          if (Array.isArray(currentItem.allKeys) && currentItem.allKeys.length > 1) {
            const updates = {};
            const primaryKey = String(validId);
            currentItem.allKeys.forEach(k => {
              if (String(k) === primaryKey) {
                updates[k] = targetQty;
              } else {
                updates[k] = 0;
              }
            });

            const res = await fetchFn('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates })
            });
            const data = await res.json();
            if (!res.ok) {
              const rawMsg = data.description || data.message || '';
              const matchQty = rawMsg.match(/only\s+(\d+)\s+item/i) || rawMsg.match(/all\s+(\d+)\s+/i) || rawMsg.match(/(\d+)\s+units?\s+available/i);
              const limit = matchQty ? parseInt(matchQty[1], 10) : (currentItem._prevQuantity || 1);
              setKnownStockLimit(currentItem, trackKey, limit);
              currentItem.quantity = limit;
              delete qtyPendingTargets[trackKey];
              updateCartCalculations(true);
              hideLoader(true);
              if (rawMsg) showToast(`⚠️ ${rawMsg}`, 'warning');
              this.refresh(true);
            } else {
              this.syncWithCart(data);
            }
          } else {
            const res = await fetchFn('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                id: validId, 
                quantity: targetQty,
                properties: currentItem.properties || {}
              })
            });
            const data = await res.json();
            const isLineParamError = !res.ok && (
              (data.message && /line parameter|valid id|expected Integer to be a|\(?-mix/i.test(data.message)) ||
              (data.description && /line parameter|valid id|expected Integer to be a|\(?-mix/i.test(data.description))
            );

            if (isLineParamError) {
              // Line key shifted due to concurrent updates or property recalculations.
              // Silently reconcile cart with fresh /cart.js instead of showing scary developer toast!
              console.warn('[Orgacia] Shopify line key shifted, silently realigning cart...');
              try {
                const freshRes = await fetchFn(`/cart.js?_t=${Date.now()}`, { cache: 'no-store' });
                const freshCart = await freshRes.json();
                this.syncWithCart(freshCart);

                const freshItem = cartState.items.find(i => 
                  (currentItem.variantId && String(i.variantId) === String(currentItem.variantId)) ||
                  (currentItem.productId && String(i.productId) === String(currentItem.productId)) ||
                  (currentItem.title && i.title && i.title.toLowerCase() === currentItem.title.toLowerCase())
                );
                if (freshItem && freshItem.quantity !== targetQty && targetQty > 0) {
                  const freshId = getValidShopifyLineId(freshItem);
                  if (freshId) {
                    const retryRes = await fetchFn('/cart/change.js', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ 
                        id: freshId, 
                        quantity: targetQty,
                        properties: (freshItem.properties && Object.keys(freshItem.properties).length > 0) ? freshItem.properties : (currentItem.properties || {})
                      })
                    });
                    const retryData = await retryRes.json();
                    this.syncWithCart(retryData);
                  }
                }
              } catch (syncErr) {
                console.warn('[Orgacia] Silent resync notice:', syncErr);
              }
              return;
            }

            if (!res.ok || data.status === 422 || (data.message && data.message.toLowerCase().includes('error'))) {
              const rawMsg = data.description || data.message || '';
              if (!/expected Integer|line parameter|\(?-mix/i.test(rawMsg)) {
                // Extract available limit from message (e.g. "Only 1 item was added to your cart due to availability.")
                const matchQty = rawMsg.match(/only\s+(\d+)\s+item/i) || 
                                 rawMsg.match(/all\s+(\d+)\s+/i) || 
                                 rawMsg.match(/(\d+)\s+units?\s+available/i) ||
                                 rawMsg.match(/(\d+)\s+in\s+stock/i);
                const limit = matchQty ? parseInt(matchQty[1], 10) : (currentItem._prevQuantity || (targetQty > 1 ? targetQty - 1 : 1));
                
                // Cache known stock limit
                setKnownStockLimit(currentItem, trackKey, limit);

                // Revert quantity in client state immediately (0ms UI restoration)
                currentItem.quantity = limit;
                delete qtyPendingTargets[trackKey];
                updateCartCalculations(true);

                // Instantly dismiss loading progress bar
                hideLoader(true);

                const errorMsg = rawMsg || `Maximum available quantity reached for ${currentItem.title || 'this item'}.`;
                showToast(`⚠️ ${errorMsg}`, 'warning');

                // Perform silent resync in background (zero UI freeze, no loader)
                this.refresh(true);
                return;
              }
              hideLoader(true);
              this.refresh(true);
              return;
            }

            // If Shopify server clamped the quantity to available stock
            const updatedLine = data.items?.find(i => String(i.id) === String(currentItem.id) || String(i.key) === String(currentItem.key));
            if (updatedLine && updatedLine.quantity < targetQty) {
              const clampLimit = updatedLine.quantity;
              setKnownStockLimit(currentItem, trackKey, clampLimit);
              currentItem.quantity = clampLimit;
              delete qtyPendingTargets[trackKey];
              updateCartCalculations(true);
              hideLoader(true);
              showToast(`⚠️ Only ${clampLimit} ${clampLimit === 1 ? 'unit is' : 'units are'} available in stock for ${currentItem.title || 'this item'}.`, 'warning');
            }

            this.syncWithCart(data);
          }
        } catch (err) {
          console.warn('Update qty notice', err);
          hideLoader(true);
          try {
            const freshCart = await fetchFn(`/cart.js?_t=${Date.now()}`, { cache: 'no-store' }).then(r => r.json());
            this.syncWithCart(freshCart);
          } catch (e) {}
        } finally {
          this._finalizeQtyUpdate(trackKey);
        }
      });
    },

    _finalizeQtyUpdate: function (trackKey) {
      qtyInFlight[trackKey] = false;
      hideLoader(true);

      const targetQty = qtyPendingTargets[trackKey];
      const currentItem = cartState.items.find(i => 
        !i.isGift && !i.isGiftWrap && (
          String(i.key || '') === String(trackKey) ||
          String(i.variantId || '') === String(trackKey) ||
          String(i.id || '') === String(trackKey)
        )
      );

      // If user clicked again while the previous request was in flight, dispatch next queued target
      // (only if targetQty does not exceed known stock limit)
      const stockLimit = currentItem ? getKnownStockLimit(currentItem, trackKey) : null;
      if (currentItem && typeof targetQty === 'number' && currentItem.quantity !== targetQty && (typeof stockLimit !== 'number' || targetQty <= stockLimit)) {
        this._dispatchShopifyQtyUpdate(trackKey);
      } else {
        delete qtyPendingTargets[trackKey];
      }
    },

    removeItem: function (index) {
      const item = cartState.items[index];
      if (!item) return;

      const trackKey = String(item.key || item.variantId || item.id);
      // Clear ALL tracking state for this item to prevent stale data on re-add
      if (qtyDebounceTimers[trackKey]) {
        clearTimeout(qtyDebounceTimers[trackKey]);
        delete qtyDebounceTimers[trackKey];
      }
      delete qtyPendingTargets[trackKey];
      qtyInFlight[trackKey] = false;
      // Also clear by alternate keys (id, variantId) to cover all lookup paths
      const altKeys = [String(item.id || ''), String(item.variantId || '')];
      altKeys.forEach(ak => {
        if (ak && ak !== trackKey) {
          if (qtyDebounceTimers[ak]) { clearTimeout(qtyDebounceTimers[ak]); delete qtyDebounceTimers[ak]; }
          delete qtyPendingTargets[ak];
          qtyInFlight[ak] = false;
        }
      });

      // Handle Gift Wrap Removal Safely (NEVER call Shopify change.js for virtual gift wrap!)
      if (item.isGiftWrap) {
        cartState.isGiftWrapAdded = false;
        cartState.items = cartState.items.filter(i => !i.isGiftWrap);
        const giftWrapCheckbox = document.getElementById('oc-giftwrap-checkbox');
        if (giftWrapCheckbox) giftWrapCheckbox.checked = false;
        showToast('Removed Gift Wrap from cart');
        updateCartCalculations();
        hideLoader(true);
        return;
      }

      // Handle Free Gift Removal
      if (item.isGift) {
        const giftKey = item.key || item.variantId || item.id;
        tombstoneItem(item);
        cartState.items.splice(index, 1);
        updateCartCalculations();
        hideLoader(true);

        if (isShopifyEnv && giftKey) {
          const fetchFn = rawFetch || window.fetch;
          fetchFn('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: giftKey, quantity: 0 })
          }).catch(() => {});
        }
        return;
      }

      // Real or Mock Item: Optimistic Instant Removal (0ms)
      tombstoneItem(item);
      cartState.items.splice(index, 1);

      // If removing this item leaves NO regular products, immediately purge any orphan gifts or gift wraps
      const remainingRegular = cartState.items.filter(i => !i.isGift && !i.isGiftWrap && (i.quantity > 0));
      if (remainingRegular.length === 0) {
        const orphanGifts = cartState.items.filter(i => i.isGift);
        if (orphanGifts.length > 0) {
          orphanGifts.forEach(g => tombstoneItem(g));
          removeGiftFromShopify(orphanGifts);
        }
        cartState.items = [];
        cartState.isGiftWrapAdded = false;
        cartState.item_count = 0;
      }

      updateCartCalculations();

      // Cleanly reset product page form quantity inputs and pack widget to Tier 1 ONLY if the removed item matches the current page
      if (typeof document !== 'undefined') {
        if (isCurrentPageForProduct(item)) {
          const prodForm = document.querySelector('form[action*="/cart/add"], form[action*="/cart/add.js"], .shopify-product-form');
          if (prodForm) {
            const qInput = prodForm.querySelector('[name="quantity"], [data-quantity-input], input.quantity__input');
            if (qInput) {
              qInput.value = '1';
              try {
                qInput.dispatchEvent(new Event('change', { bubbles: true }));
                qInput.dispatchEvent(new Event('input', { bubbles: true }));
              } catch(e) {}
            }
          }
          const tier1Card = document.querySelector('#tier-1, #minimog-pack-1, .orgacia-pack-widget .org-pack-card[data-tier-index="1"], .orgacia-pack-widget .org-pack-card[data-quantity="1"], .bundle-card[data-qty="1"], .tier-card[data-qty="1"]');
          if (tier1Card && typeof tier1Card.click === 'function') {
            tier1Card.click();
          }
        }
        try {
          document.dispatchEvent(new CustomEvent('cart:item-removed', { bubbles: true, detail: { id: item.id, variantId: item.variantId, productId: item.productId } }));
          if (cartState.items.length === 0) {
            document.dispatchEvent(new CustomEvent('cart:cleared', { bubbles: true }));
          }
        } catch(e) {}
      }

      const validShopifyId = getValidShopifyLineId(item);

      if (isShopifyEnv && validShopifyId) {
        showLoader();
        enqueueCartAction(async () => {
          const fetchFn = rawFetch || window.fetch;
          try {
            let res;
            // If this item was consolidated from multiple line keys, zero them all out via /cart/update.js
            if (Array.isArray(item.allKeys) && item.allKeys.length > 1) {
              const updates = {};
              item.allKeys.forEach(k => { updates[k] = 0; });
              res = await fetchFn('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates })
              });
            } else {
              res = await fetchFn('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: validShopifyId, quantity: 0 })
              });
            }

            if (!res.ok) {
              // Line key shifted or parameter mismatch (e.g. numeric ID used for item with properties)
              // Fetch fresh authoritative cart to find real line key and remove it
              console.warn('[Orgacia] Line remove failed with id', validShopifyId, '- fetching fresh cart to realign key');
              const freshRes = await fetchFn(`/cart.js?_t=${Date.now()}`, { cache: 'no-store' });
              const freshCart = await freshRes.json();
              const freshLine = freshCart.items?.find(fi => 
                (validShopifyId && String(fi.key) === String(validShopifyId)) ||
                (item.variantId && String(fi.variant_id) === String(item.variantId)) ||
                (item.id && String(fi.id) === String(item.id)) ||
                (item.productId && String(fi.product_id) === String(item.productId))
              );
              if (freshLine && freshLine.key) {
                const retryRes = await fetchFn('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ id: freshLine.key, quantity: 0 })
                });
                if (retryRes.ok) {
                  const retryCart = await retryRes.json();
                  this.syncWithCart(retryCart);
                  return;
                }
              }
              this.syncWithCart(freshCart);
            } else {
              const cart = await res.json();
              this.syncWithCart(cart);
            }
          } catch (err) {
            console.error('Remove item error', err);
          } finally {
            hideLoader(true);
          }
        });
      } else {
        hideLoader(true);
      }
    },

    clearCart: function () {
      cartState.items = [];
      cartState.appliedCoupon = null;
      updateCartCalculations();
      renderCartView();
      const emptyState = document.getElementById('oc-empty-state');
      if (emptyState) {
        emptyState.classList.add('oc-show');
        emptyState.style.display = 'flex';
      }
    },

    applyCoupon: function (code) {
      return validateAndApplyCoupon(code);
    },

    removeCoupon: function () {
      cartState.appliedCoupon = null;
      updateCartCalculations();
      showToast('Coupon removed from cart');
    },

    showToast: function (msg, type) {
      showToast(msg, type);
    },

    hideToast: function () {
      hideToast();
    },

    getKnownStockLimit: function (item, trackKey) {
      return getKnownStockLimit(item, trackKey);
    },

    setKnownStockLimit: function (item, trackKey, limit) {
      return setKnownStockLimit(item, trackKey, limit);
    },

    knownStockLimits: knownStockLimits,

    triggerConfetti: function () {
      triggerConfetti();
    },

    setSavingsStyle: function (styleName) {
      config.savingsStyle = styleName;
      if (config.savingsCallout) config.savingsCallout.style = styleName;
      const ribbon = document.getElementById('oc-turkish-savings-ribbon');
      if (ribbon) {
        ribbon.className = `oc-turkish-savings-ribbon oc-savings-style--${styleName}`;
        if (cartState.discountAmount > 0 && cartState.items.length > 0) {
          ribbon.style.display = 'block';
        }
      }
    },

    setUpsellLayout: function (layout) {
      if (!config.upsell) config.upsell = {};
      config.upsell.layout = layout;
      renderUpsells();
    },

    setUpsellBadgeStyle: function (style) {
      if (!config.upsell) config.upsell = {};
      config.upsell.badgeStyle = style;
      renderUpsells();
    },

    setDynamicUpsellRule: function (triggerId, upsellItems) {
      if (!config.upsell) config.upsell = {};
      if (!config.upsell.rules) config.upsell.rules = {};
      config.upsell.rules[triggerId] = upsellItems;
      renderUpsells();
    },

    addCoupon: function (coupon) {
      if (!coupon || !coupon.code) return;
      if (!config.coupons) config.coupons = [];
      const codeUpper = coupon.code.toUpperCase();
      const existingIdx = config.coupons.findIndex(c => c.code.toUpperCase() === codeUpper);
      const newEntry = Object.assign({
        enabled: true,
        title: `${coupon.value}${coupon.type === 'percentage' ? '%' : '₹'} Discount`,
        type: 'percentage',
        value: 10,
        bullets: ['Available in store offers'],
        minItems: 1,
        minAmount: 0
      }, coupon, { code: codeUpper });

      if (existingIdx >= 0) {
        config.coupons[existingIdx] = newEntry;
      } else {
        config.coupons.push(newEntry);
      }
      updateCartCalculations();
      renderOffers();
      showToast(`🏷️ Coupon "${codeUpper}" added to available offers!`);
    },

    removeCouponFromList: function (code) {
      if (!config.coupons) return;
      const codeUpper = code.toUpperCase();
      config.coupons = config.coupons.filter(c => c.code.toUpperCase() !== codeUpper);
      if (cartState.appliedCoupon && cartState.appliedCoupon.code.toUpperCase() === codeUpper) {
        this.removeCoupon();
      }
      updateCartCalculations();
      renderOffers();
      showToast(`🏷️ Coupon "${codeUpper}" removed from offers`);
    },

    setCoupons: function (couponsList) {
      config.coupons = couponsList;
      updateCartCalculations();
      renderOffers();
    },

    getCoupons: function () {
      return config.coupons || [];
    },

    setMilestoneStyle: function (styleName) {
      if (!config.tiers) config.tiers = {};
      config.tiers.style = styleName;
      const progressSection = document.getElementById('oc-progress-section');
      if (progressSection) {
        progressSection.className = `oc-progress-section oc-tiers-style--${styleName}`;
      }
      renderMilestones();
      updateCartCalculations();
    },

    setMilestoneTopClaimStyle: function (styleName) {
      config.milestoneTopClaimStyle = styleName;
      if (config.tiers) config.tiers.milestoneTopClaimStyle = styleName;
      updateCartCalculations();
    },

    setMilestoneBottomClaimStyle: function (styleName) {
      config.milestoneBottomClaimStyle = styleName;
      if (config.tiers) config.tiers.milestoneBottomClaimStyle = styleName;
      updateCartCalculations();
    },

    setTopClaimColors: function (colors = {}) {
      config.topClaimColors = Object.assign({}, config.topClaimColors || {}, colors);
      if (typeof document !== 'undefined' && document.documentElement) {
        const root = document.documentElement;
        if (colors.bg) root.style.setProperty('--oc-top-claim-bg', colors.bg);
        if (colors.border) root.style.setProperty('--oc-top-claim-border', colors.border);
        if (colors.text) root.style.setProperty('--oc-top-claim-text', colors.text);
        if (colors.btnBg) root.style.setProperty('--oc-top-claim-btn-bg', colors.btnBg);
        if (colors.btnText) root.style.setProperty('--oc-top-claim-btn-text', colors.btnText);
      }
    },

    setBottomClaimColors: function (colors = {}) {
      config.bottomClaimColors = Object.assign({}, config.bottomClaimColors || {}, colors);
      if (typeof document !== 'undefined' && document.documentElement) {
        const root = document.documentElement;
        if (colors.bg) root.style.setProperty('--oc-bottom-claim-bg', colors.bg);
        if (colors.border) root.style.setProperty('--oc-bottom-claim-border', colors.border);
        if (colors.text) root.style.setProperty('--oc-bottom-claim-text', colors.text);
        if (colors.btnBg) root.style.setProperty('--oc-bottom-claim-btn-bg', colors.btnBg);
        if (colors.btnText) root.style.setProperty('--oc-bottom-claim-btn-text', colors.btnText);
      }
    },

    setTierUpgradeStyle: function (style) {
      if (!config.tierUpgrade) config.tierUpgrade = Object.assign({}, DEFAULT_CONFIG.tierUpgrade);
      config.tierUpgrade.style = style;
      updateCartCalculations();
    },

    setTierUpgradePalette: function (palette) {
      if (!config.tierUpgrade) config.tierUpgrade = Object.assign({}, DEFAULT_CONFIG.tierUpgrade);
      config.tierUpgrade.palette = palette;
      updateCartCalculations();
    },

    setTierUpgradeFontSize: function (size) {
      if (!config.tierUpgrade) config.tierUpgrade = Object.assign({}, DEFAULT_CONFIG.tierUpgrade);
      config.tierUpgrade.fontSize = size;
      if (typeof document !== 'undefined' && document.documentElement) {
        document.documentElement.style.setProperty('--oc-tier-upgrade-font-size', `${size}px`);
      }
      updateCartCalculations();
    },

    setTierUpgradeCustomColors: function (colors = {}) {
      if (!config.tierUpgrade) config.tierUpgrade = Object.assign({}, DEFAULT_CONFIG.tierUpgrade);
      config.tierUpgrade.customColors = Object.assign({}, config.tierUpgrade.customColors || {}, colors);
      if (typeof document !== 'undefined' && document.documentElement) {
        const root = document.documentElement;
        if (colors.bg) root.style.setProperty('--oc-tier-upgrade-custom-bg', colors.bg);
        if (colors.border) root.style.setProperty('--oc-tier-upgrade-custom-border', colors.border);
        if (colors.text) root.style.setProperty('--oc-tier-upgrade-custom-text', colors.text);
        if (colors.btnBg) root.style.setProperty('--oc-tier-upgrade-custom-btn-bg', colors.btnBg);
        if (colors.btnText) root.style.setProperty('--oc-tier-upgrade-custom-btn-text', colors.btnText);
        if (colors.badgeBg) root.style.setProperty('--oc-tier-upgrade-custom-badge-bg', colors.badgeBg);
        if (colors.badgeText) root.style.setProperty('--oc-tier-upgrade-custom-badge-text', colors.badgeText);
      }
      updateCartCalculations();
    },

    executeTierSwap: function (itemKey, targetQty, variantTitle) {
      const itemIndex = cartState.items.findIndex(i => 
        !i.isGift && !i.isGiftWrap && (
          String(i.key || '') === String(itemKey) ||
          String(i.variantId || '') === String(itemKey) ||
          String(i.id || '') === String(itemKey)
        )
      );
      if (itemIndex === -1) return;

      const item = cartState.items[itemIndex];
      const oldQty = Math.max(1, parseInt(item.quantity, 10) || 1);
      const newQty = parseInt(targetQty, 10) || 1;
      if (newQty <= 0 || newQty === oldQty) return;

      // 0ms instant UI update
      item.quantity = newQty;

      // Immediately resolve qualifying bundle tier and calculate discounted price
      const tiers = getDynamicBundleTiers(item);
      const sortedTiers = Array.isArray(tiers) 
        ? [...tiers].sort((a, b) => (b.minQty || b.qty || 0) - (a.minQty || a.qty || 0)) 
        : [];
      const matchedTier = sortedTiers.find(t => newQty >= (t.minQty || t.qty || 1) && (t.discountPct || 0) > 0) || null;

      if (!item.properties) item.properties = {};
      const effectiveDiscount = matchedTier ? matchedTier.discountPct : (newQty >= 4 ? 10 : (newQty >= 2 ? 5 : 0));
      const effectiveTitle = `Pack of ${newQty}`;
      const effectiveLabel = (matchedTier && matchedTier.label) || `Pack of ${newQty} (${effectiveDiscount}% OFF)`;

      if (effectiveDiscount > 0) {
        item.properties._is_bundle = 'true';
        item.properties._bundle_tier = effectiveTitle;
        item.properties._bundle_tier_qty = String(newQty);
        item.properties._bundle_discount = String(effectiveDiscount);
        item.properties._bundle_discount_pct = String(effectiveDiscount);
        if (sortedTiers.length > 0) {
          item.properties._bundle_tiers = JSON.stringify(sortedTiers);
        }
        if (matchedTier && matchedTier.discountCode) {
          item.properties._bundle_discount_code = matchedTier.discountCode;
        }

        item.isBundleItem = true;
        item.discountPct = effectiveDiscount;
        item.bundleDiscountPct = effectiveDiscount;
        item.bundleLabel = effectiveLabel;
        item.variant_title = effectiveTitle;
        const baseUnit = Number(item.basePrice) || Number(item.price) || 0;
        if (baseUnit > 0) {
          item.calculatedPrice = config.roundDecimals 
            ? Math.round(baseUnit * (1 - effectiveDiscount / 100)) 
            : (Math.round(baseUnit * (1 - effectiveDiscount / 100) * 100) / 100);
          item.compare_at_price = baseUnit;
        }
      }

      updateCartCalculations(false);

      // Synchronize with Shopify Cart via debounce/queue
      const trackKey = String(item.key || item.variantId || item.id);
      const validShopifyId = getValidShopifyLineId(item);
      if (isShopifyEnv && validShopifyId) {
        qtyPendingTargets[trackKey] = newQty;
        if (qtyDebounceTimers[trackKey]) {
          clearTimeout(qtyDebounceTimers[trackKey]);
        }
        qtyDebounceTimers[trackKey] = setTimeout(() => {
          delete qtyDebounceTimers[trackKey];
          OrgaciaCart._dispatchShopifyQtyUpdate(trackKey);
        }, 150);
      }

      // Customer celebration feedback
      const label = `Pack of ${newQty}`;
      showToast(`⚡ Upgraded to ${label}! Special bundle savings applied.`);
      if (config.celebration && config.celebration.enabled && typeof triggerConfetti === 'function') {
        triggerConfetti();
      }
    },

    updateConfig: function (newConfig) {
      config = Object.assign({}, config, newConfig);
      if (newConfig.tierUpgrade) {
        config.tierUpgrade = Object.assign({}, config.tierUpgrade || DEFAULT_CONFIG.tierUpgrade, newConfig.tierUpgrade);
      }
      if (newConfig.savingsStyle) {
        this.setSavingsStyle(newConfig.savingsStyle);
      }
      if (newConfig.tiersStyle) {
        this.setMilestoneStyle(newConfig.tiersStyle);
      }
      if (newConfig.milestoneTopClaimStyle) {
        this.setMilestoneTopClaimStyle(newConfig.milestoneTopClaimStyle);
      }
      if (newConfig.milestoneBottomClaimStyle) {
        this.setMilestoneBottomClaimStyle(newConfig.milestoneBottomClaimStyle);
      }
      if (newConfig.topClaimColors) {
        this.setTopClaimColors(newConfig.topClaimColors);
      }
      if (newConfig.bottomClaimColors) {
        this.setBottomClaimColors(newConfig.bottomClaimColors);
      }
      if (newConfig.roundDecimals !== undefined) {
        config.roundDecimals = !!newConfig.roundDecimals;
      }
      renderMilestones();
      renderUpsells();
      renderOffers();
      updateCartCalculations();
      renderCart();
    },

    setRoundDecimals: function (enabled) {
      config.roundDecimals = !!enabled;
      updateCartCalculations();
      renderCart();
    },

    getRoundDecimals: function () {
      return !!config.roundDecimals;
    },

    setTierUpgradeDiscountFormat: function (format) {
      if (!config.tierUpgrade) config.tierUpgrade = {};
      config.tierUpgrade.discountFormat = format || 'save_amount';
      renderCart();
    },

    getTierUpgradeDiscountFormat: function () {
      return (config.tierUpgrade && config.tierUpgrade.discountFormat) || 'save_amount';
    },

    getConfig: function () {
      return config;
    },

    getCartState: function () {
      return cartState;
    },

    getCart: function () {
      return cartState;
    },

    recalculateDiscounts: function () {
      updateCartCalculations();
    },

    setProductBenefit: function (productIdentifier, tagline, bg, color) {
      if (!config.upsell) config.upsell = {};
      if (!Array.isArray(config.upsell.productBenefits)) config.upsell.productBenefits = [];
      const idStr = String(productIdentifier || '').toLowerCase().trim();
      const existing = config.upsell.productBenefits.find(b => 
        String(b.productId || '').toLowerCase() === idStr ||
        String(b.handle || '').toLowerCase() === idStr ||
        String(b.title || '').toLowerCase() === idStr
      );
      if (existing) {
        existing.tag = tagline;
        if (bg) existing.bg = bg;
        if (color) existing.color = color;
      } else {
        config.upsell.productBenefits.push({
          title: productIdentifier,
          handle: productIdentifier,
          productId: productIdentifier,
          tag: tagline,
          bg: bg || '#F0FDF4',
          color: color || '#166534'
        });
      }
      renderUpsells();
    },

    setDuoComboConfig: function (options) {
      if (!config.upsell) config.upsell = {};
      if (!config.upsell.duoCombo) config.upsell.duoCombo = {};
      Object.assign(config.upsell.duoCombo, options || {});
      if (options.enabled !== undefined) {
        config.upsell.enableDuoCombo = options.enabled;
      }
      renderUpsells();
    },

    openGiftModal: openGiftModal,
    closeGiftModal: closeGiftModal,
    saveGiftClaim: saveGiftClaim,
    toggleGiftProduct: toggleGiftProduct
  };

  // Expose globally — init() is called by the liquid snippet with ORGACIA_SETTINGS
  window.OrgaciaCart = OrgaciaCart;

  // Handle Back-Forward Cache (bfcache), tab visibility, and history navigation
  if (typeof window !== 'undefined') {
    window.addEventListener('pageshow', function (event) {
      resetCheckoutButton();
      if (window.OrgaciaCart && typeof window.OrgaciaCart.refresh === 'function') {
        window.OrgaciaCart.refresh(true);
      }
    });
    window.addEventListener('focus', resetCheckoutButton);
    window.addEventListener('popstate', resetCheckoutButton);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) resetCheckoutButton();
      });
    }
  }

  function checkThankYouPageOrderCapture() {
    try {
      const path = (window.location.pathname || '').toLowerCase();
      const search = window.location.search || '';
      const params = new URLSearchParams(search);

      const isThankYouPath = path.includes('/thank_you') || 
                             path.includes('/orders/') || 
                             path.includes('/checkouts/') ||
                             path.includes('/order-confirmation') ||
                             path.includes('/confirmation');

      const hasOrderParams = params.has('order_id') || 
                             params.has('order_number') || 
                             params.has('fastrr_order_id') || 
                             params.has('order_name') ||
                             (params.has('status') && params.get('status') === 'success') ||
                             (params.has('payment_status') && params.get('payment_status') === 'success');

      if (!isThankYouPath && !hasOrderParams) return;

      let orderId = '';
      let orderNum = '';
      let totalPrice = '3.00';
      let isCod = false;
      let isPaid = true;
      let gateway = 'PayU (Prepaid)';
      let customerName = 'Store Customer';

      // 1. Classic window.Shopify.checkout (if present in legacy theme)
      if (window.Shopify && window.Shopify.checkout) {
        const chk = window.Shopify.checkout;
        orderId = String(chk.order_id || chk.id || '');
        orderNum = String(chk.order_number || chk.order_name || orderId);
        totalPrice = String(chk.total_price || '3.00');
        const chkGw = (chk.payment_gateway_names?.[0] || chk.gateway || '').toLowerCase();
        if (chkGw.includes('cod') || chkGw.includes('cash on delivery') || chk.financial_status === 'pending') {
          isCod = true;
          isPaid = false;
          gateway = 'Cash on Delivery (COD)';
        } else {
          isPaid = true;
          isCod = false;
          gateway = chk.payment_gateway_names?.[0] || 'PayU (Prepaid)';
        }
        if (chk.billing_address?.first_name || chk.shipping_address?.first_name) {
          customerName = `${chk.billing_address?.first_name || chk.shipping_address?.first_name} ${chk.billing_address?.last_name || chk.shipping_address?.last_name}`.trim();
        }
      }

      // 2. Fastrr URL parameters or status return redirect
      if (!orderId) {
        orderId = params.get('fastrr_order_id') || params.get('order_id') || params.get('id') || params.get('order_number') || params.get('order_name') || '';
        if (orderId) {
          orderNum = params.get('order_number') || params.get('order_name') || orderId;
          if (params.has('total') || params.has('amount') || params.has('total_price')) {
            totalPrice = String(params.get('total') || params.get('amount') || params.get('total_price'));
          }
          const mode = (params.get('payment_mode') || params.get('gateway') || params.get('payment_method') || '').toLowerCase();
          if (mode.includes('cod') || mode.includes('cash') || params.get('is_cod') === 'true') {
            isCod = true;
            isPaid = false;
            gateway = 'Cash on Delivery (COD)';
          } else {
            isPaid = true;
            isCod = false;
            gateway = mode ? mode.toUpperCase() : 'PayU (Prepaid)';
          }
        }
      }

      // 3. Modern Shopify Checkout Extensibility DOM parsing
      if (!orderId) {
        const numEl = document.querySelector('.os-order-number, [data-order-number], [data-checkout-order-number], .thank-you__order-number, .order-number');
        if (numEl) {
          orderNum = numEl.textContent.trim().replace(/^Order\s*/i, '');
          orderId = orderNum.replace('#', '');
        } else {
          const pathMatch = path.match(/\/orders\/([a-zA-Z0-9_\-]+)/);
          if (pathMatch) {
            orderId = pathMatch[1];
            orderNum = orderId;
          }
        }

        const priceEl = document.querySelector('.payment-due__price, [data-checkout-payment-due-target], .os-total-due__total, .order-summary__emphasis');
        if (priceEl) {
          const rawPrice = priceEl.textContent.replace(/[^0-9.]/g, '');
          if (rawPrice) totalPrice = rawPrice;
        }

        const pageText = (document.body ? document.body.innerText : '').toLowerCase();
        if (pageText.includes('cash on delivery') || pageText.includes('payment pending') || pageText.includes('pay on delivery')) {
          isCod = true;
          isPaid = false;
          gateway = 'Cash on Delivery (COD)';
        } else if (pageText.includes('payu') || pageText.includes('razorpay') || pageText.includes('upi') || pageText.includes('paid')) {
          isPaid = true;
          isCod = false;
          gateway = pageText.includes('payu') ? 'PayU' : 'Prepaid (Online)';
        }
      }

      if (!orderId) return;

      const seenKey = 'orgacia_order_captured_' + orderId;
      if (sessionStorage.getItem(seenKey)) return;
      sessionStorage.setItem(seenKey, 'true');

      const isMobile = (window.innerWidth <= 768 || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) ? 'Mobile' : 'Desktop';
      const cleanOrderNum = String(orderNum).startsWith('#') ? String(orderNum) : ('#' + orderNum);

      let savedAttribution = {};
      try {
        const attrStr = sessionStorage.getItem('orgacia_attribution');
        if (attrStr) savedAttribution = JSON.parse(attrStr);
      } catch(e) {}

      const isNative = path.includes('/orders/') || path.includes('/checkouts/');
      const provider = isNative ? 'Shopify Native Checkout' : 'Shiprocket Fastrr';
      const channel = isNative ? 'web' : 'orgacia>>fastrr';

      const payload = {
        action: 'capture_order',
        event: 'order_captured',
        shop: window.Shopify?.shop || 'demo1243.myshopify.com',
        order: {
          id: String(orderId),
          name: cleanOrderNum,
          order_number: cleanOrderNum.replace('#', ''),
          created_at: new Date().toISOString(),
          financial_status: isPaid ? 'paid' : 'pending',
          gateway: gateway,
          payment_gateway_names: [gateway, isPaid ? 'Fastrr' : 'COD'],
          total_price: String(totalPrice),
          total_discounts: '0.00',
          currency: window.Shopify?.currency?.active || 'INR',
          tags: isPaid ? 'Paid, Fastrr' : 'COD, Fastrr',
          source_name: channel,
          customer: {
            first_name: customerName,
            last_name: ''
          },
          note_attributes: [
            { name: '_bundle_source', value: 'product_page_bundle' },
            { name: '_bundle_tier', value: 'Pack of 1 (Standard Unit)' },
            { name: '_checkout_provider', value: provider },
            { name: '_device_type', value: isMobile },
            { name: 'utm_source', value: savedAttribution.utm_source || 'direct' },
            { name: 'utm_medium', value: savedAttribution.utm_medium || 'direct' },
            { name: 'utm_campaign', value: savedAttribution.utm_campaign || 'storefront_organic' }
          ]
        }
      };

      fetch('https://orgacia-shopify-store.pages.dev/api/track', {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(() => {});

      fetch('/api/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(() => {});

    } catch(e) {
      console.warn('[Orgacia] Thank-you page capture check error:', e);
    }
  }

  // Auto-initialize if running in prototype / demo environment where liquid block is absent
  if (typeof window !== 'undefined') {
    const autoInit = () => {
      if (!document.getElementById('oc-drawer')) {
        OrgaciaCart.init();
      }
      checkThankYouPageOrderCapture();
      if (typeof window.initPackWidgets === 'function') {
        window.initPackWidgets();
      }
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', autoInit);
    } else {
      autoInit();
    }
  }

})(window, document);
